// Updated at 1759857892
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sanitizeHtml } from '../utils/sanitize';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import html2pdf from 'html2pdf.js';
import { JsonStructuredPdfService } from '../services/jsonStructuredPdfService';
import ExportControls from './ExportControls';
import { ExportOptions } from '../types/export.types';
import { CitationMarker } from './CitationMarker';

// Tailwind-styled wrapper around ReactMarkdown for the YOY add-on sections —
// keeps the GFM markdown tables (and any bullet/heading) consistent with the
// rest of the report.
//
// IMPORTANT: `prose-table:w-full` forces tables to span the full container
// width. Without it, HTML <table> defaults to content-width and the
// schedules / coverage-details matrices look narrow with empty space to
// the right of them.
const MarkdownBlock: React.FC<{ children: string }> = ({ children }) => (
  <div className="prose prose-sm dark:prose-invert max-w-none
                  prose-table:my-2 prose-table:w-full prose-table:border-collapse
                  prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:font-medium prose-th:text-gray-700 dark:prose-th:text-gray-200 prose-th:bg-gray-100 dark:prose-th:bg-gray-700/50
                  prose-td:px-3 prose-td:py-2 prose-td:text-gray-800 dark:prose-td:text-gray-200 prose-td:border-t prose-td:border-gray-200 dark:prose-td:border-gray-700 prose-td:align-top">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
  </div>
);

// ===== INTERFACES =====

interface Citation {
  id: string;
  sourceDocId: string;
  section: string;
  page: string | number;
  text: string;
  clickable: boolean;
  url: string;
  startChar?: number;
  endChar?: number;
  type?: string;
  docName?: string;
  docId?: string;
}

// New citation format from prompt
interface QuoteCitation {
  quote?: string;
  text?: string;
  source?: string;
  confidence?: number;
}

interface CitationText {
  text: string;
  citations: QuoteCitation[];
}

interface UniversalJsonResponse {
  responseMetadata: {
    workflowType: 'policy_comparison' | 'policy_analysis' | 'loss_run_analysis' | 'loss_run_analyzer' | 'research_analysis' | 'risk_assessment' | 'insurance_requirements' | 'commercial_lines_exec_renewal' | 'client_renewal_presentation' | 'financial_analysis' | 'financial_analysis_comparative' | 'rate_analysis' | 'portfolio_analyzer' | 'homeowners_comparison' | 'personal_umbrella';
    generatedAt: string;
    documentsAnalyzed: Array<{
      id: string;
      name: string;
      type?: string;
      policyType?: string;
    }>;
    processingTime?: number;
    confidence?: 'High' | 'Medium' | 'Low';
    // Loss run analysis specific fields
    insuredIdentification?: {
      insuredName: string;
      insurers: string[];
      linesOfBusiness: string[];
    };
    evaluationPeriod?: {
      startDate: string;
      endDate: string;
      policyPeriods: string[];
    };
    dataQuality?: {
      completeness: string;
      claimsAnalyzed: number;
      missingDataElements: string[];
    };
  };
  
  executiveSummary: {
    paragraphs: Array<{
      content: CitationText;
    }>;
  };
  
  analysisMatrices: Array<{
    title: string;
    type?: string;
    description?: string;
    headers: string[];
    rows: Array<Array<CitationText | string | any> | Record<string, any>>;
    summary?: string;
    insights?: string[];
    includeIf?: string;
  }>;
  
  keyFindings: {
    summary: CitationText;
    findings: Array<{
      title?: string;
      category: string;
      finding?: CitationText | {
        observation?: CitationText;
        implication?: CitationText;
        recommendation?: CitationText;
        businessImpact?: CitationText;
      };
      description?: CitationText | string;
      impact?: 'Positive' | 'Negative' | 'Neutral' | 'Critical';
      impactLevel?: string;
      confidence?: 'High' | 'Medium' | 'Low';
      priority?: 'High' | 'Medium' | 'Low' | 'Immediate';
      riskLevel?: 'Critical' | 'High' | 'Medium' | 'Low';
      lossReductionPotential?: string;
    }>;
  };
  
  recommendations?: Array<{
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    category: string;
    action: CitationText;
    rationale: CitationText;
    timeline?: string;
  }>;
  
  // Loss run analysis specific sections
  riskManagementMatrix?: {
    title: string;
    recommendations: Array<{
      riskCategory: string;
      keyObservation: CitationText;
      businessImpact: CitationText;
      actionableRecommendation: CitationText;
      implementationSteps: string[];
      expectedOutcome: string;
      priorityLevel: string;
      estimatedCost: string;
      timeline: string;
    }>;
  };
  
  trendAnalysis?: {
    overallTrend: string;
    keyMetrics: {
      frequencyTrend: {
        direction: string;
        percentageChange: string;
        analysis: string;
      };
      severityTrend: {
        direction: string;
        percentageChange: string;
        analysis: string;
      };
      lossRatioTrend: {
        currentRatio: string;
        historicalAverage: string;
        analysis: string;
      };
    };
    emergingRisks: Array<{
      risk: string;
      indicators: string[];
      potentialImpact: string;
      mitigationStrategy: string;
    }>;
  };
  
  benchmarkingInsights?: {
    industryComparison: {
      performanceVsBenchmark: string;
      keyDifferentiators: string[];
      improvementOpportunities: string[];
    };
    internalComparison: {
      bestPerformingAreas: string[];
      worstPerformingAreas: string[];
      lessonsLearned: string[];
    };
  };
  
  costBenefitAnalysis?: Array<{
    recommendation: string;
    implementationCost: string;
    potentialSavings: string;
    paybackPeriod: string;
    nonFinancialBenefits: string[];
    implementationComplexity: string;
  }>;
  
  dataQualityAssessment?: {
    completenessScore?: string;
    dataGaps?: string[];           // Loss run format
    missingElements?: string[];    // Policy comparison format
    assumptionsMade?: string[];    // Make optional
    recommendedDataCollection?: string[];  // Loss run format
    recommendedFollowUp?: string[];        // Policy comparison format
    confidenceInAnalysis?: string;         // Optional for policy comparison
  };
  
  citationIndex?: {
    totalCitations: number;
    documentSources: Array<{
      docId: string;
      name: string;
      citationCount: number;
      baseUrl: string;
    }>;
  };
  
  strategicConsiderations?: Array<{
    consideration: string;
    analysis: string;
    options: string[];
    recommendedApproach: string;
    citations: Array<{
      text: string;
      source: string;
      confidence: number;
    }>;
  }>;
  
  riskMitigationStrategies?: Array<{
    risk: string;
    strategy: string;
    implementation: string;
    expectedReduction: string;
  }>;
  
  nonStandardProvisions?: {
    identified: boolean;
    provisions: Array<{
      provision: string;
      location: string;
      deviation: string;
      riskAssessment: string;
      citations: Array<{
        docId: string;
        docName: string;
        page: number | null;
        section?: string;
      }>;
    }>;
  };
  
  clauseWordingComparison?: {
    description?: string;
    analysis?: CitationText | string;
    comparisons?: Array<{
      clause?: string;
      section?: string;
      description?: string;
      currentWording?: CitationText | string;
      proposedWording?: CitationText | string;
      standardWording?: CitationText | string;
      differences?: CitationText | string;
      impact?: CitationText | string;
      recommendation?: CitationText | string;
      citations?: Array<{
        docId: string;
        docName: string;
        page: number | null;
        section?: string;
      }>;
    }>;
  };

  // Renewal-specific sections
  renewalRecommendations?: {
    summary?: string;
    recommendations: Array<{
      priority: 'critical' | 'high' | 'medium' | 'low';
      category: string;
      recommendation: string;
      rationale: string;
      expectedOutcome: string;
      marketContext: string;
    }>;
  };

  marketAnalysis?: {
    marketConditions?: {
      assessment: string;
      trends: string[];
      citations: any[];
    };
    competitivePosition?: {
      assessment: string;
      advantages: string[];
      concerns: string[];
      citations: any[];
    };
  };

  renewalStrategy?: {
    overallStrategy?: string;
    keyActions?: string[];
    timeline?: string;
    riskConsiderations?: string[];
  };
}

interface UniversalJsonRendererProps {
  data: UniversalJsonResponse;
  onCitationClick: (citation: any) => void;
  className?: string;
  showExportControls?: boolean;
  hideCitations?: boolean;
  customerName?: string;
  documents?: Array<{
    id: string;
    name: string;
  }>;
  customPdfExportHandler?: () => Promise<void>;
  compactHeader?: boolean; // When true, hide the duplicate title/icon hero and render a slim toolbar (confidence + exports)
  // When provided, the ExportControls render into this DOM node (typically
  // the outer page header) instead of as a toolbar above the report body.
  exportPortalRef?: React.RefObject<HTMLDivElement | null>;
  selectedSections?: Record<string, boolean>;
  sectionOrder?: string[];
  manualPageBreaks?: Set<string>;
  keepTogether?: Set<string>;
  workflowType?: string; // Override workflow type from props instead of AI response
}

// ===== HELPER FUNCTIONS =====

// Format numbers with commas and currency symbols
const formatNumber = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return 'N/A';

  // If it's already a formatted string with commas or $, just return it
  if (typeof value === 'string') {
    // Check if it already has dollar sign or commas - likely already formatted
    if (value.includes('$') || value.includes(',')) {
      return value;
    }

    // Try to extract a number from the string
    const numericValue = value.replace(/[^0-9.-]/g, '');
    if (!numericValue || isNaN(Number(numericValue))) {
      return value; // Return original if can't parse
    }
    value = numericValue;
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return value.toString();

  // Format with commas
  return num.toLocaleString('en-US');
};

// Format currency values with $ and commas
const formatCurrency = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return 'N/A';

  // If it's a string, extract the numeric portion (even if it has $ already)
  let numValue: number;
  if (typeof value === 'string') {
    // Remove everything except digits, decimal point, and minus sign
    const cleanedValue = value.replace(/[^0-9.-]/g, '');
    if (!cleanedValue || isNaN(Number(cleanedValue))) {
      // Return original if can't parse
      return value;
    }
    numValue = parseFloat(cleanedValue);
  } else {
    numValue = value;
  }

  if (isNaN(numValue)) return value.toString();

  // Format as currency with commas
  return '$' + numValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// ===== MAIN COMPONENT =====

export const UniversalJsonRenderer: React.FC<UniversalJsonRendererProps> = ({
  data,
  onCitationClick,
  className = '',
  showExportControls = true,
  hideCitations = false,
  customerName,
  documents = [],
  customPdfExportHandler,
  compactHeader = false,
  exportPortalRef,
  selectedSections = {},
  sectionOrder = [],
  manualPageBreaks = new Set<string>(),
  keepTogether = new Set<string>(),
  workflowType: propWorkflowType
}) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [showAllClaims, setShowAllClaims] = React.useState(false);
  // The portal target is populated after the parent mounts, so on the first
  // render `exportPortalRef.current` is still null. Trigger one re-render
  // after mount so createPortal sees the live DOM node.
  const [portalReady, setPortalReady] = useState(false);
  useEffect(() => {
    if (exportPortalRef?.current) setPortalReady(true);
  }, [exportPortalRef]);
  void portalReady; // referenced via the conditional render below

  // Helper function to check if a section should be shown
  const shouldShowSection = (sectionId: string) => {
    // If no selectedSections provided, show all sections
    if (Object.keys(selectedSections).length === 0) {
      console.log(`📋 shouldShowSection(${sectionId}): No selectedSections, showing all`);
      return true;
    }
    // Otherwise check if this section is selected
    const result = selectedSections[sectionId] !== false;
    console.log(`📋 shouldShowSection(${sectionId}): ${result ? 'SHOW' : 'HIDE'} (selectedSections[${sectionId}] = ${selectedSections[sectionId]})`);
    return result;
  };

  // Helper function to get CSS order for a section based on sectionOrder
  const getSectionOrder = (sectionId: string): number => {
    if (sectionOrder.length === 0) return 0;
    const index = sectionOrder.indexOf(sectionId);
    return index >= 0 ? index : 999; // Put unknown sections at the end
  };

  // Helper function to get section styles for page breaks and keep-together
  const getSectionStyles = (sectionId: string): React.CSSProperties => {
    const styles: React.CSSProperties = {
      order: getSectionOrder(sectionId)
    };
    return styles;
  };

  // Helper function to get section data attributes for print CSS
  const getSectionDataAttrs = (sectionId: string): Record<string, string> => {
    const attrs: Record<string, string> = {
      'data-section-id': sectionId
    };
    if (manualPageBreaks.has(sectionId)) {
      attrs['data-page-break-after'] = 'true';
    }
    if (keepTogether.has(sectionId)) {
      attrs['data-keep-together'] = 'true';
    }
    return attrs;
  };

  // Debug logging to verify props are received
  console.log('🔍🔍🔍 UniversalJsonRenderer loaded with selectedSections:', selectedSections);
  console.log('🔍🔍🔍 UniversalJsonRenderer: customPdfExportHandler prop:', customPdfExportHandler ? 'PRESENT' : 'MISSING');
  console.log('🔍🔍🔍 UniversalJsonRenderer: customPdfExportHandler type:', typeof customPdfExportHandler);
  
  // Helper function to map generic document IDs (doc_1, doc_2) to actual UUIDs
  const resolveDocumentId = (docId: string): string | null => {
    if (!docId || !documents.length) return null;
    
    // Handle direct UUID (already resolved)
    if (!docId.startsWith('doc_')) {
      return docId;
    }
    
    // Parse doc_N format to get index
    const match = docId.match(/^doc_(\d+)$/);
    if (!match) return null;
    
    const docIndex = parseInt(match[1]) - 1; // Convert to 0-based index
    
    // Get the document at this index
    if (docIndex >= 0 && docIndex < documents.length) {
      return documents[docIndex].id;
    }
    
    console.warn(`🔍 Could not resolve document ID: ${docId} (documents available: ${documents.length})`);
    return null;
  };

  // Helper function to safely convert any value to a renderable string
  // Prevents React error #31 when objects are accidentally rendered directly
  const safeString = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      // Handle CitationText-style objects with text property
      if (value.text && typeof value.text === 'string') return value.text;
      // Handle arrays by joining
      if (Array.isArray(value)) return value.map(v => safeString(v)).join(', ');
      // Fallback to JSON for unknown objects
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Helper function to extract text from complex nested objects
  const extractTextFromNestedObject = (obj: any): { text: string; citations?: any[] } => {
    console.log('🔍 extractTextFromNestedObject received:', {
      type: typeof obj,
      isNull: obj === null,
      isUndefined: obj === undefined,
      keys: obj ? Object.keys(obj) : 'none',
      obj: obj
    });

    if (!obj) return { text: '' };

    // Handle simple string - MUST BE FIRST
    if (typeof obj === 'string') {
      console.log('🔍 extractTextFromNestedObject: Handling simple string:', obj);
      return { text: obj };
    }

    // Handle direct CitationText format {text: "...", citations: [...]}
    if (obj && obj.text) {
      console.log('🔍 extractTextFromNestedObject: Found text+citations format:', {
        text: obj.text?.substring(0, 100),
        citationsCount: obj.citations?.length || 0,
        citationsStructure: obj.citations?.map((c: any) => ({ docId: c.docId, docName: c.docName, page: c.page }))
      });
      return { text: obj.text, citations: obj.citations };
    }

    // Handle YOY prompt structure: Check for nested objects with content
    if (obj && typeof obj === 'object') {
      // YOY prompt returns objects like: { "content": { "text": "...", "citations": [...] } }
      if (obj.content && obj.content.text) {
        return { text: obj.content.text, citations: obj.content.citations };
      }

      // Also handle direct nested format: { "text": "...", "citations": [...] } buried deeper
      const findTextRecursively = (item: any): { text: string; citations?: any[] } | null => {
        if (!item || typeof item !== 'object') return null;

        if (item.text && typeof item.text === 'string') {
          return { text: item.text, citations: item.citations };
        }

        // Look through all object properties for text
        for (const key of Object.keys(item)) {
          if (typeof item[key] === 'object' && item[key] !== null) {
            const result = findTextRecursively(item[key]);
            if (result && result.text) return result;
          }
        }
        return null;
      };

      const foundText = findTextRecursively(obj);
      if (foundText) return foundText;
    }
    
    // Handle complex nested structure from comparePoliciesPrompt and year-over-year:
    // {observation: {text: "...", citations: []}, implication: {text: "...", citations: []}, recommendation: {text: "...", citations: []}}
    // Also handles businessImpact field for year-over-year analysis
    if (obj.observation || obj.implication || obj.recommendation || obj.businessImpact) {
      const allCitations: any[] = [];
      const textParts: string[] = [];
      
      // Check observation - make sure text is not empty
      if (obj.observation && obj.observation.text && obj.observation.text.trim()) {
        textParts.push(`**Observation:** ${obj.observation.text.trim()}`);
        if (obj.observation.citations) allCitations.push(...obj.observation.citations);
      }
      
      // Check implication - make sure text is not empty  
      if (obj.implication && obj.implication.text && obj.implication.text.trim()) {
        textParts.push(`**Implication:** ${obj.implication.text.trim()}`);
        if (obj.implication.citations) allCitations.push(...obj.implication.citations);
      }
      
      // Check businessImpact (year-over-year specific) - make sure text is not empty
      if (obj.businessImpact && obj.businessImpact.text && obj.businessImpact.text.trim()) {
        textParts.push(`**Business Impact:** ${obj.businessImpact.text.trim()}`);
        if (obj.businessImpact.citations) allCitations.push(...obj.businessImpact.citations);
      }
      
      // Check recommendation - make sure text is not empty
      if (obj.recommendation && obj.recommendation.text && obj.recommendation.text.trim()) {
        textParts.push(`**Recommendation:** ${obj.recommendation.text.trim()}`);
        if (obj.recommendation.citations) allCitations.push(...obj.recommendation.citations);
      }
      
      if (textParts.length > 0) {
        return { 
          text: textParts.join('\n\n'), 
          citations: allCitations 
        };
      } else {
        // All fields are empty - this might be the issue! Log more detail
        console.warn('🔍 All finding text fields are empty - debugging info:', {
          hasObservation: !!obj.observation,
          hasImplication: !!obj.implication, 
          hasBusinessImpact: !!obj.businessImpact,
          hasRecommendation: !!obj.recommendation,
          observationText: obj.observation?.text,
          implicationText: obj.implication?.text,
          businessImpactText: obj.businessImpact?.text,
          recommendationText: obj.recommendation?.text,
          fullObject: obj
        });
        return { text: '[Finding fields detected but all text is empty - check AI response structure]' };
      }
    }
    
    // Handle nested observation format {observation: {text: "...", citations: [...]}} (legacy)
    if (obj.observation && obj.observation.text) {
      return { text: obj.observation.text, citations: obj.observation.citations };
    }
    
    // Handle other nested formats
    if (obj.content && typeof obj.content === 'string') {
      return { text: obj.content, citations: obj.citations };
    }
    
    if (obj.description && typeof obj.description === 'string') {
      return { text: obj.description, citations: obj.citations };
    }
    
    // Additional fallbacks for various JSON structures
    // Handle simple objects with direct properties that might contain text
    if (obj && typeof obj === 'object') {
      // Check for common text fields
      const textFields = ['description', 'content', 'summary', 'details', 'value', 'finding'];
      for (const field of textFields) {
        if (obj[field] && typeof obj[field] === 'string' && obj[field].trim()) {
          console.log(`🔍 Found text in ${field} field:`, obj[field]);
          return { text: obj[field], citations: obj.citations || [] };
        }
      }
      
      // Try to extract any string values from the object
      const stringValues = Object.values(obj).filter(val => typeof val === 'string' && val.trim());
      if (stringValues.length > 0) {
        console.log('🔍 Found string values in object:', stringValues);
        return { text: stringValues.join(' • '), citations: obj.citations || [] };
      }
    }

    // Final fallback: convert to formatted JSON string
    console.warn('🔍 UniversalJsonRenderer: Unknown object structure, converting to JSON string:', {
      type: typeof obj,
      isObject: typeof obj === 'object',
      isNull: obj === null,
      keys: obj ? Object.keys(obj) : 'none',
      values: obj ? Object.values(obj).map(v => ({ type: typeof v, value: typeof v === 'string' ? v.substring(0, 100) : v })) : 'none',
      obj: obj
    });
    return { text: JSON.stringify(obj, null, 2) };
  };

  // Enhanced helper function for safe content rendering with comprehensive error handling
  const renderSafeContent = (content: any, sectionName: string, hideCitations?: boolean, fallbackCitations?: Citation[]) => {
    try {
      // Handle null or undefined content
      if (!content) {
        console.warn(`🔍 ${sectionName}: No content provided`);
        return <div className="text-gray-500 italic">No content available for {sectionName}</div>;
      }

      // Try to render using the enhanced citation text function
      const result = renderCitationText(content, hideCitations, fallbackCitations);
      
      // Check if the result indicates no content was found
      if (typeof result === 'object' && result.props?.children === 'No content available') {
        console.warn(`🔍 ${sectionName}: renderCitationText returned no content, trying fallbacks:`, content);
        
        // Try to render as raw JSON for debugging
        return <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border-l-4 border-orange-400">
          <div className="text-orange-700 dark:text-orange-300 font-medium mb-2">
            {sectionName}: Unable to parse content structure
          </div>
          <details>
            <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800 mb-2">
              Show raw data for debugging
            </summary>
            <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded overflow-auto max-h-40">
              {JSON.stringify(content, null, 2)}
            </pre>
          </details>
        </div>;
      }
      
      return result;
    } catch (error) {
      console.error(`🔍 ${sectionName}: Error rendering content:`, error, content);
      return <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded border-l-4 border-red-400">
        <div className="text-red-700 dark:text-red-300 font-medium mb-2">
          {sectionName}: Rendering Error
        </div>
        <div className="text-sm text-red-600 dark:text-red-400 mb-2">
          {error instanceof Error ? error.message : 'Unknown error occurred'}
        </div>
        <details>
          <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800">
            Show raw data
          </summary>
          <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded overflow-auto max-h-40 mt-2">
            {JSON.stringify(content, null, 2)}
          </pre>
        </details>
      </div>;
    }
  };

  // Helper function to check if a section has any content worth displaying
  const hasValidContent = (content: any): boolean => {
    if (!content) return false;
    if (typeof content === 'string') return content.trim().length > 0;
    if (Array.isArray(content)) return content.length > 0;
    if (typeof content === 'object') {
      // Check if object has any meaningful properties
      const keys = Object.keys(content);
      if (keys.length === 0) return false;
      
      // Check for common empty patterns
      if (content.text === '' && (!content.citations || content.citations.length === 0)) return false;
      if (content.paragraphs && (!content.paragraphs.length)) return false;
      
      return true;
    }
    return false;
  };

  // Helper function to render CitationText objects with clickable quotes
  const renderCitationText = (citationText: CitationText | string | any, forcedStripCitations?: boolean, fallbackCitations?: Citation[]) => {
    // Extract text and citations from potentially nested structure
    const { text, citations } = extractTextFromNestedObject(citationText);

    console.log('🔍 renderCitationText debug:', {
      inputType: typeof citationText,
      inputValue: citationText,
      extractedText: text,
      extractedCitations: citations,
      hasText: !!text,
      textTrimmed: text?.trim()
    });

    // Handle legacy string format fallback
    if (!text && typeof citationText === 'string') {
      console.log('🔍 Using legacy string fallback for:', citationText);
      return renderTextWithCitations(citationText, fallbackCitations || []);
    }

    // Additional fallback for direct strings
    if (typeof citationText === 'string' && citationText.trim()) {
      console.log('🔍 Using direct string rendering for:', citationText);
      return <span>{citationText}</span>;
    }

    // If no text extracted, show debug info
    if (!text || text.trim() === '') {
      console.warn('🔍 renderCitationText: No text extracted!', { input: citationText, extracted: text });
      return <span className="text-red-500 text-sm">No content available</span>;
    }
    
    // If forced stripping or no citations, just show text
    if (forcedStripCitations || !citations || citations.length === 0) {
      return <span>{text}</span>;
    }
    
    // Helper function to render markdown-style bold text
    const renderMarkdownText = (text: string) => {
      // Split by bold markers (**text**) and render accordingly
      const parts = text.split(/(\*\*.*?\*\*)/);
      
      return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // Bold text
          return <strong key={index} className="font-semibold text-gray-900 dark:text-white">{part.slice(2, -2)}</strong>;
        } else if (part.includes('\n')) {
          // Handle line breaks
          return part.split('\n').map((line, lineIndex) => (
            <React.Fragment key={`${index}-${lineIndex}`}>
              {line}
              {lineIndex < part.split('\n').length - 1 && <br />}
            </React.Fragment>
          ));
        } else {
          // Regular text
          return <span key={index}>{part}</span>;
        }
      });
    };

    // Filter out invalid citations (missing required fields)
    const validCitations = citations.filter(citation => {
      const hasDocName = citation.docName && citation.docName.trim();
      const hasPage = citation.page && citation.page.toString().trim();
      const hasDocId = citation.docId && citation.docId.trim();

      if (!hasDocName && !hasDocId) {
        console.warn('🔍 Filtering out citation with no docName or docId:', citation);
        return false;
      }
      if (!hasPage) {
        console.warn('🔍 Citation missing page number:', citation);
      }
      return true;
    });

    // Dedupe citations that point to the same (docId or docName) + page.
    // This collapses the "same pill rendered 6 times" case we saw in loss-run.
    const dedupedCitations = (() => {
      const seen = new Set<string>();
      return validCitations.filter((c) => {
        const key = `${c.docId || c.docName}|${c.page ?? c.pageNumber ?? '?'}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })();

    // Map distinct source documents to a stable letter so when a section
    // cites multiple docs the user can tell them apart at a glance.
    const docLetters = (() => {
      const order: string[] = [];
      dedupedCitations.forEach((c) => {
        const key = c.docId || c.docName || 'doc';
        if (!order.includes(key)) order.push(key);
      });
      // Only show letters when more than one distinct source — otherwise just the icon.
      if (order.length <= 1) return new Map<string, string>();
      const m = new Map<string, string>();
      order.forEach((key, i) => m.set(key, String.fromCharCode(65 + (i % 26))));
      return m;
    })();

    return (
      <span className="text-gray-700 dark:text-gray-300 leading-relaxed">
        {renderMarkdownText(text)}
        {dedupedCitations.map((citation, index) => {
          const docName = citation.docName || citation.docId || 'Document';
          const page = citation.page || citation.pageNumber || '?';
          const docKey = citation.docId || citation.docName || 'doc';
          return (
            <CitationMarker
              key={`${docKey}-${page}-${index}`}
              citation={{
                docName,
                docId: citation.docId,
                page,
                confidence: citation.confidence,
              }}
              sourceLetter={docLetters.get(docKey)}
              onClick={() => {
                const resolvedDocId = resolveDocumentId(citation.docId || 'unknown');
                if (!resolvedDocId) {
                  console.error(`❌ Could not find document for citation: ${citation.docId}`);
                  alert(`Could not find document: ${docName}`);
                  return;
                }
                onCitationClick({
                  id: `page-${index}`,
                  sourceDocId: resolvedDocId,
                  section: citation.section,
                  page: page,
                  documentName: docName,
                  clickable: true,
                  url: '',
                  type: 'page',
                  confidence: citation.confidence,
                  // Prefer the citation's verbatim `quote` (new direct-quote
                  // contract from the AI) so the highlighter searches the
                  // PDF for the exact source span. Fall back to the
                  // surrounding narrative only when no quote is provided.
                  text: (citation as any).quote || text,
                  // Section-local siblings + the index of this one so the
                  // modal can render prev/next citation buttons.
                  siblings: dedupedCitations.map((s, i) => ({
                    docId: s.docId,
                    docName: s.docName || s.docId || 'Document',
                    page: s.page || s.pageNumber || '?',
                    confidence: s.confidence,
                    text: (s as any).quote || text,
                    sectionSiblingIndex: i,
                  })),
                  siblingIndex: index,
                });
              }}
            />
          );
        })}
      </span>
    );
  };

  const renderTextWithCitations = (content: string, citations: Citation[]) => {
    // Replace [CITE: "quote"] with clickable citation links
    let processedContent = content;
    
    // First, handle the new [1 source] pattern
    const sourcePattern = /^(.+?)\[(\d+)\s+sources?\]$/;
    const sourceMatch = content.match(sourcePattern);
    if (sourceMatch) {
      const [, value] = sourceMatch;
      return (
        <span>
          {value}
          <button
            className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors ml-1"
            onClick={() => onCitationClick({
              type: 'quote',
              text: value,
              found: true
            })}
            title={`View citation for ${value}`}
          >
            📄
          </button>
        </span>
      );
    }
    
    // Then handle the original [CITE: "quote"] pattern
    const citationRegex = /\[CITE:\s*"([^"]+)"\]/g;
    let match;
    const citationMatches: { match: string; text: string; citation: Citation | undefined }[] = [];
    
    // Find all citation matches and their corresponding citation objects
    while ((match = citationRegex.exec(content)) !== null) {
      const quoteText = match[1];
      const correspondingCitation = citations.find(c => c.text === quoteText);
      citationMatches.push({
        match: match[0],
        text: quoteText,
        citation: correspondingCitation
      });
    }
    
    // Replace citation markers with clickable spans
    citationMatches.forEach(({ match, text, citation }, index) => {
      const citationId = citation?.id || `cite-${index}`;
      processedContent = processedContent.replace(
        match,
        `<span class="citation-link" data-citation-id="${citationId}" title="Click to view citation: ${text}">📄</span>`
      );
    });

    // Handle new JSON citation array format: [{"docId": "doc_1", "docName": "...", "page": 1, "section": "..."}]
    const jsonCitationMatches: { match: string; citations: any[] }[] = [];
    
    // Use a more robust approach to find JSON citation arrays
    // Look for bracket-enclosed content that might be JSON citations
    const bracketMatches = content.match(/\[[^\]]*?"docId"[^\]]*?\]/g);
    
    if (bracketMatches) {
      bracketMatches.forEach(match => {
        try {
          // Try to parse as JSON
          const parsedCitations = JSON.parse(match);
          if (Array.isArray(parsedCitations) && parsedCitations.length > 0) {
            // Check if at least the first item has docId (indicating it's a citation)
            if (parsedCitations[0] && parsedCitations[0].docId) {
              jsonCitationMatches.push({
                match: match,
                citations: parsedCitations
              });
            }
          }
        } catch (error) {
          // If it fails to parse, it's not a valid JSON citation
          console.warn('Could not parse potential JSON citation:', match.substring(0, 100) + '...', error);
        }
      });
    }
    
    // Replace JSON citation arrays with clickable buttons
    jsonCitationMatches.forEach(({ match, citations }, index) => {
      const citationButtons = citations.map((citation, citIndex) => {
        const citationId = `json-cite-${index}-${citIndex}`;
        const title = `${citation.docName || 'Document'} - Page ${citation.page || '?'} - ${citation.section || 'Section'}`;
        return `<button class="json-citation-link inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors ml-1" data-citation-data="${encodeURIComponent(JSON.stringify(citation))}" title="${title}">📄</button>`;
      }).join('');
      
      processedContent = processedContent.replace(match, citationButtons);
    });
    
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(processedContent) }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.classList.contains('citation-link')) {
            const citationId = target.getAttribute('data-citation-id');
            const citation = citations.find(c => c.id === citationId);
            if (citation) {
              onCitationClick(citation);
            }
          } else if (target.classList.contains('json-citation-link')) {
            // Handle JSON citation clicks
            const citationData = target.getAttribute('data-citation-data');
            if (citationData) {
              try {
                const citation = JSON.parse(decodeURIComponent(citationData));
                onCitationClick({
                  type: 'document',
                  text: `${citation.docName} - Page ${citation.page} - ${citation.section}`,
                  source: citation.docName,
                  page: citation.page,
                  section: citation.section,
                  found: true
                });
              } catch (error) {
                console.warn('Failed to parse citation data:', error);
              }
            }
          }
        }}
      />
    );
  };

  const renderMatrix = (matrix: any, index: number) => {
    // Debug: Log matrix structure to understand the data format
    console.log('🔍 Matrix debug info:', {
      title: matrix.title,
      headers: matrix.headers,
      rowCount: matrix.rows?.length,
      firstRowStructure: matrix.rows?.[0] ? {
        isArray: Array.isArray(matrix.rows[0]),
        keys: Array.isArray(matrix.rows[0]) ? 'array' : Object.keys(matrix.rows[0]),
        sampleData: matrix.rows[0]
      } : 'no rows'
    });

    // Special debug logging for Risk Mitigation Strategies matrix
    if (matrix.title === 'Risk Mitigation Strategies' && matrix.rows?.length > 0) {
      console.log('🔍 Risk Mitigation Strategies debug:', {
        headers: matrix.headers,
        firstRowKeys: Array.isArray(matrix.rows[0]) ? 'array-based' : Object.keys(matrix.rows[0]),
        riskAreaValue: Array.isArray(matrix.rows[0]) ? matrix.rows[0][0] : matrix.rows[0]['Risk Area'],
        allRowData: matrix.rows[0]
      });
    }

    // Special debug logging for Premium Analysis matrix
    if (matrix.title === 'Premium Analysis' && matrix.rows?.length > 0) {
      console.log('🔍 Premium Analysis debug:', {
        headers: matrix.headers,
        firstRowKeys: Array.isArray(matrix.rows[0]) ? 'array-based' : Object.keys(matrix.rows[0]),
        costComponentValue: Array.isArray(matrix.rows[0]) ? matrix.rows[0][0] : matrix.rows[0]['Cost Component'],
        allRowData: matrix.rows[0]
      });
    }
    
    // Calculate intelligent column widths based on content and headers
    const getColumnWidths = (matrix: any) => {
      const headers = matrix.headers || [];
      const columnCount = headers.length;

      if (columnCount === 0) return [];

      // Define intelligent width distributions for common matrix types
      if (columnCount === 3) {
        // For 3-column matrices (like Pro/Con Analysis)
        // Give more space to content-heavy columns
        if (headers.some((h: string) => h.toLowerCase().includes('advantage') || h.toLowerCase().includes('disadvantage'))) {
          return ['25%', '37.5%', '37.5%']; // First column smaller, content columns larger
        }
        return ['30%', '35%', '35%']; // Slightly biased toward content
      }

      if (columnCount === 4) {
        // For 4-column matrices (like Coverage Architecture)
        return ['20%', '25%', '25%', '30%']; // Last column (analysis) gets more space
      }

      if (columnCount === 5) {
        // For 5-column matrices (like Financial Evolution)
        return ['25%', '20%', '20%', '17.5%', '17.5%']; // Balanced but slightly favor first column
      }

      // Fallback: equal distribution
      const equalWidth = (100 / columnCount).toFixed(1) + '%';
      return Array(columnCount).fill(equalWidth);
    };

    const columnWidths = getColumnWidths(matrix);

    // Debug log to verify width calculations
    console.log('🎯 Matrix width calculation:', {
      matrixTitle: matrix.title,
      headers: matrix.headers,
      columnCount: matrix.headers?.length,
      calculatedWidths: columnWidths
    });
    
    // Generate section ID matching WorkflowReport.tsx createSectionId logic
    // WorkflowReport uses: `matrix-${createSectionId(matrix.title)}`
    const baseSectionId = matrix.title
      ? matrix.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
      : `${index}`;
    const sectionId = `matrix-${baseSectionId}`;

    return (
      <div id={sectionId} key={index} className="analysis-matrix mb-8 keep-together">
        <h3 className="text-xl font-bold mb-2 text-gray-800 dark:text-white">{matrix.title}</h3>
        {matrix.description && (
          <div className="text-gray-600 dark:text-gray-300 mb-4">
            {renderSafeContent(matrix.description, `Matrix ${index + 1} Description`, hideCitations)}
          </div>
        )}
        
        <div className="overflow-x-auto" style={{ position: 'relative', width: '100%' }}>
          <table 
            className="w-full border-collapse border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm matrix-table" 
            style={{ 
              tableLayout: 'fixed', 
              width: '100%',
              minWidth: '100%',
              pageBreakInside: 'avoid'
            }}
          >
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                {matrix.headers.map((header: string, i: number) => (
                  <th
                    key={i}
                    className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-800 dark:text-white"
                    style={{ width: columnWidths[i] || '20%' }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((row: any, rowIndex: number) => {
                // New cells-array shape from the AI: {cells: [{column, value,
                // citations}]}. Translate to the legacy {<header>: {value,
                // citations}} dict so the existing render path handles both.
                let normalizedRow = row;
                if (
                  row &&
                  typeof row === 'object' &&
                  !Array.isArray(row) &&
                  Array.isArray((row as any).cells)
                ) {
                  const out: Record<string, any> = {};
                  (row as any).cells.forEach((c: any) => {
                    if (c && typeof c === 'object' && typeof c.column === 'string') {
                      out[c.column] = { value: c.value ?? '', citations: c.citations || [] };
                    }
                  });
                  normalizedRow = out;
                }
                return (
                <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  {Array.isArray(normalizedRow) ? (
                    // New array-based structure from prompt
                    normalizedRow.map((cell: any, cellIndex: number) => (
                      <td
                        key={cellIndex}
                        className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 break-words"
                        style={{ width: columnWidths[cellIndex] || '20%', maxWidth: '300px' }}
                      >
                        {(() => {
                          
                          if (typeof cell === 'object' && cell !== null) {
                            if (cell.text) {
                              return renderSafeContent(cell as CitationText, `Matrix ${matrix.title || index + 1} Cell`, hideCitations);
                            } else if (cell.hasOwnProperty('value')) {
                              // Handle objects with value property (like matrix cells)
                              return (
                                <span className="font-medium">
                                  {cell.value}
                                  {cell.citations && cell.citations.length > 0 && !hideCitations && (
                                    <>
                                      {(() => {
                                        // Use CitationMarker so matrix cells look IDENTICAL to
                                        // inline prose citations across every workflow (was
                                        // previously emitting a different "Doc - Page N" pill).
                                        const order: string[] = [];
                                        cell.citations.forEach((c: any) => {
                                          const k = c.docId || c.docName || 'doc';
                                          if (!order.includes(k)) order.push(k);
                                        });
                                        const letterMap = new Map<string, string>();
                                        if (order.length > 1) {
                                          order.forEach((k, i) => letterMap.set(k, String.fromCharCode(65 + (i % 26))));
                                        }
                                        return cell.citations.map((citation: any, citIndex: number) => {
                                          const docKey = citation.docId || citation.docName || 'doc';
                                          return (
                                            <CitationMarker
                                              key={citIndex}
                                              citation={{
                                                docName: citation.docName || citation.docId || 'Document',
                                                docId: citation.docId,
                                                page: citation.page,
                                                confidence: citation.confidence,
                                              }}
                                              sourceLetter={letterMap.get(docKey)}
                                              onClick={() => onCitationClick({
                                                type: 'page',
                                                page: citation.page,
                                                section: citation.section,
                                                documentId: citation.docId,
                                                documentName: citation.docName,
                                                confidence: citation.confidence,
                                                text: citation.quote || cell.value,
                                                found: true,
                                              })}
                                            />
                                          );
                                        });
                                      })()}
                                    </>
                                  )}
                                </span>
                              );
                            } else {
                              return renderSafeContent(cell, `Matrix ${matrix.title || index + 1} Cell`, hideCitations);
                            }
                          } else if (typeof cell === 'string') {
                            return renderSafeContent(cell, `Matrix ${matrix.title || index + 1} Cell`, hideCitations);
                          } else {
                            return String(cell || '');
                          }
                        })()}
                      </td>
                    ))
                  ) : (
                    // Legacy object-based structure (also reached when we've
                    // normalized a new cells-array shape to header-keyed dict).
                    matrix.headers.map((header: string, cellIndex: number) => {
                      // Handle edge case where AI might generate array data but frontend expects object data
                      // Convert array-based row to object-based row using headers as keys
                      let processedRow = normalizedRow;
                      if (Array.isArray(normalizedRow) && matrix.headers && matrix.headers.length === normalizedRow.length) {
                        processedRow = {};
                        matrix.headers.forEach((headerKey: string, idx: number) => {
                          processedRow[headerKey] = normalizedRow[idx];
                        });
                        console.log('🔄 Converted array-based row to object-based for matrix:', matrix.title, processedRow);
                      }
                      // For document comparison matrices, map document name headers to simple keys
                      let cellKey = header.toLowerCase().replace(/\s+/g, '').replace(/[^\w]/g, '');
                      
                      // Check if this is a document column (not the first column which is usually the comparison item)
                      // and if the header looks like a document name (contains file extension or specific patterns)
                      if (cellIndex > 0 && (header.includes('.pdf') || header.includes('.PDF') || 
                          header.includes('Policy') || header.includes('Document'))) {
                        // Try to use simple doc1, doc2, etc. keys
                        const docIndex = cellIndex; // cellIndex 1 = doc1, cellIndex 2 = doc2, etc.
                        const simpleKey = `doc${docIndex}`;
                        if (processedRow[simpleKey] !== undefined) {
                          cellKey = simpleKey;
                        }
                      }
                      
                      // Enhanced key lookup with multiple fallback strategies to handle inconsistent key formats
                      // This fixes the issue where "Risk Area" column data was intermittently missing
                      const rawValue = processedRow[cellKey] || // Try transformed key first (e.g., "riskarea")
                                       processedRow[header] ||  // Try exact header match (e.g., "Risk Area")
                                       processedRow[header.toLowerCase()] || // Try lowercase header (e.g., "risk area")
                                       processedRow[header.trim()] || // Try trimmed header
                                       processedRow[header.replace(/\s+/g, '')] || // Try header without spaces (e.g., "RiskArea")
                                       processedRow[header.replace(/\s+/g, '').toLowerCase()] || // Try header without spaces, lowercase
                                       // Additional fallbacks for common patterns
                                       (Object.keys(processedRow).find(key => 
                                         key.toLowerCase().replace(/\s+/g, '') === header.toLowerCase().replace(/\s+/g, '')
                                       ) ? processedRow[Object.keys(processedRow).find(key => 
                                         key.toLowerCase().replace(/\s+/g, '') === header.toLowerCase().replace(/\s+/g, '')
                                       )!] : '') || '';
                      
                      // Debug logging for Risk Area column specifically
                      if (header === 'Risk Area' && !rawValue && matrix.title === 'Risk Mitigation Strategies') {
                        console.warn('🚨 Risk Area column empty!', {
                          header,
                          cellKey,
                          availableKeys: Object.keys(processedRow),
                          rowData: processedRow,
                          originalRowData: row,
                          matrixTitle: matrix.title
                        });
                      }

                      // Debug logging for Cost Component column specifically
                      if (header === 'Cost Component' && !rawValue && matrix.title === 'Premium Analysis') {
                        console.warn('🚨 Cost Component column empty!', {
                          header,
                          cellKey,
                          availableKeys: Object.keys(processedRow),
                          rowData: processedRow,
                          originalRowData: row,
                          matrixTitle: matrix.title
                        });
                      }
                      
                      // Extract the actual value - handle both simple values and {value, citations} objects
                      const value = rawValue;
                      
                      if (header.toLowerCase().includes('citation')) {
                        return (
                          <td
                            key={cellIndex}
                            className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm"
                            style={{ width: columnWidths[cellIndex] || '20%' }}
                          >
                            {processedRow.citations?.map((citation: Citation, citIndex: number) => (
                              <CitationMarker
                                key={citIndex}
                                citation={{
                                  docName: citation.docName || citation.docId || 'Document',
                                  docId: citation.docId,
                                  page: citation.page,
                                  confidence: (citation as any).confidence,
                                }}
                                onClick={() => onCitationClick({
                                  ...citation,
                                  type: 'page',
                                  sourceDocId: citation.docId || (citation as any).sourceDocId,
                                  documentId: citation.docId || (citation as any).sourceDocId,
                                  documentName: citation.docName,
                                  text: (citation as any).quote || (citation as any).text,
                                  clickable: true,
                                  url: citation.docId || (citation as any).sourceDocId,
                                  found: true,
                                })}
                              />
                            ))}
                          </td>
                        );
                      }
                      
                      return (
                        <td
                          key={cellIndex}
                          className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                          style={{ width: columnWidths[cellIndex] || '20%' }}
                        >
                          {(() => {
                            console.log('🚨 NEW DEBUG: Matrix cell value debug:', {
                              matrixTitle: matrix.title,
                              cellIndex: cellIndex,
                              valueType: typeof value,
                              value: value,
                              isNull: value === null,
                              hasText: value?.text,
                              hasCitations: value?.citations
                            });

                            if (typeof value === 'object' && value !== null) {
                              if (value.text && value.citations) {
                                console.log('🔍 Using text+citations path for:', value);
                                return renderSafeContent(value as CitationText, `Matrix ${matrix.title || index + 1} Value Cell`, hideCitations);
                              } else if (value.value !== undefined) {
                                // Handle objects with value property (like matrix cells with citations)
                                return (
                                  <span className="font-medium">
                                    {value.value}
                                    {value.citations && value.citations.length > 0 && !hideCitations && (
                                      <span className="ml-1 inline-flex items-center gap-0.5">
                                        {value.citations.map((citation: any, citIndex: number) => (
                                          <CitationMarker
                                            key={citIndex}
                                            citation={{
                                              docName: citation.docName || citation.docId || 'Document',
                                              docId: citation.docId,
                                              page: citation.page,
                                              confidence: citation.confidence,
                                            }}
                                            onClick={() => onCitationClick({
                                              type: 'page',
                                              page: citation.page,
                                              section: citation.section,
                                              sourceDocId: citation.docId,
                                              documentId: citation.docId,
                                              documentName: citation.docName,
                                              text: citation.quote || value.value || value.text,
                                              clickable: true,
                                              url: citation.docId,
                                              found: true,
                                            })}
                                          />
                                        ))}
                                      </span>
                                    )}
                                  </span>
                                );
                              } else {
                                console.log('🔍 Using generic object path for:', value);
                                return renderSafeContent(value as CitationText, `Matrix ${matrix.title || index + 1} Value Cell`, hideCitations);
                              }
                            } else if (typeof value === 'string') {
                              console.log('🔍 Using string path for:', value);
                              return renderSafeContent(value, `Matrix ${matrix.title || index + 1} Value Cell`, hideCitations);
                            } else {
                              console.log('🔍 Using fallback string conversion for:', value);
                              return String(value || '');
                            }
                          })()}
                        </td>
                      );
                    })
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const getWorkflowTypeDisplay = (workflowType: string) => {
    const types: { [key: string]: { title: string; icon: string; color: string; bgClass: string; borderClass: string; badgeClass: string; textClass: string } } = {
      'policy_comparison': {
        title: 'Policy Comparison',
        icon: '⚖️',
        color: 'blue',
        bgClass: 'bg-blue-50 dark:bg-blue-900/20',
        borderClass: 'border-blue-200 dark:border-blue-700',
        badgeClass: 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
        textClass: 'text-blue-800 dark:text-blue-200'
      },
      'document_comparison': {
        title: 'Document Comparison',
        icon: '📄',
        color: 'blue',
        bgClass: 'bg-blue-50 dark:bg-blue-900/20',
        borderClass: 'border-blue-200 dark:border-blue-700',
        badgeClass: 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
        textClass: 'text-blue-800 dark:text-blue-200'
      },
      'policy_analysis': { 
        title: 'Policy Analysis', 
        icon: '🔍', 
        color: 'green',
        bgClass: 'bg-green-50 dark:bg-green-900/20',
        borderClass: 'border-green-200 dark:border-green-700',
        badgeClass: 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200',
        textClass: 'text-green-800 dark:text-green-200'
      },
      'loss_run_analysis': {
        title: 'Loss Run Analysis',
        icon: '📈',
        color: 'orange',
        bgClass: 'bg-orange-50 dark:bg-orange-900/20',
        borderClass: 'border-orange-200 dark:border-orange-700',
        badgeClass: 'bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200',
        textClass: 'text-orange-800 dark:text-orange-200'
      },
      'loss_run_analyzer': {
        title: 'Loss Run Analyzer',
        icon: '📊',
        color: 'amber',
        bgClass: 'bg-amber-50 dark:bg-amber-900/20',
        borderClass: 'border-amber-200 dark:border-amber-700',
        badgeClass: 'bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200',
        textClass: 'text-amber-800 dark:text-amber-200'
      },
      'research_analysis': { 
        title: 'Research Analysis', 
        icon: '🔬', 
        color: 'purple',
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        borderClass: 'border-purple-200 dark:border-purple-700',
        badgeClass: 'bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200',
        textClass: 'text-purple-800 dark:text-purple-200'
      },
      'risk_assessment': { 
        title: 'Risk Assessment', 
        icon: '⚠️', 
        color: 'red',
        bgClass: 'bg-red-50 dark:bg-red-900/20',
        borderClass: 'border-red-200 dark:border-red-700',
        badgeClass: 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200',
        textClass: 'text-red-800 dark:text-red-200'
      },
      'renewal_analysis': { 
        title: 'Renewal Analysis Report', 
        icon: '🔄', 
        color: 'teal',
        bgClass: 'bg-teal-50 dark:bg-teal-900/20',
        borderClass: 'border-teal-200 dark:border-teal-700',
        badgeClass: 'bg-teal-100 dark:bg-teal-800 text-teal-800 dark:text-teal-200',
        textClass: 'text-teal-800 dark:text-teal-200'
      },
      'year_over_year': {
        title: 'Year Over Year Report',
        icon: '📊',
        color: 'indigo',
        bgClass: 'bg-indigo-50 dark:bg-indigo-900/20',
        borderClass: 'border-indigo-200 dark:border-indigo-700',
        badgeClass: 'bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200',
        textClass: 'text-indigo-800 dark:text-indigo-200'
      },
      'coverage_analysis': {
        title: 'Coverage Analysis',
        icon: '🛡️',
        color: 'cyan',
        bgClass: 'bg-cyan-50 dark:bg-cyan-900/20',
        borderClass: 'border-cyan-200 dark:border-cyan-700',
        badgeClass: 'bg-cyan-100 dark:bg-cyan-800 text-cyan-800 dark:text-cyan-200',
        textClass: 'text-cyan-800 dark:text-cyan-200'
      },
      'insurance_requirements': {
        title: 'Insurance Requirements Analysis',
        icon: '📋',
        color: 'purple',
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        borderClass: 'border-purple-200 dark:border-purple-700',
        badgeClass: 'bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200',
        textClass: 'text-purple-800 dark:text-purple-200'
      },
      'yoy_renewal': {
        title: 'Multi-Line Renewal Review',
        icon: '📈',
        color: 'indigo',
        bgClass: 'bg-indigo-50 dark:bg-indigo-900/20',
        borderClass: 'border-indigo-200 dark:border-indigo-700',
        badgeClass: 'bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200',
        textClass: 'text-indigo-800 dark:text-indigo-200'
      },
      'personal_lines_yoy_renewal': {
        title: 'Personal Lines Renewal Review',
        icon: '🏠',
        color: 'teal',
        bgClass: 'bg-teal-50 dark:bg-teal-900/20',
        borderClass: 'border-teal-200 dark:border-teal-700',
        badgeClass: 'bg-teal-100 dark:bg-teal-800 text-teal-800 dark:text-teal-200',
        textClass: 'text-teal-800 dark:text-teal-200'
      },
      'commercial_lines_exec_renewal': {
        title: 'Executive Renewal Summary',
        icon: '🏢',
        color: 'amber',
        bgClass: 'bg-amber-50 dark:bg-amber-900/20',
        borderClass: 'border-amber-200 dark:border-amber-700',
        badgeClass: 'bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200',
        textClass: 'text-amber-800 dark:text-amber-200'
      },
      'client_renewal_presentation': {
        title: 'Client Renewal Presentation',
        icon: '📋',
        color: 'violet',
        bgClass: 'bg-violet-50 dark:bg-violet-900/20',
        borderClass: 'border-violet-200 dark:border-violet-700',
        badgeClass: 'bg-violet-100 dark:bg-violet-800 text-violet-800 dark:text-violet-200',
        textClass: 'text-violet-800 dark:text-violet-200'
      },
      'financial_analysis': {
        title: 'Financial Analysis',
        icon: '💰',
        color: 'emerald',
        bgClass: 'bg-emerald-50 dark:bg-emerald-900/20',
        borderClass: 'border-emerald-200 dark:border-emerald-700',
        badgeClass: 'bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200',
        textClass: 'text-emerald-800 dark:text-emerald-200'
      },
      'financial_analysis_comparative': {
        title: 'Financial Analysis',
        icon: '💰',
        color: 'emerald',
        bgClass: 'bg-emerald-50 dark:bg-emerald-900/20',
        borderClass: 'border-emerald-200 dark:border-emerald-700',
        badgeClass: 'bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200',
        textClass: 'text-emerald-800 dark:text-emerald-200'
      },
      'rate_analysis': {
        title: 'Rate Analysis',
        icon: '📊',
        color: 'indigo',
        bgClass: 'bg-indigo-50 dark:bg-indigo-900/20',
        borderClass: 'border-indigo-200 dark:border-indigo-700',
        badgeClass: 'bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200',
        textClass: 'text-indigo-800 dark:text-indigo-200'
      },
      'portfolio_analyzer': {
        title: 'Portfolio Analyzer',
        icon: '📋',
        color: 'slate',
        bgClass: 'bg-slate-50 dark:bg-slate-900/20',
        borderClass: 'border-slate-200 dark:border-slate-700',
        badgeClass: 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200',
        textClass: 'text-slate-800 dark:text-slate-200'
      },
      'homeowners_comparison': {
        title: 'Homeowners Insurance Comparison',
        icon: '🏠',
        color: 'teal',
        bgClass: 'bg-teal-50 dark:bg-teal-900/20',
        borderClass: 'border-teal-200 dark:border-teal-700',
        badgeClass: 'bg-teal-100 dark:bg-teal-800 text-teal-800 dark:text-teal-200',
        textClass: 'text-teal-800 dark:text-teal-200'
      }
    };

    // Normalize the workflow type - handle variations from AI responses
    const normalizedType = workflowType.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_v\d+.*$/, '');

    // Format unknown workflow types: "document_comparison" → "Document Comparison"
    const formatTitle = (slug: string) => slug.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return types[workflowType] || types[normalizedType] || {
      title: formatTitle(workflowType),
      icon: '📊',
      color: 'gray',
      bgClass: 'bg-gray-50 dark:bg-gray-900/20',
      borderClass: 'border-gray-200 dark:border-gray-700',
      badgeClass: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200',
      textClass: 'text-gray-800 dark:text-gray-200'
    };
  };

  // Determine effective workflow type - skip 'existing_report' as it's not a real workflow type
  const effectiveWorkflowType = (() => {
    // 1. Use prop if it's a real workflow type (not 'existing_report')
    if (propWorkflowType && propWorkflowType !== 'existing_report') {
      return propWorkflowType;
    }

    // 2. Try responseMetadata.workflowType
    if (data.responseMetadata?.workflowType) {
      return data.responseMetadata.workflowType;
    }

    // 3. Detect from data structure
    const anyData = data as any;
    if (anyData.complianceMatrix || anyData.executiveSummary?.overallStatus) {
      return 'insurance_requirements';
    }
    if (anyData.executiveRenewalSummary || anyData.riskIntelligenceReport) {
      return 'commercial_lines_exec_renewal';
    }
    if (anyData.restrictiveEndorsements || (anyData.coverageHighlights && anyData.programOverview)) {
      return 'client_renewal_presentation';
    }
    if (anyData.lossRunSummary || anyData.detailedClaimsList || anyData.severityBreakdown) {
      return 'loss_run_analysis';
    }
    if (anyData.yearOverYearAnalysis || anyData.trendAnalysis) {
      return 'year_over_year';
    }
    if (anyData.financialRiskAssessment || anyData.ermRiskMapping || anyData.underwritingRecommendation) {
      return 'financial_analysis';
    }
    if (anyData.portfolioStructure && anyData.portfolioSynthesis) {
      return 'portfolio_analyzer';
    }

    // 4. Default fallback
    return 'policy_comparison';
  })();
  const workflowDisplay = getWorkflowTypeDisplay(effectiveWorkflowType);

  // Enhanced popup window with improved page break handling
  const handleDOMPdfExport = async () => {
    try {
      console.log('📄 Creating enhanced PDF export window with better page break handling...');
      
      if (!reportRef.current) {
        throw new Error('Report content not found');
      }

      // Create a new window with just the report content
      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      if (!printWindow) {
        throw new Error('Could not open print window - popup blocker may be active');
      }

      // Get all the styles from the current page
      const currentStyles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n');
          } catch (e) {
            return '';
          }
        })
        .join('\n');

      // Clone the report content
      const reportClone = reportRef.current.cloneNode(true) as HTMLElement;
      
      // Remove interactive elements from clone
      reportClone.querySelectorAll('button, .export-controls, .cursor-pointer').forEach(el => {
        el.remove();
      });

      // Generate filename
      const workflowType = data.responseMetadata?.workflowType || 'report';
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${workflowType.replace(/_/g, '-')}-landscape-${timestamp}.pdf`;

      // Create the print document with improved page break handling
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Report Export - ${filename}</title>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
          <style>
            ${currentStyles}
            
            /* Download button styles */
            .download-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 9999;
              background: #1e40af;
              padding: 15px 20px;
              border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            }
            
            .download-btn {
              background: #ffffff;
              color: #1e40af;
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              font-weight: bold;
              cursor: pointer;
              font-size: 14px;
              margin-right: 10px;
            }
            
            .download-btn:hover {
              background: #f3f4f6;
            }
            
            .download-btn:disabled {
              background: #e5e7eb;
              color: #9ca3af;
              cursor: not-allowed;
            }
            
            .close-btn {
              background: transparent;
              color: white;
              border: 1px solid white;
              padding: 8px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 12px;
            }
            
            .close-btn:hover {
              background: rgba(255,255,255,0.1);
            }
          </style>
        </head>
        <body>
          <div class="download-controls">
            <button class="download-btn" onclick="downloadPDF()" id="downloadBtn">📄 Download PDF</button>
            <button class="close-btn" onclick="window.close()">✕ Close</button>
          </div>
          
          ${reportClone.outerHTML}
          
          <script>
            async function downloadPDF() {
              const downloadBtn = document.getElementById('downloadBtn');
              const originalText = downloadBtn.textContent;
              
              try {
                // Update button to show progress
                downloadBtn.disabled = true;
                downloadBtn.textContent = '⏳ Generating PDF...';
                
                // Hide download controls before capture
                document.querySelector('.download-controls').style.display = 'none';
                
                // Get the report content
                const reportElement = document.querySelector('.universal-json-report') || document.body;
                
                // Generate canvas with high quality and better content detection
                const canvas = await html2canvas(reportElement, {
                  scale: 2,
                  useCORS: true,
                  allowTaint: true,
                  backgroundColor: '#ffffff',
                  logging: false,
                  width: reportElement.scrollWidth,
                  height: reportElement.scrollHeight,
                  onclone: (clonedDoc) => {
                    const style = clonedDoc.createElement('style');
                    style.textContent = \`
                      * {
                        -webkit-print-color-adjust: exact !important;
                        color-adjust: exact !important;
                        print-color-adjust: exact !important;
                      }
                      .bg-red-50, .bg-yellow-50, .bg-green-50, .bg-blue-50, .bg-gray-50, .bg-purple-50 {
                        opacity: 1 !important;
                      }
                      body, div, p, span, h1, h2, h3, h4, h5, h6 {
                        opacity: 1 !important;
                        visibility: visible !important;
                      }
                    \`;
                    clonedDoc.head.appendChild(style);
                  }
                });
                
                // Create PDF in landscape orientation
                const { jsPDF } = window.jspdf;
                const pdf = new jsPDF({
                  orientation: 'landscape',
                  unit: 'mm',
                  format: 'a4'
                });
                
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                const margin = 10;
                const imgWidth = pdfWidth - (margin * 2);
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                
                const imgData = canvas.toDataURL('image/png', 0.95);
                
                // Enhanced page break detection - find content blocks
                const detectContentBlocks = () => {
                  const reportElement = document.querySelector('.universal-json-report');
                  if (!reportElement) return [];
                  
                  const contentSelectors = [
                    '.analysis-matrix',
                    '.matrix-container', 
                    'table',
                    '.key-finding',
                    '.recommendation',
                    '.strategy',
                    '.content-block',
                    '.keep-together',
                    '.bg-red-50',
                    '.bg-yellow-50', 
                    '.bg-green-50',
                    '.bg-blue-50',
                    '.bg-gray-50',
                    '.bg-purple-50',
                    '.rounded-lg',
                    '.border'
                  ];
                  
                  const blocks = [];
                  contentSelectors.forEach(selector => {
                    const elements = reportElement.querySelectorAll(selector);
                    elements.forEach(el => {
                      const rect = el.getBoundingClientRect();
                      const reportRect = reportElement.getBoundingClientRect();
                      const relativeTop = rect.top - reportRect.top + reportElement.scrollTop;
                      const relativeBottom = relativeTop + rect.height;
                      
                      blocks.push({
                        element: el,
                        top: relativeTop,
                        bottom: relativeBottom,
                        height: rect.height,
                        selector: selector
                      });
                    });
                  });
                  
                  // Sort by position and merge overlapping blocks
                  blocks.sort((a, b) => a.top - b.top);
                  const mergedBlocks = [];
                  
                  blocks.forEach(block => {
                    const lastMerged = mergedBlocks[mergedBlocks.length - 1];
                    if (lastMerged && block.top <= lastMerged.bottom + 20) {
                      // Merge overlapping blocks
                      lastMerged.bottom = Math.max(lastMerged.bottom, block.bottom);
                      lastMerged.height = lastMerged.bottom - lastMerged.top;
                    } else {
                      mergedBlocks.push(block);
                    }
                  });
                  
                  return mergedBlocks;
                };
                
                const contentBlocks = detectContentBlocks();
                console.log('📄 Detected', contentBlocks.length, 'content blocks for smart page breaks');
                
                let yPosition = 0;
                let pageNumber = 1;
                const pageContentHeight = pdfHeight - (margin * 2);
                
                // Add content to PDF with smart page breaks
                while (yPosition < imgHeight) {
                  if (pageNumber > 1) {
                    pdf.addPage();
                  }
                  
                  let currentPageHeight = pageContentHeight;
                  const remainingHeight = imgHeight - yPosition;
                  const maxPossibleHeight = Math.min(pageContentHeight, remainingHeight);
                  
                  // Convert current position to document pixels
                  const currentPositionPx = (yPosition / imgHeight) * reportElement.scrollHeight;
                  const proposedEndPositionPx = currentPositionPx + (maxPossibleHeight / imgHeight) * reportElement.scrollHeight;
                  
                  // Find content blocks that would be split by this page break
                  const conflictingBlocks = contentBlocks.filter(block => 
                    block.top < proposedEndPositionPx && 
                    block.bottom > proposedEndPositionPx &&
                    block.height > 30 // Only consider blocks taller than 30px
                  );
                  
                  if (conflictingBlocks.length > 0) {
                    console.log(\`📄 Page \${pageNumber}: Avoiding \${conflictingBlocks.length} content block conflicts\`);
                    
                    // Find the earliest conflicting block
                    const earliestBlock = conflictingBlocks.reduce((earliest, current) => 
                      current.top < earliest.top ? current : earliest
                    );
                    
                    // Calculate height to break just before this block
                    const breakBeforeHeight = (earliestBlock.top - currentPositionPx) / reportElement.scrollHeight * imgHeight;
                    
                    // If breaking before gives us reasonable content (at least 25% of page)
                    if (breakBeforeHeight > pageContentHeight * 0.25) {
                      currentPageHeight = Math.max(breakBeforeHeight, pageContentHeight * 0.15);
                    } else {
                      // Try to include the entire conflicting block
                      const latestBlock = conflictingBlocks.reduce((latest, current) => 
                        current.bottom > latest.bottom ? current : latest
                      );
                      const includeBlockHeight = (latestBlock.bottom - currentPositionPx) / reportElement.scrollHeight * imgHeight;
                      
                      // If including the block doesn't make the page too long (within 150% of normal)
                      if (includeBlockHeight <= pageContentHeight * 1.5) {
                        currentPageHeight = includeBlockHeight;
                      } else {
                        // Use default height as last resort
                        currentPageHeight = maxPossibleHeight;
                      }
                    }
                  } else {
                    currentPageHeight = maxPossibleHeight;
                  }
                  
                  // Ensure boundaries
                  currentPageHeight = Math.min(currentPageHeight, remainingHeight);
                  currentPageHeight = Math.max(currentPageHeight, pageContentHeight * 0.1);
                  
                  // Calculate source rectangle for this page
                  const sourceY = (yPosition / imgHeight) * canvas.height;
                  const sourceHeight = (currentPageHeight / imgHeight) * canvas.height;
                  
                  // Create a canvas for this page slice
                  const pageCanvas = document.createElement('canvas');
                  const pageCtx = pageCanvas.getContext('2d');
                  
                  if (pageCtx) {
                    pageCanvas.width = canvas.width;
                    pageCanvas.height = sourceHeight;
                    
                    // Draw the slice
                    pageCtx.drawImage(
                      canvas,
                      0, sourceY, canvas.width, sourceHeight,
                      0, 0, canvas.width, sourceHeight
                    );
                    
                    const pageImgData = pageCanvas.toDataURL('image/png', 0.95);
                    
                    // Add to PDF
                    pdf.addImage(pageImgData, 'PNG', margin, margin, imgWidth, currentPageHeight);
                  }
                  
                  // Add page number
                  pdf.setFontSize(8);
                  pdf.setTextColor(100, 100, 100);
                  pdf.text(\`Page \${pageNumber}\`, pdfWidth - 30, pdfHeight - 5);
                  
                  yPosition += currentPageHeight;
                  pageNumber++;
                  
                  // Safety check
                  if (pageNumber > 50) {
                    console.warn('PDF generation stopped - too many pages');
                    break;
                  }
                }
                
                // Download the PDF
                pdf.save('${filename}');
                
                // Update button to show success
                downloadBtn.textContent = '✅ Downloaded!';
                setTimeout(() => {
                  downloadBtn.textContent = originalText;
                  downloadBtn.disabled = false;
                  document.querySelector('.download-controls').style.display = 'block';
                }, 2000);
                
              } catch (error) {
                console.error('PDF generation failed:', error);
                alert('PDF generation failed: ' + error.message);
                downloadBtn.textContent = originalText;
                downloadBtn.disabled = false;
                document.querySelector('.download-controls').style.display = 'block';
              }
            }
            
            // Keyboard shortcut
            document.addEventListener('keydown', function(e) {
              if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                downloadPDF();
              }
            });
          </script>
        </body>
        </html>
      `);

      printWindow.document.close();
      
      console.log('✅ Enhanced PDF export window opened with smart page break detection');
      
    } catch (error) {
      console.error('❌ Export failed:', error);
      alert('Export failed. Please try the manual export option.');
      throw error;
    }
  };

  // Export Handler (Enhanced with DOM capture option)
  const handleExport = async (options: ExportOptions) => {
    try {
      const metadata = {
        generatedAt: data.responseMetadata?.generatedAt || new Date().toISOString(),
        comparisonType: data.responseMetadata?.workflowType || 'policy_comparison',
        documentsAnalyzed: data.responseMetadata?.documentsAnalyzed || [],
        confidence: data.responseMetadata?.confidence || 'Medium',
        processingTime: data.responseMetadata?.processingTime
      };

      // Check if we should use DOM-based export for PDF (preserves exact styling)
      // Skip DOM-based export for specific workflows - use text-based for better handling
      const workflowsUsingTextExport = ['loss_run_analysis', 'insurance_requirements', 'commercial_lines_exec_renewal', 'policy_comparison', 'homeowners_comparison', 'personal_umbrella'];
      const useTextBasedExport = workflowsUsingTextExport.includes(data.responseMetadata?.workflowType || '');
      if (options.formatType === 'pdf' && options.preserveStyling !== false && !useTextBasedExport) {
        await handleDOMPdfExport();
        return;
      }

      console.log('🔍 EXPORT DEBUG: Using traditional export service for:', options.formatType);

      if (options.formatType === 'docx') {
        await JsonStructuredPdfService.exportUniversalJsonToWord(data, metadata, options);
      } else {
        // Use text-based PDF export for selectable text
        await JsonStructuredPdfService.exportUniversalJsonToTextPDF(data, metadata, options);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  };

  return (
    <div ref={reportRef} className={`universal-json-report max-w-7xl mx-auto p-6 ${className}`}>
      {/* Header with Metadata */}
      {compactHeader ? (
        <>
          {/* Portal the export buttons into the outer page header if a slot
              ref was provided. Otherwise render the compact toolbar above
              the report content. */}
          {showExportControls && exportPortalRef?.current
            ? createPortal(
                <ExportControls
                  onExport={handleExport}
                  className="export-controls"
                  currentCitationState={!hideCitations}
                  reportContentRef={reportRef}
                  customPdfExportHandler={customPdfExportHandler}
                  reportData={data}
                  clientName={customerName}
                />,
                exportPortalRef.current
              )
            : null}
          {/* The slim local toolbar — confidence pill plus exports when
              there's no portal slot. Hidden entirely when we've portaled. */}
          {(!exportPortalRef?.current) && (
            <div className="flex items-center justify-end gap-3 mb-6 flex-wrap">
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${workflowDisplay.badgeClass}`}>
                {data.responseMetadata?.confidence || 'Medium'} Confidence
              </div>
              {showExportControls && (
                <ExportControls
                  onExport={handleExport}
                  className="export-controls"
                  currentCitationState={!hideCitations}
                  reportContentRef={reportRef}
                  customPdfExportHandler={customPdfExportHandler}
                  reportData={data}
                  clientName={customerName}
                />
              )}
            </div>
          )}
        </>
      ) : (
      <div className={`${workflowDisplay.bgClass} border ${workflowDisplay.borderClass} rounded-lg p-6 mb-8`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-3xl print:text-lg">{workflowDisplay.icon}</span>
            <h1 className="text-3xl print:text-lg font-bold text-gray-800 dark:text-white">
              {customerName ? `${customerName} - ${workflowDisplay.title}` : workflowDisplay.title}
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${workflowDisplay.badgeClass}`}>
              {data.responseMetadata?.confidence || 'Medium'} Confidence
            </div>
            {showExportControls && (
              <ExportControls
                onExport={handleExport}
                className="ml-3 export-controls"
                currentCitationState={!hideCitations}
                reportContentRef={reportRef}
                customPdfExportHandler={customPdfExportHandler}
                reportData={data}
                clientName={customerName}
              />
            )}
          </div>
        </div>
        
        {/* Metadata section removed per user request */}
        {false && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Generated:</span>
            <div className="text-gray-800 dark:text-gray-200">
              {new Date(data.responseMetadata?.generatedAt || Date.now()).toLocaleString()}
            </div>
          </div>
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Documents:</span>
            <div className="text-gray-800 dark:text-gray-200">
              {data.responseMetadata?.documentsAnalyzed?.length || 0}
            </div>
          </div>
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-300">Citations:</span>
            <div className="text-gray-800 dark:text-gray-200">
              {(() => {
                // Count actual citations in the content
                let totalCitations = 0;
                
                // Count citations in executive summary
                data.executiveSummary?.paragraphs?.forEach(p => {
                  if (p.content?.citations) totalCitations += p.content.citations.length;
                });
                
                // Count citations in matrices
                data.analysisMatrices?.forEach(matrix => {
                  matrix.rows?.forEach(row => {
                    if (Array.isArray(row)) {
                      row.forEach(cell => {
                        if (cell?.citations) totalCitations += cell.citations.length;
                      });
                    }
                  });
                });
                
                // Count citations in key findings
                if (data.keyFindings?.summary?.citations) totalCitations += data.keyFindings.summary.citations.length;
                data.keyFindings?.findings?.forEach(f => {
                  // Handle both simple and complex finding structures
                  if (f.finding) {
                    if (typeof f.finding === 'object' && 'citations' in f.finding) {
                      // Simple CitationText structure
                      totalCitations += f.finding.citations.length;
                    } else if (typeof f.finding === 'object') {
                      // Complex structure with observation, implication, recommendation
                      if (f.finding.observation?.citations) totalCitations += f.finding.observation.citations.length;
                      if (f.finding.implication?.citations) totalCitations += f.finding.implication.citations.length;
                      if (f.finding.recommendation?.citations) totalCitations += f.finding.recommendation.citations.length;
                    }
                  }
                });
                
                // Count citations in recommendations
                data.recommendations?.forEach(rec => {
                  if (rec.action?.citations) totalCitations += rec.action.citations.length;
                  if (rec.rationale?.citations) totalCitations += rec.rationale.citations.length;
                });
                
                return totalCitations > 0 ? totalCitations : data.citationIndex?.totalCitations || 'N/A';
              })()}
            </div>
          </div>
          {data.responseMetadata?.processingTime && (
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-300">Processing Time:</span>
              <div className="text-gray-800 dark:text-gray-200">
                {((data.responseMetadata.processingTime || 0) / 1000).toFixed(1)}s
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      )}

      {/* Sections container - uses flexbox for reordering */}
      <div className="sections-container flex flex-col">

      {/* Executive Summary - Skip for insurance requirements, commercial_lines_exec_renewal, client_renewal_presentation, and rate_analysis (they have their own structures) */}
      {shouldShowSection('executive-summary') && data.responseMetadata?.workflowType !== 'insurance_requirements' && data.responseMetadata?.workflowType !== 'commercial_lines_exec_renewal' && data.responseMetadata?.workflowType !== 'client_renewal_presentation' && data.responseMetadata?.workflowType !== 'rate_analysis' && !(data as any).executiveRenewalSummary && !((data as any).coverageHighlights && (data as any).restrictiveEndorsements) && !(data as any).analysisTables && !(data as any).driverHierarchy && (
      <div {...getSectionDataAttrs('executive-summary')} style={getSectionStyles('executive-summary')}>
      <section id="executive-summary" className="mb-10">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
          Executive Summary
        </h2>
        {(() => {
          // Enhanced error handling for Executive Summary
          if (!data.executiveSummary) {
            console.warn('🔍 Executive Summary: No executiveSummary data found');
            return <div className="text-gray-500 italic p-5 bg-gray-50 dark:bg-gray-800 rounded-lg">
              No executive summary available in this report.
            </div>;
          }

          if (!data.executiveSummary.paragraphs || data.executiveSummary.paragraphs.length === 0) {
            console.warn('🔍 Executive Summary: No paragraphs found, trying alternative structures');

            // Check for PORTFOLIO ANALYZER structure (tldr, bottomLine, keyFindings, criticalIssues)
            const portfolioSummary = data.executiveSummary as any;
            if (portfolioSummary.tldr || portfolioSummary.bottomLine || portfolioSummary.keyFindings) {
              console.log('🔍 Executive Summary: Detected portfolio analyzer structure');

              // Format currency values
              const formatCurrency = (value: number) => {
                if (value === undefined || value === null) return 'N/A';
                return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
              };

              return (
                <div className="space-y-6">
                  {/* TL;DR Section */}
                  {portfolioSummary.tldr && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-700">
                      <h3 className="text-lg font-semibold mb-3 text-blue-800 dark:text-blue-300">TL;DR</h3>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent(portfolioSummary.tldr, 'Portfolio TL;DR', hideCitations)}
                      </div>
                    </div>
                  )}

                  {/* Bottom Line Numbers */}
                  {portfolioSummary.bottomLine && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-700">
                      <h3 className="text-lg font-semibold mb-4 text-green-800 dark:text-green-300">Bottom Line</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {portfolioSummary.bottomLine.maximumAvailableCoverage !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Maximum Available Coverage</div>
                            <div className="text-xl font-bold text-green-700 dark:text-green-300">
                              {formatCurrency(portfolioSummary.bottomLine.maximumAvailableCoverage)}
                            </div>
                          </div>
                        )}
                        {portfolioSummary.bottomLine.probableRecovery !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Probable Recovery</div>
                            <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                              {formatCurrency(portfolioSummary.bottomLine.probableRecovery)}
                            </div>
                          </div>
                        )}
                        {portfolioSummary.bottomLine.insuredTotalRetention !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Insured Total Retention</div>
                            <div className="text-xl font-bold text-orange-700 dark:text-orange-300">
                              {formatCurrency(portfolioSummary.bottomLine.insuredTotalRetention)}
                            </div>
                          </div>
                        )}
                        {portfolioSummary.bottomLine.coverageGaps !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Coverage Gaps</div>
                            <div className="text-xl font-bold text-red-700 dark:text-red-300">
                              {formatCurrency(portfolioSummary.bottomLine.coverageGaps)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Key Findings */}
                  {portfolioSummary.keyFindings && Array.isArray(portfolioSummary.keyFindings) && portfolioSummary.keyFindings.length > 0 && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 p-5 rounded-lg border border-indigo-200 dark:border-indigo-700">
                      <h3 className="text-lg font-semibold mb-3 text-indigo-800 dark:text-indigo-300">Key Findings</h3>
                      <ul className="space-y-3">
                        {portfolioSummary.keyFindings.map((finding: any, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-indigo-500 mt-1">•</span>
                            <div className="text-gray-700 dark:text-gray-300">
                              {renderSafeContent(finding, `Key Finding ${index + 1}`, hideCitations)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Critical Issues */}
                  {portfolioSummary.criticalIssues && Array.isArray(portfolioSummary.criticalIssues) && portfolioSummary.criticalIssues.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-700">
                      <h3 className="text-lg font-semibold mb-3 text-red-800 dark:text-red-300">Critical Issues</h3>
                      <ul className="space-y-3">
                        {portfolioSummary.criticalIssues.map((issue: any, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-red-500 mt-1">•</span>
                            <div className="text-gray-700 dark:text-gray-300">
                              {renderSafeContent(issue, `Critical Issue ${index + 1}`, hideCitations)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Critical Actions (homeowners comparison) */}
                  {portfolioSummary.criticalActions && Array.isArray(portfolioSummary.criticalActions) && portfolioSummary.criticalActions.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-700">
                      <h3 className="text-lg font-semibold mb-3 text-red-800 dark:text-red-300">Critical Actions</h3>
                      <ul className="space-y-3">
                        {portfolioSummary.criticalActions.map((action: any, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-red-500 mt-1">•</span>
                            <div className="text-gray-700 dark:text-gray-300">
                              {renderSafeContent(action, `Critical Action ${index + 1}`, hideCitations)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Overall Assessment (homeowners comparison) */}
                  {portfolioSummary.overallAssessment && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-300">Overall Assessment</h3>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent(portfolioSummary.overallAssessment, 'Overall Assessment', hideCitations)}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Check for SIMPLIFIED loss run analysis structure (flat fields)
            const lossRunSummary = data.executiveSummary as any;
            if (lossRunSummary.totalClaims || lossRunSummary.totalIncurred || lossRunSummary.totalPaid) {
              console.log('🔍 Executive Summary: Detected simplified loss run structure');

              // Format currency values
              const formatCurrency = (value: string | number) => {
                if (!value) return 'N/A';
                const num = typeof value === 'string' ? parseFloat(value.replace(/[,$]/g, '')) : value;
                if (isNaN(num)) return value;
                return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
              };

              return (
                <div className="space-y-6">
                  {/* Portfolio Overview */}
                  <div id="portfolio-overview" className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-700">
                    <h3 className="text-lg font-semibold mb-4 text-blue-800 dark:text-blue-300">Portfolio Overview</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {lossRunSummary.totalClaims && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Total Claims</div>
                          <div className="text-xl font-bold text-blue-700 dark:text-blue-300">{lossRunSummary.totalClaims}</div>
                        </div>
                      )}
                      {lossRunSummary.openClaimsCount && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Open Claims</div>
                          <div className="text-xl font-bold text-orange-700 dark:text-orange-300">{lossRunSummary.openClaimsCount}</div>
                        </div>
                      )}
                      {lossRunSummary.closedClaimsCount && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Closed Claims</div>
                          <div className="text-xl font-bold text-green-700 dark:text-green-300">{lossRunSummary.closedClaimsCount}</div>
                        </div>
                      )}
                      {lossRunSummary.dateRange && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Date Range</div>
                          <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{lossRunSummary.dateRange}</div>
                        </div>
                      )}
                      {lossRunSummary.carriersProcessed && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm col-span-2">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Carriers</div>
                          <div className="text-lg font-semibold text-purple-700 dark:text-purple-300">{lossRunSummary.carriersProcessed}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div id="financial-summary" className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-700">
                    <h3 className="text-lg font-semibold mb-4 text-green-800 dark:text-green-300">Financial Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {lossRunSummary.totalIncurred && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Total Incurred</div>
                          <div className="text-lg font-bold text-red-700 dark:text-red-300">{formatCurrency(lossRunSummary.totalIncurred)}</div>
                        </div>
                      )}
                      {lossRunSummary.totalPaid && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Total Paid</div>
                          <div className="text-lg font-bold text-orange-700 dark:text-orange-300">{formatCurrency(lossRunSummary.totalPaid)}</div>
                        </div>
                      )}
                      {lossRunSummary.outstandingReserves && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Outstanding Reserves</div>
                          <div className="text-lg font-bold text-yellow-700 dark:text-yellow-300">{formatCurrency(lossRunSummary.outstandingReserves)}</div>
                        </div>
                      )}
                      {lossRunSummary.averageClaimSize && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Average Claim</div>
                          <div className="text-lg font-bold text-green-700 dark:text-green-300">{formatCurrency(lossRunSummary.averageClaimSize)}</div>
                        </div>
                      )}
                      {lossRunSummary.largestClaim && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Largest Claim</div>
                          <div className="text-lg font-bold text-red-700 dark:text-red-300">{lossRunSummary.largestClaim}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            // Check for Financial Analysis structure (has text, financialCondition, trajectory, recommendation)
            if (lossRunSummary.text && (lossRunSummary.financialCondition || lossRunSummary.trajectory)) {
              console.log('🔍 Executive Summary: Detected financial analysis structure');
              return (
                <div className="space-y-6">
                  {/* Main Summary Text */}
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 p-6 rounded-r-lg">
                    <p className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                      {lossRunSummary.text}
                    </p>
                    {/* Citations for Executive Summary */}
                    {!hideCitations && lossRunSummary.citations && lossRunSummary.citations.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {lossRunSummary.citations.map((citation: any, citIdx: number) => (
                          <button
                            key={citIdx}
                            onClick={() => onCitationClick({
                              ...citation,
                              type: 'page',
                              sourceDocId: citation.docId,
                              documentId: citation.docId,
                              documentName: citation.docName,
                              clickable: true,
                              url: citation.docId,
                              found: true
                            })}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                            title={`${citation.docName} - Page ${citation.page}${citation.section ? ` - ${citation.section}` : ''}${citation.quote ? `\n"${citation.quote}"` : ''}`}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>p.{citation.page}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Financial Status Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {lossRunSummary.financialCondition && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Financial Condition</div>
                        <div className={`text-lg font-semibold ${
                          lossRunSummary.financialCondition.toLowerCase().includes('strong') || lossRunSummary.financialCondition.toLowerCase().includes('healthy') ? 'text-green-600 dark:text-green-400' :
                          lossRunSummary.financialCondition.toLowerCase().includes('weak') || lossRunSummary.financialCondition.toLowerCase().includes('poor') ? 'text-red-600 dark:text-red-400' :
                          'text-yellow-600 dark:text-yellow-400'
                        }`}>
                          {lossRunSummary.financialCondition}
                        </div>
                      </div>
                    )}
                    {lossRunSummary.trajectory && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Trajectory</div>
                        <div className={`text-lg font-semibold ${
                          lossRunSummary.trajectory.toLowerCase().includes('improv') || lossRunSummary.trajectory.toLowerCase().includes('upward') ? 'text-green-600 dark:text-green-400' :
                          lossRunSummary.trajectory.toLowerCase().includes('declin') || lossRunSummary.trajectory.toLowerCase().includes('downward') ? 'text-red-600 dark:text-red-400' :
                          'text-gray-700 dark:text-gray-300'
                        }`}>
                          {lossRunSummary.trajectory}
                        </div>
                      </div>
                    )}
                    {lossRunSummary.recommendation && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Recommendation</div>
                        <div className={`text-lg font-semibold ${
                          lossRunSummary.recommendation.toLowerCase().includes('accept') || lossRunSummary.recommendation.toLowerCase().includes('approve') ? 'text-green-600 dark:text-green-400' :
                          lossRunSummary.recommendation.toLowerCase().includes('decline') || lossRunSummary.recommendation.toLowerCase().includes('reject') ? 'text-red-600 dark:text-red-400' :
                          'text-yellow-600 dark:text-yellow-400'
                        }`}>
                          {lossRunSummary.recommendation}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            // Check for YOY Renewal structure FIRST (has renewalOverview, recommendation, recommendedPathway)
            // Must check BEFORE old loss run forensic which also has financialSummary
            if (lossRunSummary.renewalOverview || lossRunSummary.recommendedPathway ||
                (lossRunSummary.recommendation && lossRunSummary.topAdvantages)) {
              console.log('🔍 Executive Summary: Detected yoy_renewal structure');

              return (
                <div className="space-y-6">
                  {/* Analysis Track */}
                  {lossRunSummary.analysisTrack && (
                    <div id="analysis-track" className="bg-indigo-50 dark:bg-indigo-900/20 p-5 rounded-lg border-l-4 border-indigo-500">
                      <h3 className="text-lg font-semibold mb-3 text-indigo-800 dark:text-indigo-300">Analysis Track</h3>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent(lossRunSummary.analysisTrack, 'Analysis Track', hideCitations)}
                      </div>
                    </div>
                  )}

                  {/* Renewal Overview */}
                  {lossRunSummary.renewalOverview && (
                    <div id="renewal-overview" className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border-l-4 border-blue-500">
                      <h3 className="text-lg font-semibold mb-3 text-blue-800 dark:text-blue-300">Renewal Overview</h3>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent(lossRunSummary.renewalOverview, 'Renewal Overview', hideCitations)}
                      </div>
                    </div>
                  )}

                  {/* Financial Summary */}
                  {lossRunSummary.financialSummary && (
                    <div id="financial-summary-yoy" className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border-l-4 border-green-500">
                      <h3 className="text-lg font-semibold mb-3 text-green-800 dark:text-green-300">Financial Summary</h3>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent(lossRunSummary.financialSummary, 'Financial Summary', hideCitations)}
                      </div>
                    </div>
                  )}

                  {/* Top Advantages */}
                  {lossRunSummary.topAdvantages && lossRunSummary.topAdvantages.length > 0 && (
                    <div id="top-advantages" className="bg-emerald-50 dark:bg-emerald-900/20 p-5 rounded-lg border-l-4 border-emerald-500">
                      <h3 className="text-lg font-semibold mb-3 text-emerald-800 dark:text-emerald-300">Top Advantages</h3>
                      <ul className="space-y-2">
                        {lossRunSummary.topAdvantages.map((advantage: any, idx: number) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span className="text-gray-700 dark:text-gray-300">
                              {renderSafeContent(advantage, `Advantage ${idx + 1}`, hideCitations)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Top Concerns */}
                  {lossRunSummary.topConcerns && lossRunSummary.topConcerns.length > 0 && (
                    <div id="top-concerns" className="bg-amber-50 dark:bg-amber-900/20 p-5 rounded-lg border-l-4 border-amber-500">
                      <h3 className="text-lg font-semibold mb-3 text-amber-800 dark:text-amber-300">Top Concerns</h3>
                      <ul className="space-y-2">
                        {lossRunSummary.topConcerns.map((concern: any, idx: number) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-amber-500 mt-1">⚠</span>
                            <span className="text-gray-700 dark:text-gray-300">
                              {renderSafeContent(concern, `Concern ${idx + 1}`, hideCitations)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Strategic Trade-Off */}
                  {lossRunSummary.strategicTradeOff && (
                    <div id="strategic-trade-off" className="bg-purple-50 dark:bg-purple-900/20 p-5 rounded-lg border-l-4 border-purple-500">
                      <h3 className="text-lg font-semibold mb-3 text-purple-800 dark:text-purple-300">Strategic Trade-Off</h3>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent(lossRunSummary.strategicTradeOff, 'Strategic Trade-Off', hideCitations)}
                      </div>
                    </div>
                  )}

                  {/* Recommendation */}
                  {lossRunSummary.recommendation && (
                    <div id="recommendation" className="bg-blue-100 dark:bg-blue-900/30 p-5 rounded-lg border-l-4 border-blue-600">
                      <h3 className="text-lg font-semibold mb-3 text-blue-900 dark:text-blue-200">Recommendation</h3>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent(lossRunSummary.recommendation, 'Recommendation', hideCitations)}
                      </div>
                    </div>
                  )}

                  {/* Recommended Pathway */}
                  {lossRunSummary.recommendedPathway && (
                    <div id="recommended-pathway" className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-lg p-4 flex items-center gap-3">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <div>
                        <div className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide">Recommended Pathway</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">{lossRunSummary.recommendedPathway}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Check for OLD loss run forensic analysis structure (nested)
            // Only match if it has portfolioOverview or extractionQuality (not just financialSummary)
            if (lossRunSummary.portfolioOverview || lossRunSummary.extractionQuality ||
                (lossRunSummary.financialSummary && !lossRunSummary.renewalOverview && !lossRunSummary.recommendedPathway)) {
              console.log('🔍 Executive Summary: Detected old loss run forensic structure');

              // Helper to render RichText or plain string with citations
              const renderRichTextValue = (value: any) => {
                if (!value) return null;
                // If it's a RichText object with text and citations
                if (typeof value === 'object' && value.text) {
                  return renderCitationText(value, hideCitations);
                }
                // Otherwise render as plain text (backward compatible)
                return <span>{typeof value === 'string' ? value : JSON.stringify(value)}</span>;
              };

              return (
                <div className="space-y-6">
                  {/* Portfolio Overview */}
                  {lossRunSummary.portfolioOverview && (
                    <div id="portfolio-overview" className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-700">
                      <h3 className="text-lg font-semibold mb-4 text-blue-800 dark:text-blue-300">Portfolio Overview</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {lossRunSummary.portfolioOverview.totalClaims && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Claims</div>
                            <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                              {renderRichTextValue(lossRunSummary.portfolioOverview.totalClaims)}
                            </div>
                          </div>
                        )}
                        {lossRunSummary.portfolioOverview.carriersProcessed && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Carriers Processed</div>
                            <div className="text-xl font-bold text-purple-700 dark:text-purple-300">
                              {renderRichTextValue(lossRunSummary.portfolioOverview.carriersProcessed)}
                            </div>
                          </div>
                        )}
                        {lossRunSummary.portfolioOverview.dateRange && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Date Range</div>
                            <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                              {renderRichTextValue(lossRunSummary.portfolioOverview.dateRange)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Financial Summary */}
                  {lossRunSummary.financialSummary && (
                    <div id="financial-summary" className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-700">
                      <h3 className="text-lg font-semibold mb-4 text-green-800 dark:text-green-300">Financial Summary</h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {lossRunSummary.financialSummary.totalIncurred && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Incurred</div>
                            <div className="text-lg font-bold text-red-700 dark:text-red-300">
                              {renderRichTextValue(lossRunSummary.financialSummary.totalIncurred)}
                            </div>
                          </div>
                        )}
                        {lossRunSummary.financialSummary.totalPaid && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Paid</div>
                            <div className="text-lg font-bold text-orange-700 dark:text-orange-300">
                              {renderRichTextValue(lossRunSummary.financialSummary.totalPaid)}
                            </div>
                          </div>
                        )}
                        {lossRunSummary.financialSummary.outstandingReserves && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Outstanding Reserves</div>
                            <div className="text-lg font-bold text-yellow-700 dark:text-yellow-300">
                              {renderRichTextValue(lossRunSummary.financialSummary.outstandingReserves)}
                            </div>
                          </div>
                        )}
                        {lossRunSummary.financialSummary.averageClaim && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Average Claim</div>
                            <div className="text-lg font-bold text-green-700 dark:text-green-300">
                              {renderRichTextValue(lossRunSummary.financialSummary.averageClaim)}
                            </div>
                          </div>
                        )}
                        {lossRunSummary.financialSummary.medianClaim && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Median Claim</div>
                            <div className="text-lg font-bold text-teal-700 dark:text-teal-300">
                              {renderRichTextValue(lossRunSummary.financialSummary.medianClaim)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Key Risk Insights */}
                  {lossRunSummary.keyRiskInsights && (
                    <div id="key-risk-insights" className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-700">
                      <h3 className="text-lg font-semibold mb-4 text-red-800 dark:text-red-300">Key Risk Insights</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {lossRunSummary.keyRiskInsights.primaryLossDriver && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Primary Loss Driver</div>
                            <div className="text-lg font-semibold text-red-700 dark:text-red-300">
                              {renderRichTextValue(lossRunSummary.keyRiskInsights.primaryLossDriver)}
                            </div>
                          </div>
                        )}
                        {lossRunSummary.keyRiskInsights.highestRiskLocation && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Highest Risk Location</div>
                            <div className="text-lg font-semibold text-orange-700 dark:text-orange-300">
                              {renderRichTextValue(lossRunSummary.keyRiskInsights.highestRiskLocation)}
                            </div>
                          </div>
                        )}
                        {lossRunSummary.keyRiskInsights.openClaimExposure && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Open Claim Exposure</div>
                            <div className="text-lg font-semibold text-yellow-700 dark:text-yellow-300">
                              {renderRichTextValue(lossRunSummary.keyRiskInsights.openClaimExposure)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Extraction Quality */}
                  {lossRunSummary.extractionQuality && (
                    <div id="extraction-quality" className="bg-purple-50 dark:bg-purple-900/20 p-5 rounded-lg border border-purple-200 dark:border-purple-700">
                      <h3 className="text-lg font-semibold mb-4 text-purple-800 dark:text-purple-300">Extraction Quality Report</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {lossRunSummary.extractionQuality.filesProcessed && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Files Processed</div>
                            <div className="text-lg font-bold text-purple-700 dark:text-purple-300">{formatNumber(lossRunSummary.extractionQuality.filesProcessed)}</div>
                          </div>
                        )}
                        {lossRunSummary.extractionQuality.claimsExtracted && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Claims Extracted</div>
                            <div className="text-lg font-bold text-blue-700 dark:text-blue-300">{formatNumber(lossRunSummary.extractionQuality.claimsExtracted)}</div>
                          </div>
                        )}
                        {lossRunSummary.extractionQuality.confidence && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Confidence Level</div>
                            <div className={`text-lg font-bold ${
                              lossRunSummary.extractionQuality.confidence === 'High' ? 'text-green-700 dark:text-green-300' :
                              lossRunSummary.extractionQuality.confidence === 'Medium' ? 'text-yellow-700 dark:text-yellow-300' :
                              'text-red-700 dark:text-red-300'
                            }`}>{lossRunSummary.extractionQuality.confidence}</div>
                          </div>
                        )}
                        {lossRunSummary.extractionQuality.carriersIdentified && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Carriers Identified</div>
                            <div className="text-lg font-semibold text-indigo-700 dark:text-indigo-300">{lossRunSummary.extractionQuality.carriersIdentified}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Check for NEW loss_run_analyzer structure (portfolioSnapshot, keyFindings, lossDrivers)
            if (lossRunSummary.portfolioSnapshot || lossRunSummary.keyFindings || lossRunSummary.lossDrivers) {
              console.log('🔍 Executive Summary: Detected loss_run_analyzer structure');

              const getTrendColor = (trend: string) => {
                switch (trend?.toLowerCase()) {
                  case 'improving': return 'text-green-700 dark:text-green-300';
                  case 'deteriorating': return 'text-red-700 dark:text-red-300';
                  default: return 'text-yellow-700 dark:text-yellow-300';
                }
              };

              const getTrendIcon = (trend: string) => {
                switch (trend?.toLowerCase()) {
                  case 'improving': return '↗️';
                  case 'deteriorating': return '↘️';
                  default: return '➡️';
                }
              };

              const getImpactBadge = (impact: string) => {
                switch (impact?.toLowerCase()) {
                  case 'high': return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200';
                  case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200';
                  default: return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200';
                }
              };

              return (
                <div className="space-y-6">
                  {/* Portfolio Snapshot */}
                  {lossRunSummary.portfolioSnapshot && (
                    <div id="portfolio-snapshot" className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-700">
                      <h3 className="text-lg font-semibold mb-4 text-blue-800 dark:text-blue-300">Portfolio Snapshot</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {lossRunSummary.portfolioSnapshot.totalCarriers !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Carriers</div>
                            <div className="text-xl font-bold text-purple-700 dark:text-purple-300">{lossRunSummary.portfolioSnapshot.totalCarriers}</div>
                          </div>
                        )}
                        {lossRunSummary.portfolioSnapshot.totalPolicies !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Policies</div>
                            <div className="text-xl font-bold text-indigo-700 dark:text-indigo-300">{lossRunSummary.portfolioSnapshot.totalPolicies}</div>
                          </div>
                        )}
                        {lossRunSummary.portfolioSnapshot.totalClaims !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Claims</div>
                            <div className="text-xl font-bold text-blue-700 dark:text-blue-300">{lossRunSummary.portfolioSnapshot.totalClaims}</div>
                          </div>
                        )}
                        {lossRunSummary.portfolioSnapshot.totalIncurred && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Incurred</div>
                            <div className="text-lg font-bold text-red-700 dark:text-red-300">{formatCurrency(lossRunSummary.portfolioSnapshot.totalIncurred)}</div>
                          </div>
                        )}
                        {lossRunSummary.portfolioSnapshot.openClaimsCount !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Open Claims</div>
                            <div className="text-xl font-bold text-orange-700 dark:text-orange-300">{lossRunSummary.portfolioSnapshot.openClaimsCount}</div>
                          </div>
                        )}
                        {lossRunSummary.portfolioSnapshot.closedClaimsCount !== undefined && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Closed Claims</div>
                            <div className="text-xl font-bold text-green-700 dark:text-green-300">{lossRunSummary.portfolioSnapshot.closedClaimsCount}</div>
                          </div>
                        )}
                        {lossRunSummary.portfolioSnapshot.dateRange && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm col-span-2">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Date Range</div>
                            <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{lossRunSummary.portfolioSnapshot.dateRange}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* v3.1 fallback: Trend Direction box only when no v4.1 trendAnalysis is present.
                      v4.1 § 12 / § 15.1: trend label is structurally relocated to the dedicated
                      Trend Analysis section below (after Loss Drivers, before Analyst Notes). */}
                  {lossRunSummary.trendDirection && !lossRunSummary.trendAnalysis && (
                    <div id="trend-direction" className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getTrendIcon(lossRunSummary.trendDirection)}</span>
                        <div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Overall Trend</div>
                          <div className={`text-xl font-bold ${getTrendColor(lossRunSummary.trendDirection)}`}>
                            {lossRunSummary.trendDirection}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Key Findings */}
                  {lossRunSummary.keyFindings && lossRunSummary.keyFindings.length > 0 && (
                    <div id="key-findings" className="bg-amber-50 dark:bg-amber-900/20 p-5 rounded-lg border border-amber-200 dark:border-amber-700">
                      <h3 className="text-lg font-semibold mb-4 text-amber-800 dark:text-amber-300">Key Findings</h3>
                      <div className="space-y-3">
                        {lossRunSummary.keyFindings.map((item: any, idx: number) => {
                          // Handle both old format (finding is string) and new format (finding is {text, citations})
                          const findingContent = typeof item.finding === 'object' && item.finding?.text
                            ? item.finding  // Pass full object with citations
                            : item.finding;
                          return (
                            <div key={idx} className="bg-white dark:bg-gray-800 p-4 rounded shadow-sm overflow-hidden">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <span className={`px-3 py-1.5 rounded text-xs font-semibold ${getImpactBadge(item.impact)}`}>
                                  {item.impact || 'N/A'} Impact
                                </span>
                              </div>
                              <div className="text-gray-800 dark:text-gray-200 break-words overflow-wrap-anywhere">
                                {renderSafeContent(findingContent, `Key Finding ${idx + 1}`, hideCitations)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Loss Drivers */}
                  {lossRunSummary.lossDrivers && lossRunSummary.lossDrivers.length > 0 && (
                    <div id="loss-drivers" className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-700">
                      <h3 className="text-lg font-semibold mb-4 text-red-800 dark:text-red-300">Loss Drivers</h3>
                      <div className="flex flex-wrap gap-2">
                        {lossRunSummary.lossDrivers.map((driver: string, idx: number) => (
                          <span key={idx} className="bg-red-100 dark:bg-red-800/50 text-red-800 dark:text-red-200 px-3 py-1 rounded-full text-sm font-medium">
                            {driver}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trend Analysis (v4.1) — TextWithCitations whose text holds the five
                      required components (## Year-over-Year Frequency, ## Year-over-Year
                      Severity, ## Open-Claim Aging, ## Reporting Lag Distribution,
                      ## Development Indicators) followed by ## Synthesis as the closing
                      element. The synthesis label mirrors lossRunSummary.trendDirection. */}
                  {lossRunSummary.trendAnalysis && (
                    <div id="trend-analysis" className="bg-slate-50 dark:bg-slate-900/30 p-5 rounded-lg border border-slate-200 dark:border-slate-700">
                      <h3 className="text-lg font-semibold mb-3 text-slate-800 dark:text-slate-200">Trend Analysis</h3>
                      <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {renderSafeContent(lossRunSummary.trendAnalysis, 'Trend Analysis', hideCitations)}
                      </div>
                      {lossRunSummary.trendDirection && (
                        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center gap-3">
                          <span className="text-2xl">{getTrendIcon(lossRunSummary.trendDirection)}</span>
                          <div>
                            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Synthesis</div>
                            <div className={`text-xl font-bold ${getTrendColor(lossRunSummary.trendDirection)}`}>
                              {lossRunSummary.trendDirection}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Analyst Notes */}
                  {lossRunSummary.analystNotes && (
                    <div id="analyst-notes" className="bg-purple-50 dark:bg-purple-900/20 p-5 rounded-lg border border-purple-200 dark:border-purple-700">
                      <h3 className="text-lg font-semibold mb-3 text-purple-800 dark:text-purple-300">Analyst Notes</h3>
                      <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {renderSafeContent(lossRunSummary.analystNotes, 'Analyst Notes', hideCitations)}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Try alternative executive summary structures
            const altSummary = (data.executiveSummary as any).summary ||
                              (data.executiveSummary as any).content ||
                              (data.executiveSummary as any).text ||
                              data.executiveSummary;

            if (altSummary && typeof altSummary === 'object' && altSummary !== data.executiveSummary) {
              return <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-lg">
                {renderSafeContent(altSummary, 'Executive Summary', hideCitations)}
              </div>;
            }

            if (typeof altSummary === 'string' && altSummary.trim()) {
              return <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-lg">
                {altSummary}
              </div>;
            }

            return <div className="text-gray-500 italic p-5 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>Executive summary structure not recognized.</div>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800">
                  Debug: Show executiveSummary data
                </summary>
                <pre className="text-xs mt-2 bg-white dark:bg-gray-900 p-2 rounded overflow-auto max-h-40">
                  {JSON.stringify(data.executiveSummary, null, 2)}
                </pre>
              </details>
            </div>;
          }

          return data.executiveSummary.paragraphs.map((paragraph, index) => (
            <div key={index} className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-lg">
              {renderSafeContent(paragraph.content, `Executive Summary Paragraph ${index + 1}`, hideCitations)}
            </div>
          ));
        })()}
      </section>
      </div>
      )}

      {/* Loss Run Summary */}
      {shouldShowSection('loss-run-summary') && (data as any).lossRunSummary && (
      <div {...getSectionDataAttrs('loss-run-summary')} style={getSectionStyles('loss-run-summary')}>
      <section id="loss-run-summary" className="mb-10">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
          Loss Run Summary
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {(data as any).lossRunSummary.analysisTimeframe && (
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Analysis Period</div>
              <div className="text-lg font-bold text-blue-700 dark:text-blue-300">
                {(data as any).lossRunSummary.analysisTimeframe}
              </div>
            </div>
          )}
          {(data as any).lossRunSummary.totalClaims && (
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Claims</div>
              <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
                {(data as any).lossRunSummary.totalClaims}
              </div>
            </div>
          )}
          {(data as any).lossRunSummary.averageClaimSize && (
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Claim Size</div>
              <div className="text-lg font-bold text-green-700 dark:text-green-300">
                {(data as any).lossRunSummary.averageClaimSize}
              </div>
            </div>
          )}
          {(data as any).lossRunSummary.severityTrend && (
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Severity Trend</div>
              <div className="text-lg font-bold text-orange-700 dark:text-orange-300">
                {(data as any).lossRunSummary.severityTrend}
              </div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {(data as any).lossRunSummary.totalIncurred && (
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Incurred</div>
              <div className="text-lg font-bold text-red-700 dark:text-red-300">
                {(data as any).lossRunSummary.totalIncurred}
              </div>
            </div>
          )}
          {(data as any).lossRunSummary.totalPaid && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Paid</div>
              <div className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                {(data as any).lossRunSummary.totalPaid}
              </div>
            </div>
          )}
          {(data as any).lossRunSummary.lossRatio && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Loss Ratio</div>
              <div className="text-lg font-bold text-yellow-700 dark:text-yellow-300">
                {(data as any).lossRunSummary.lossRatio}
              </div>
            </div>
          )}
          {(data as any).lossRunSummary.frequencyRate && (
            <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Frequency Rate</div>
              <div className="text-lg font-bold text-teal-700 dark:text-teal-300">
                {(data as any).lossRunSummary.frequencyRate}
              </div>
            </div>
          )}
        </div>
      </section>
      </div>
      )}

      {/* Analysis Matrices - Skip for insurance requirements, loss_run_analysis, commercial_lines_exec_renewal, and financial_analysis (they have their own structures) */}
      {(() => {
        const wfType = data.responseMetadata?.workflowType?.toLowerCase() || '';
        const skipWorkflows = ['insurance_requirements', 'loss_run_analysis', 'commercial_lines_exec_renewal'];
        const isSkippedWorkflow = skipWorkflows.includes(wfType) || wfType.includes('financial_analysis');
        const hasExecRenewal = !!(data as any).executiveRenewalSummary;
        const hasMatrices = data.analysisMatrices && Array.isArray(data.analysisMatrices) && data.analysisMatrices.length > 0;
        const shouldShow = shouldShowSection('analysis-matrices') && !isSkippedWorkflow && !hasExecRenewal && hasMatrices;
        console.log('🔍 Analysis Matrices Check:', { wfType, isSkippedWorkflow, hasExecRenewal, hasMatrices, shouldShow });
        return shouldShow;
      })() && (
      <div {...getSectionDataAttrs('analysis-matrices')} style={getSectionStyles('analysis-matrices')}>
      <section id="analysis-matrices" className="mb-10">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
          Analysis Matrices
        </h2>
        {(() => {
          // Enhanced error handling for Analysis Matrices
          if (!data.analysisMatrices || data.analysisMatrices.length === 0) {
            console.warn('🔍 Analysis Matrices: No analysisMatrices found or empty array');

            // Check for NEW loss run forensic structure - render breakdowns instead
            const extData = data as any;
            if (extData.categoryBreakdown || extData.severityBreakdown || extData.riskHeatMap) {
              console.log('🔍 Analysis Matrices: Detected loss run breakdowns, rendering those instead');
              return (
                <div className="space-y-6">
                  {/* Category Breakdown */}
                  {extData.categoryBreakdown && Object.keys(extData.categoryBreakdown).length > 0 && (
                    <div id="category-breakdown" className="bg-indigo-50 dark:bg-indigo-900/20 p-5 rounded-lg border border-indigo-200 dark:border-indigo-700">
                      <h3 className="text-lg font-semibold mb-4 text-indigo-800 dark:text-indigo-300">Category Breakdown</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Count</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Incurred</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Percentage</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {Object.entries(extData.categoryBreakdown).map(([category, breakdown]: [string, any]) => (
                              <tr key={category} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{category}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatNumber(breakdown.count)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatCurrency(breakdown.totalIncurred)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{breakdown.percentage || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Severity Breakdown */}
                  {extData.severityBreakdown && Object.keys(extData.severityBreakdown).length > 0 && (
                    <div id="severity-breakdown" className="bg-orange-50 dark:bg-orange-900/20 p-5 rounded-lg border border-orange-200 dark:border-orange-700">
                      <h3 className="text-lg font-semibold mb-4 text-orange-800 dark:text-orange-300">Severity Breakdown</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Severity</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Count</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Incurred</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Percentage</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {Object.entries(extData.severityBreakdown).map(([severity, breakdown]: [string, any]) => (
                              <tr key={severity} className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                severity === 'Catastrophic' ? 'bg-red-50 dark:bg-red-900/10' :
                                severity === 'Severe' ? 'bg-orange-50 dark:bg-orange-900/10' :
                                severity === 'Moderate' ? 'bg-yellow-50 dark:bg-yellow-900/10' :
                                ''
                              }`}>
                                <td className={`px-4 py-3 text-sm font-medium ${
                                  severity === 'Catastrophic' ? 'text-red-700 dark:text-red-300' :
                                  severity === 'Severe' ? 'text-orange-700 dark:text-orange-300' :
                                  severity === 'Moderate' ? 'text-yellow-700 dark:text-yellow-300' :
                                  'text-gray-900 dark:text-white'
                                }`}>{severity}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatNumber(breakdown.count)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatCurrency(breakdown.totalIncurred)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{breakdown.percentage || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Risk Heat Map */}
                  {extData.riskHeatMap && Object.keys(extData.riskHeatMap).length > 0 && (
                    <div id="risk-heat-map" className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-700">
                      <h3 className="text-lg font-semibold mb-4 text-red-800 dark:text-red-300">Risk Heat Map (Location × Category)</h3>
                      <div className="overflow-x-auto print:overflow-visible">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 print:table-auto">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-normal break-words">Location</th>
                              {(() => {
                                const allCategories = new Set<string>();
                                Object.values(extData.riskHeatMap).forEach((locData: any) => {
                                  Object.keys(locData).forEach(cat => allCategories.add(cat));
                                });
                                return Array.from(allCategories).map(cat => (
                                  <th key={cat} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-normal break-words">{cat}</th>
                                ));
                              })()}
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {Object.entries(extData.riskHeatMap).map(([location, categories]: [string, any]) => {
                              const allCategories = new Set<string>();
                              Object.values(extData.riskHeatMap).forEach((locData: any) => {
                                Object.keys(locData).forEach(cat => allCategories.add(cat));
                              });
                              return (
                                <tr key={location} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-normal break-words">{location}</td>
                                  {Array.from(allCategories).map(cat => (
                                    <td key={cat} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-normal break-words">
                                      {categories[cat] || '-'}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Check for loss_run_analyzer structures - frequencyAnalysis and severityAnalysis
            if (extData.frequencyAnalysis || extData.severityAnalysis) {
              console.log('🔍 Analysis Matrices: Detected loss_run_analyzer structures (frequencyAnalysis/severityAnalysis)');
              return (
                <div className="space-y-6">
                  {/* Claims by Line of Business */}
                  {extData.frequencyAnalysis?.claimsByLOB && Array.isArray(extData.frequencyAnalysis.claimsByLOB) && extData.frequencyAnalysis.claimsByLOB.length > 0 && (
                    <div id="claims-by-lob" className="bg-indigo-50 dark:bg-indigo-900/20 p-5 rounded-lg border border-indigo-200 dark:border-indigo-700">
                      <h3 className="text-lg font-semibold mb-4 text-indigo-800 dark:text-indigo-300">Claims by Line of Business</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Line of Business</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Count</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Percentage</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {extData.frequencyAnalysis.claimsByLOB.map((item: any, idx: number) => (
                              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.lob}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatNumber(item.count)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{item.percentage || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Severity Distribution */}
                  {extData.severityAnalysis?.distribution && Array.isArray(extData.severityAnalysis.distribution) && extData.severityAnalysis.distribution.length > 0 && (
                    <div id="severity-distribution" className="bg-orange-50 dark:bg-orange-900/20 p-5 rounded-lg border border-orange-200 dark:border-orange-700">
                      <h3 className="text-lg font-semibold mb-4 text-orange-800 dark:text-orange-300">Severity Distribution</h3>
                      {/* Summary stats */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {extData.severityAnalysis.averageClaimSize && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Average Claim Size</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">{extData.severityAnalysis.averageClaimSize}</div>
                          </div>
                        )}
                        {extData.severityAnalysis.medianClaimSize && (
                          <div className="bg-white dark:bg-gray-800 p-3 rounded">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Median Claim Size</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">{extData.severityAnalysis.medianClaimSize}</div>
                          </div>
                        )}
                      </div>
                      {/* v4.1 § 11.3 — Median evidence audit trail (forced-emission gate output) */}
                      {extData.severityAnalysis.medianEvidence && (
                        <div className="bg-white dark:bg-gray-800 p-3 mb-4 rounded border-l-4 border-orange-300 dark:border-orange-600">
                          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Median &amp; Mean Audit Trail</div>
                          <div className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                            {extData.severityAnalysis.medianEvidence}
                          </div>
                        </div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Count</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Incurred</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Percentage</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {extData.severityAnalysis.distribution.map((item: any, idx: number) => (
                              <tr key={idx} className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                item.category === 'Catastrophic' ? 'bg-red-50 dark:bg-red-900/10' :
                                item.category === 'Severe' ? 'bg-orange-50 dark:bg-orange-900/10' :
                                item.category === 'Moderate' ? 'bg-yellow-50 dark:bg-yellow-900/10' :
                                ''
                              }`}>
                                <td className={`px-4 py-3 text-sm font-medium ${
                                  item.category === 'Catastrophic' ? 'text-red-700 dark:text-red-300' :
                                  item.category === 'Severe' ? 'text-orange-700 dark:text-orange-300' :
                                  item.category === 'Moderate' ? 'text-yellow-700 dark:text-yellow-300' :
                                  'text-gray-900 dark:text-white'
                                }`}>{item.category}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatNumber(item.count)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatCurrency(item.totalIncurred)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{item.percentage || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Geographic Distribution */}
                  {extData.frequencyAnalysis?.claimsByState && Array.isArray(extData.frequencyAnalysis.claimsByState) && extData.frequencyAnalysis.claimsByState.length > 0 && (() => {
                    // v4.1 § 9.3 — surface state evidence if any state row carries it
                    const hasStateEvidence = extData.frequencyAnalysis.claimsByState.some(
                      (s: any) => s.stateSourceBreakdown || (Array.isArray(s.sampleSourceQuotes) && s.sampleSourceQuotes.length > 0)
                    );
                    return (
                      <div id="geographic-distribution" className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-700">
                        <h3 className="text-lg font-semibold mb-2 text-green-800 dark:text-green-300">Geographic Distribution</h3>
                        {/* v4.1 attribution coverage statement */}
                        {extData.frequencyAnalysis.attributionCoverage && (
                          <div className="mb-3 text-sm text-gray-700 dark:text-gray-300">
                            <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mr-2">Attribution coverage:</span>
                            {extData.frequencyAnalysis.attributionCoverage}
                          </div>
                        )}
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">State</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Count</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Percentage</th>
                                {hasStateEvidence && (
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Source Breakdown</th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                              {extData.frequencyAnalysis.claimsByState.map((item: any, idx: number) => (
                                <React.Fragment key={idx}>
                                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.state}</td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatNumber(item.count)}</td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{item.percentage || 'N/A'}</td>
                                    {hasStateEvidence && (
                                      <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                        {item.stateSourceBreakdown || '—'}
                                      </td>
                                    )}
                                  </tr>
                                  {Array.isArray(item.sampleSourceQuotes) && item.sampleSourceQuotes.length > 0 && (
                                    <tr className="bg-green-50/50 dark:bg-green-900/10">
                                      <td colSpan={hasStateEvidence ? 4 : 3} className="px-4 py-2">
                                        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Source quotes for {item.state}</div>
                                        <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                                          {item.sampleSourceQuotes.map((q: any, qIdx: number) => (
                                            <li key={qIdx} className="font-mono">
                                              <span className="text-gray-900 dark:text-gray-100">{q.claimNumber}</span>
                                              {q.sourceField && <>: &ldquo;{q.sourceField}&rdquo;</>}
                                              {q.sourceLocation && <span className="text-gray-500 dark:text-gray-400"> ({q.sourceLocation})</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Claims by Period */}
                  {extData.frequencyAnalysis?.claimsPerPeriod && Array.isArray(extData.frequencyAnalysis.claimsPerPeriod) && extData.frequencyAnalysis.claimsPerPeriod.length > 0 && (
                    <div id="claims-by-period" className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-700">
                      <h3 className="text-lg font-semibold mb-4 text-blue-800 dark:text-blue-300">Claims by Period</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Period</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Count</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {extData.frequencyAnalysis.claimsPerPeriod.map((item: any, idx: number) => (
                              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.period}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatNumber(item.count)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return <div className="text-gray-500 italic p-5 bg-gray-50 dark:bg-gray-800 rounded-lg">
              No detailed analysis matrices available in this report.
            </div>;
          }

          return data.analysisMatrices.map((matrix, index) => {
            // Generate the same matrix ID as in PdfExportPreview
            const matrixId = matrix.title
              ? `matrix-${matrix.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`
              : `matrix-${index}`;

            // Check if this individual matrix should be shown
            if (!shouldShowSection(matrixId)) {
              console.log(`🔍 Analysis Matrix ${index + 1}: Skipping (not selected) - ${matrixId}`);
              return null;
            }

            try {
              const renderedMatrix = renderMatrix(matrix, index);
              // Wrap the matrix with id and data attribute for navigation and debugging
              return <div key={matrixId} id={matrixId} data-matrix-id={matrixId}>{renderedMatrix}</div>;
            } catch (error) {
              console.error(`🔍 Analysis Matrix ${index + 1}: Error rendering matrix:`, error, matrix);
              return <div key={index} className="bg-red-50 dark:bg-red-900/20 p-4 rounded border-l-4 border-red-400 mb-6">
                <div className="text-red-700 dark:text-red-300 font-medium mb-2">
                  Analysis Matrix {index + 1}: Rendering Error
                </div>
                <div className="text-sm text-red-600 dark:text-red-400 mb-2">
                  {error instanceof Error ? error.message : 'Unknown error occurred'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Matrix title: {(matrix as any)?.title || 'Unknown'}
                </div>
                <details>
                  <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800">
                    Show raw matrix data
                  </summary>
                  <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded overflow-auto max-h-40 mt-2">
                    {JSON.stringify(matrix, null, 2)}
                  </pre>
                </details>
              </div>;
            }
          });
        })()}
      </section>
      </div>
      )}

      {/* Key Findings - Skip for insurance requirements, loss_run_analysis, loss_run_analyzer, and commercial_lines_exec_renewal (they have their own structures) */}
      {(() => {
        const wt = data.responseMetadata?.workflowType?.toLowerCase() || '';
        const hasExecRenewalData = !!(data as any).executiveRenewalSummary;
        const skipKeyFindings = wt === 'insurance_requirements' || wt === 'loss_run_analysis' || wt === 'loss_run_analyzer' || wt === 'commercial_lines_exec_renewal' || wt.includes('loss_run_analyzer') || hasExecRenewalData;
        console.log('🔍 Key Findings Skip Check:', { workflowType: data.responseMetadata?.workflowType, skipKeyFindings, hasExecRenewalData });
        return null;
      })()}
      {shouldShowSection('key-findings') && !['insurance_requirements', 'loss_run_analysis', 'loss_run_analyzer', 'commercial_lines_exec_renewal', 'client_renewal_presentation', 'rate_analysis', 'portfolio_analyzer', 'homeowners_comparison'].includes(data.responseMetadata?.workflowType?.toLowerCase() || '') && !(data.responseMetadata?.workflowType?.toLowerCase()?.includes('loss_run_analyzer')) && !(data as any).executiveRenewalSummary && !((data as any).coverageHighlights && (data as any).restrictiveEndorsements) && !(data as any).analysisTables && !(data as any).driverHierarchy && !(data as any).portfolioStructure && !(data as any).gapSynthesis && (
      <div {...getSectionDataAttrs('key-findings')} style={getSectionStyles('key-findings')}>
      <section id="key-findings" className="mb-10">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
          Key Findings
        </h2>
        {(() => {
          // Check for NEW loss run forensic structure - render critical claims and recommendations instead
          const extData = data as any;
          // EXCLUDE portfolio_analyzer - it has its own recommendations section
          if (!data.keyFindings && (extData.criticalClaims || extData.recommendations || extData.processingLog) &&
              effectiveWorkflowType !== 'portfolio_analyzer' && !(data as any).portfolioStructure) {
            console.log('🔍 Key Findings: Detected loss run forensic structure, rendering criticalClaims and recommendations');
            return (
              <div className="space-y-6">
                {/* Critical Claims */}
                {extData.criticalClaims && extData.criticalClaims.length > 0 && (
                  <div id="critical-claims" className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-700">
                    <h3 className="text-lg font-semibold mb-4 text-red-800 dark:text-red-300">Critical Claims (Over $50,000)</h3>
                    <div className="space-y-4">
                      {extData.criticalClaims.map((claim: any, index: number) => (
                        <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-red-200 dark:border-red-600 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-bold text-lg text-gray-900 dark:text-white">{claim.claim_number}</span>
                            <span className="text-xl font-bold text-red-700 dark:text-red-300">{formatCurrency(claim.total_incurred)}</span>
                          </div>
                          <p className="text-gray-700 dark:text-gray-300 mb-2">{safeString(claim.description)}</p>
                          <div className="flex flex-wrap gap-2 text-sm">
                            {claim.carrier && (
                              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                                {safeString(claim.carrier)}
                              </span>
                            )}
                            {claim.status && (
                              <span className={`px-2 py-1 rounded ${
                                safeString(claim.status) === 'Open' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                                'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              }`}>
                                {safeString(claim.status)}
                              </span>
                            )}
                            {claim.primary_category && (
                              <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded">
                                {safeString(claim.primary_category)}
                              </span>
                            )}
                            {claim.litigation_flag === 'Yes' && (
                              <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded">
                                ⚠️ Litigation
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processing Log */}
                {extData.processingLog && extData.processingLog.length > 0 && (
                  <div id="processing-log" className="bg-gray-50 dark:bg-gray-900/20 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-300">Processing Log</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">File</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Carrier</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Method</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Claims Found</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Incurred</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quality</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {extData.processingLog.map((log: any, index: number) => (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{log.filename}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{log.carrier || 'Unknown'}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{log.method || 'N/A'}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatNumber(log.claimsFound)}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{log.totalIncurred ? formatCurrency(log.totalIncurred) : 'N/A'}</td>
                              <td className={`px-4 py-3 text-sm font-medium ${
                                safeString(log.quality) === 'HIGH' ? 'text-green-700 dark:text-green-300' :
                                safeString(log.quality) === 'MEDIUM' ? 'text-yellow-700 dark:text-yellow-300' :
                                'text-red-700 dark:text-red-300'
                              }`}>{safeString(log.quality)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {extData.recommendations && extData.recommendations.length > 0 && (
                  <div id="loss-run-recommendations" className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-700">
                    <h3 className="text-lg font-semibold mb-4 text-green-800 dark:text-green-300">Recommendations</h3>
                    <div className="space-y-4">
                      {extData.recommendations.map((rec: any, index: number) => (
                        <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-green-200 dark:border-green-600 shadow-sm">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{safeString(rec.action)}</h4>
                              <p className="text-gray-700 dark:text-gray-300 text-sm">{renderSafeContent(rec.rationale, 'Recommendation Rationale', hideCitations)}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1 ml-4">
                              {rec.priority && (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  safeString(rec.priority) === 'High' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                                  safeString(rec.priority) === 'Medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                                  'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300'
                                }`}>
                                  {safeString(rec.priority)} Priority
                                </span>
                              )}
                              {rec.category && (
                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-xs">
                                  {safeString(rec.category)}
                                </span>
                              )}
                            </div>
                          </div>
                          {rec.expectedImpact && (
                            <div className="mt-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 p-2 rounded">
                              <strong>Expected Impact:</strong> {renderSafeContent(rec.expectedImpact, 'Expected Impact', hideCitations)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analysis Narrative with Citations */}
                {extData.analysisNarrative && (
                  <div id="analysis-narrative" className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-700">
                    <h3 className="text-lg font-semibold mb-4 text-blue-800 dark:text-blue-300">Analysis Narrative</h3>
                    <div className="space-y-6">
                      {/* Summary with Clickable Citations */}
                      {extData.analysisNarrative.summary && (
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-blue-200 dark:border-blue-600">
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Summary</h4>
                          {renderCitationText(extData.analysisNarrative.summary, hideCitations)}
                        </div>
                      )}

                      {/* Key Observations with Clickable Citations */}
                      {extData.analysisNarrative.keyObservations && extData.analysisNarrative.keyObservations.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-blue-200 dark:border-blue-600">
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Key Observations</h4>
                          <div className="space-y-4">
                            {extData.analysisNarrative.keyObservations.map((obs: any, idx: number) => (
                              <div key={idx} className="border-l-4 border-blue-400 dark:border-blue-500 pl-4">
                                {renderCitationText({ text: obs.observation, citations: obs.citations }, hideCitations)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Risk Assessment with Clickable Citations */}
                      {extData.analysisNarrative.riskAssessment && (
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-orange-200 dark:border-orange-600">
                          <h4 className="font-semibold text-orange-800 dark:text-orange-300 mb-2">Risk Assessment</h4>
                          {renderCitationText(extData.analysisNarrative.riskAssessment, hideCitations)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // Financial Analysis format: keyFindings is array of {finding, metric, severity, citations}
          if (Array.isArray(data.keyFindings) && data.keyFindings.length > 0 && data.keyFindings[0]?.finding !== undefined) {
            console.log('🔍 Key Findings: Detected financial analysis array format');
            return (
              <div className="space-y-4">
                {(data.keyFindings as any[]).map((item: any, idx: number) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-gray-800 dark:text-gray-200 flex-1">{item.finding}</span>
                      {item.severity && (
                        <span className={`ml-3 px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                          item.severity.toLowerCase().includes('high') || item.severity.toLowerCase().includes('critical') ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                          item.severity.toLowerCase().includes('low') ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                        }`}>
                          {item.severity}
                        </span>
                      )}
                    </div>
                    {item.metric && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Metric:</span> {item.metric}
                      </div>
                    )}
                    {/* Citations for Key Finding */}
                    {!hideCitations && item.citations && item.citations.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {item.citations.map((citation: any, citIdx: number) => (
                          <button
                            key={citIdx}
                            onClick={() => onCitationClick({
                              ...citation,
                              type: 'page',
                              sourceDocId: citation.docId,
                              documentId: citation.docId,
                              documentName: citation.docName,
                              clickable: true,
                              url: citation.docId,
                              found: true
                            })}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                            title={`${citation.docName} - Page ${citation.page}${citation.section ? ` - ${citation.section}` : ''}${citation.quote ? `\n"${citation.quote}"` : ''}`}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>p.{citation.page}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          }

          // Original Key Findings rendering
          return (
            <>
              <div className="mb-6 text-gray-700 dark:text-gray-300 text-lg italic bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                {renderSafeContent(data.keyFindings?.summary, 'Key Findings Summary', hideCitations)}
              </div>

              {data.keyFindings?.findings?.map((finding, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 mb-6 shadow-sm hover:shadow-md transition-shadow key-finding keep-together">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-xl text-gray-800 dark:text-white">{finding.category}</h4>
              <div className="flex items-center space-x-3">
                {finding.impact && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    finding.impact === 'Positive' ? 'bg-green-100 text-green-800' :
                    finding.impact === 'Negative' ? 'bg-red-100 text-red-800' :
                    finding.impact === 'Critical' ? 'bg-red-200 text-red-900' :
                    'bg-gray-100 text-gray-800'
                  } inline-flex items-center gap-1`}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                    {finding.impact} Impact
                  </span>
                )}
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 inline-flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  {finding.confidence} Confidence
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  (finding.priority as any) === 'Immediate' || (finding.priority as any) === 'Critical' ? 'bg-red-100 text-red-800' :
                  (finding.priority as any) === 'High' ? 'bg-orange-100 text-orange-800' :
                  (finding.priority as any) === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                } inline-flex items-center gap-1`}>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                  {finding.priority} Priority
                </span>
              </div>
            </div>
            
            <div className="text-gray-700 dark:text-gray-300 mb-4 text-lg leading-relaxed">
              {(() => {
                // Enhanced debug logging for finding structure
                console.log('🔍 UniversalJsonRenderer finding debug:', {
                  category: finding.category,
                  hasFinding: !!finding.finding,
                  hasDescription: !!finding.description,
                  findingType: typeof finding.finding,
                  findingKeys: finding.finding ? Object.keys(finding.finding) : 'none',
                  findingStructure: finding.finding,
                  fullFinding: finding
                });
                
                // Handle both old and new data formats with better error handling
                if (finding.finding) {
                  // New format: complex nested structure
                  try {
                    const result = renderCitationText(finding.finding, hideCitations);
                    // Check if result is the "No content available" error
                    if (typeof result === 'object' && result.props?.children === 'No content available') {
                      console.warn('🔍 renderCitationText returned no content for finding.finding, trying fallbacks:', finding.finding);
                      // Try to render as JSON if extraction failed
                      return <div className="whitespace-pre-wrap font-mono text-sm bg-gray-100 dark:bg-gray-800 p-3 rounded">
                        {JSON.stringify(finding.finding, null, 2)}
                      </div>;
                    }
                    return result;
                  } catch (error) {
                    console.error('🔍 Error rendering finding.finding:', error, finding.finding);
                    return <div className="text-orange-600 bg-orange-50 dark:bg-orange-900/20 p-3 rounded">
                      Error rendering finding content. Raw data: {JSON.stringify(finding.finding, null, 2)}
                    </div>;
                  }
                } else if (finding.description) {
                  // Old format: simple description field
                  return renderCitationText(finding.description, hideCitations);
                } else {
                  // Try to find any text content in the finding object itself
                  const findingAny = finding as any; // Type assertion for unknown properties
                  const fallbackText = findingAny.summary || findingAny.content || findingAny.text || findingAny.details;
                  if (fallbackText) {
                    console.log('🔍 Using fallback text field:', fallbackText);
                    return renderCitationText(fallbackText, hideCitations);
                  }
                  
                  // Last resort: show all finding data for debugging
                  console.warn('🔍 No recognizable content found in finding:', finding);
                  return <div className="text-gray-500">
                    <div>No content available in expected format.</div>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800">Debug: Show raw finding data</summary>
                      <pre className="text-xs mt-2 bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto">
                        {JSON.stringify(finding, null, 2)}
                      </pre>
                    </details>
                  </div>;
                }
              })()}
            </div>
          </div>
        ))}
            </>
          );
        })()}
      </section>
      </div>
      )}

      {/* Strategic Considerations */}
      {shouldShowSection('strategic-considerations') && data.strategicConsiderations && data.strategicConsiderations.length > 0 && (
        <div {...getSectionDataAttrs('strategic-considerations')} style={getSectionStyles('strategic-considerations')}>
        <section id="strategic-considerations" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Strategic Considerations
          </h2>
          {data.strategicConsiderations?.map((consideration, index) => (
            <div key={index} className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-6 mb-6 content-block keep-together">
              <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-4">{safeString(consideration.consideration)}</h4>

              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Analysis:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {renderSafeContent(consideration.analysis, `Strategic Consideration ${index + 1} Analysis`, hideCitations)}
                </div>
              </div>

              {consideration.options && consideration.options.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Options:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {consideration.options?.map((option, optionIndex) => (
                      <li key={optionIndex}>{safeString(option)}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Recommended Approach:</h5>
                <div className="text-gray-700 dark:text-gray-300 font-medium">
                  {renderSafeContent(consideration.recommendedApproach, `Strategic Consideration ${index + 1} Recommended Approach`, hideCitations)}
                </div>
              </div>
              
              {consideration.citations && consideration.citations.length > 0 && (
                <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-600">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Supporting Evidence:</strong> {consideration.citations.length} citation(s)
                  </div>
                </div>
              )}
            </div>
          ))}
        </section>
        </div>
      )}

      {/* Risk Mitigation Strategies */}
      {shouldShowSection('risk-mitigation-strategies') && data.riskMitigationStrategies && data.riskMitigationStrategies.length > 0 && (
        <div {...getSectionDataAttrs('risk-mitigation-strategies')} style={getSectionStyles('risk-mitigation-strategies')}>
        <section id="risk-mitigation-strategies" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Risk Mitigation Strategies
          </h2>
          {data.riskMitigationStrategies?.map((strategy, index) => (
            <div key={index} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6 mb-6 strategy keep-together">
              <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-4">{safeString(strategy.risk)}</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Strategy:</h5>
                  <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {renderSafeContent(strategy.strategy, `Risk Mitigation ${index + 1} Strategy`, hideCitations)}
                  </div>
                </div>

                <div>
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Implementation:</h5>
                  <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {renderSafeContent(strategy.implementation, `Risk Mitigation ${index + 1} Implementation`, hideCitations)}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-red-200 dark:border-red-600">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Expected Risk Reduction:</h5>
                <div className="text-gray-700 dark:text-gray-300 font-medium">
                  {renderSafeContent(strategy.expectedReduction, `Risk Mitigation ${index + 1} Expected Reduction`, hideCitations)}
                </div>
              </div>
            </div>
          ))}
        </section>
      </div>
      )}

      {/* Risk Management Matrix */}
      {shouldShowSection('risk-management-matrix') && data.riskManagementMatrix && data.riskManagementMatrix.recommendations && data.riskManagementMatrix.recommendations.length > 0 && (
        <div {...getSectionDataAttrs('risk-management-matrix')} style={getSectionStyles('risk-management-matrix')}>
        <section id="risk-management-matrix" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            {data.riskManagementMatrix.title || 'Risk Management Matrix'}
          </h2>
          {data.riskManagementMatrix?.recommendations?.map((rec, index) => (
            <div key={index} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6 mb-6 strategy keep-together">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-xl text-gray-800 dark:text-white">{safeString(rec.riskCategory)}</h4>
                <div className="flex items-center space-x-3">
                  <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                    safeString(rec.priorityLevel) === 'High' ? 'bg-red-200 text-red-900' :
                    safeString(rec.priorityLevel) === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {safeString(rec.priorityLevel)} Priority
                  </span>
                  {rec.timeline && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {safeString(rec.timeline)}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="mb-3">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Key Observation:</h5>
                <div className="text-gray-700 dark:text-gray-300">
                  {renderSafeContent(rec.keyObservation, `Strategic Consideration ${index + 1} Key Observation`, hideCitations)}
                </div>
              </div>
              
              <div className="mb-3">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Business Impact:</h5>
                <div className="text-gray-700 dark:text-gray-300">
                  {renderSafeContent(rec.businessImpact, `Strategic Consideration ${index + 1} Business Impact`, hideCitations)}
                </div>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Actionable Recommendation:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {renderSafeContent(rec.actionableRecommendation, `Strategic Consideration ${index + 1} Actionable Recommendation`, hideCitations)}
                </div>
              </div>
              
              {rec.implementationSteps && rec.implementationSteps.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Implementation Steps:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {rec.implementationSteps?.map((step, stepIndex) => (
                      <li key={stepIndex}>{safeString(step)}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-red-200 dark:border-red-600">
                <div>
                  <h5 className="font-semibold text-sm mb-1 text-gray-800 dark:text-white">Expected Outcome:</h5>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">{renderSafeContent(rec.expectedOutcome, `Risk Matrix ${index + 1} Expected Outcome`, hideCitations)}</div>
                </div>
                <div>
                  <h5 className="font-semibold text-sm mb-1 text-gray-800 dark:text-white">Estimated Cost:</h5>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">{renderSafeContent(rec.estimatedCost, `Risk Matrix ${index + 1} Estimated Cost`, hideCitations)}</div>
                </div>
              </div>
            </div>
          ))}
        </section>
        </div>
      )}

      {/* Trend Analysis (OLD format - skip for loss_run_analysis which uses simplified trendAnalysis) */}
      {shouldShowSection('trend-analysis') && data.trendAnalysis && data.responseMetadata?.workflowType !== 'loss_run_analysis' && (
        <div {...getSectionDataAttrs('trend-analysis')} style={getSectionStyles('trend-analysis')}>
        <section id="trend-analysis" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Trend Analysis
          </h2>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-6">
            <div className="mb-4">
              <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-2">Overall Trend: {data.trendAnalysis.overallTrend}</h4>
            </div>
            
            {data.trendAnalysis.keyMetrics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {data.trendAnalysis.keyMetrics.frequencyTrend && (
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Frequency Trend</h5>
                    <div className="text-2xl font-bold mb-1 text-blue-600">{data.trendAnalysis.keyMetrics.frequencyTrend.direction}</div>
                    <div className="text-lg text-gray-600 dark:text-gray-300 mb-2">{data.trendAnalysis.keyMetrics.frequencyTrend.percentageChange}</div>
                    <div className="text-sm text-gray-700 dark:text-gray-400">{data.trendAnalysis.keyMetrics.frequencyTrend.analysis}</div>
                  </div>
                )}
                {data.trendAnalysis.keyMetrics.severityTrend && (
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Severity Trend</h5>
                    <div className="text-2xl font-bold mb-1 text-green-600">{data.trendAnalysis.keyMetrics.severityTrend.direction}</div>
                    <div className="text-lg text-gray-600 dark:text-gray-300 mb-2">{data.trendAnalysis.keyMetrics.severityTrend.percentageChange}</div>
                    <div className="text-sm text-gray-700 dark:text-gray-400">{data.trendAnalysis.keyMetrics.severityTrend.analysis}</div>
                  </div>
                )}
                {data.trendAnalysis.keyMetrics.lossRatioTrend && (
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Loss Ratio</h5>
                    <div className="text-2xl font-bold mb-1 text-orange-600">{data.trendAnalysis.keyMetrics.lossRatioTrend.currentRatio}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">Historical: {data.trendAnalysis.keyMetrics.lossRatioTrend.historicalAverage}</div>
                    <div className="text-sm text-gray-700 dark:text-gray-400">{data.trendAnalysis.keyMetrics.lossRatioTrend.analysis}</div>
                  </div>
                )}
              </div>
            )}
            
            {data.trendAnalysis.emergingRisks && data.trendAnalysis.emergingRisks.length > 0 && (
              <div>
                <h5 className="font-semibold text-lg mb-3 text-gray-800 dark:text-white">Emerging Risks</h5>
                <div className="space-y-3">
                  {data.trendAnalysis?.emergingRisks?.map((risk, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                      <h6 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">{risk.risk}</h6>
                      <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        <strong>Indicators:</strong> {risk.indicators.join(', ')}
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        <strong>Potential Impact:</strong> {risk.potentialImpact}
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>Mitigation:</strong> {risk.mitigationStrategy}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
        </div>
      )}

      {/* Benchmarking Insights */}
      {shouldShowSection('benchmarking-insights') && data.benchmarkingInsights && (
        <div {...getSectionDataAttrs('benchmarking-insights')} style={getSectionStyles('benchmarking-insights')}>
        <section id="benchmarking-insights" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Benchmarking Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.benchmarkingInsights.industryComparison && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6">
                <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-4">Industry Comparison</h4>
                <div className="mb-3">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Performance vs Benchmark:</h5>
                  <div className="text-gray-700 dark:text-gray-300">{data.benchmarkingInsights.industryComparison.performanceVsBenchmark}</div>
                </div>
                <div className="mb-3">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Key Differentiators:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {data.benchmarkingInsights?.industryComparison?.keyDifferentiators?.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Improvement Opportunities:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {data.benchmarkingInsights?.industryComparison?.improvementOpportunities?.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            
            {data.benchmarkingInsights.internalComparison && (
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-6">
                <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-4">Internal Comparison</h4>
                <div className="mb-3">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Best Performing Areas:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {data.benchmarkingInsights?.internalComparison?.bestPerformingAreas?.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="mb-3">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Worst Performing Areas:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {data.benchmarkingInsights?.internalComparison?.worstPerformingAreas?.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Lessons Learned:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {data.benchmarkingInsights?.internalComparison?.lessonsLearned?.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>
        </div>
      )}

      {/* Cost-Benefit Analysis */}
      {shouldShowSection('cost-benefit-analysis') && data.costBenefitAnalysis && data.costBenefitAnalysis.length > 0 && (
        <div {...getSectionDataAttrs('cost-benefit-analysis')} style={getSectionStyles('cost-benefit-analysis')}>
        <section id="cost-benefit-analysis" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Cost-Benefit Analysis
          </h2>
          <div className="space-y-6">
            {data.costBenefitAnalysis?.map((analysis, index) => (
              <div key={index} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
                <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-4">{analysis.recommendation}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Implementation Cost:</h5>
                    <div className="text-2xl font-bold text-red-600">{analysis.implementationCost}</div>
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Potential Savings:</h5>
                    <div className="text-2xl font-bold text-green-600">{analysis.potentialSavings}</div>
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Payback Period:</h5>
                    <div className="text-2xl font-bold text-blue-600">{analysis.paybackPeriod}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Non-Financial Benefits:</h5>
                    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                      {analysis.nonFinancialBenefits?.map((benefit, benefitIndex) => (
                        <li key={benefitIndex}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Implementation Complexity:</h5>
                    <div className={`text-lg font-medium ${
                      analysis.implementationComplexity === 'Low' ? 'text-green-600' :
                      analysis.implementationComplexity === 'Medium' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {analysis.implementationComplexity}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
        </div>
      )}

      {/* Data Quality Assessment */}
      {shouldShowSection('data-quality-assessment') && data.dataQualityAssessment && (
        <div {...getSectionDataAttrs('data-quality-assessment')} style={getSectionStyles('data-quality-assessment')}>
        <section id="data-quality-assessment" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Data Quality Assessment
          </h2>
          <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {(data.dataQualityAssessment as any).completenessScore && (
                <div>
                  <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-2">Completeness Score</h4>
                  <div className="text-3xl font-bold text-blue-600">{(data.dataQualityAssessment as any).completenessScore}</div>
                </div>
              )}
              {(data.dataQualityAssessment as any).confidenceInAnalysis && (
                <div>
                  <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-2">Confidence in Analysis</h4>
                  <div className={`text-2xl font-bold ${
                    (data.dataQualityAssessment as any).confidenceInAnalysis === 'High' ? 'text-green-600' :
                    (data.dataQualityAssessment as any).confidenceInAnalysis === 'Medium' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {(data.dataQualityAssessment as any).confidenceInAnalysis}
                  </div>
                </div>
              )}
            </div>
            
            {(((data.dataQualityAssessment as any).dataGaps && (data.dataQualityAssessment as any).dataGaps.length > 0) || 
              ((data.dataQualityAssessment as any).missingElements && (data.dataQualityAssessment as any).missingElements.length > 0) ||
              ((data.dataQualityAssessment as any).assumptionsMade && (data.dataQualityAssessment as any).assumptionsMade.length > 0)) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(((data.dataQualityAssessment as any).dataGaps && (data.dataQualityAssessment as any).dataGaps.length > 0) || 
                  ((data.dataQualityAssessment as any).missingElements && (data.dataQualityAssessment as any).missingElements.length > 0)) && (
                  <div>
                    <h5 className="font-semibold text-lg mb-3 text-gray-800 dark:text-white">
                      {(data.dataQualityAssessment as any).dataGaps ? 'Data Gaps:' : 'Missing Elements:'}
                    </h5>
                    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                      {((data.dataQualityAssessment as any).dataGaps || (data.dataQualityAssessment as any).missingElements || []).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {((data.dataQualityAssessment as any).assumptionsMade && (data.dataQualityAssessment as any).assumptionsMade.length > 0) && (
                  <div>
                    <h5 className="font-semibold text-lg mb-3 text-gray-800 dark:text-white">Assumptions Made:</h5>
                    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                      {((data.dataQualityAssessment as any).assumptionsMade || []).map((assumption: string, index: number) => (
                        <li key={index}>{assumption}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {((data.dataQualityAssessment as any).recommendedDataCollection || (data.dataQualityAssessment as any).recommendedFollowUp) && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                <h5 className="font-semibold text-lg mb-3 text-gray-800 dark:text-white">
                  {(data.dataQualityAssessment as any).recommendedDataCollection ? 'Recommended Data Collection:' : 'Recommended Follow-up:'}
                </h5>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                  {((data.dataQualityAssessment as any).recommendedDataCollection || (data.dataQualityAssessment as any).recommendedFollowUp || []).map((recommendation: string, index: number) => (
                    <li key={index}>{recommendation}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
        </div>
      )}

      {/* Original Recommendations (if present) - EXCLUDE portfolio_analyzer and rate_analysis which have their own sections */}
      {shouldShowSection('recommendations') && data.recommendations && data.recommendations.length > 0 &&
       effectiveWorkflowType !== 'portfolio_analyzer' && !(data as any).portfolioStructure &&
       effectiveWorkflowType !== 'rate_analysis' && !(data as any).analysisTables && !(data as any).driverHierarchy && (
        <div {...getSectionDataAttrs('recommendations')} style={getSectionStyles('recommendations')}>
        <section id="recommendations" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Recommendations
          </h2>
          {data.recommendations?.map((rec, index) => (
            <div key={index} className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-6 mb-6 recommendation keep-together">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-xl text-gray-800 dark:text-white">{safeString(rec.category)}</h4>
                <div className="flex items-center space-x-3">
                  <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                    safeString(rec.priority) === 'Critical' ? 'bg-red-200 text-red-900' :
                    safeString(rec.priority) === 'High' ? 'bg-orange-100 text-orange-800' :
                    safeString(rec.priority) === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {safeString(rec.priority)} Priority
                  </span>
                  {rec.timeline && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {safeString(rec.timeline)}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="mb-3">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Action:</h5>
                <div className="text-gray-700 dark:text-gray-300">
                  {renderSafeContent(rec.action, `Recommendation ${index + 1} Action`, hideCitations)}
                </div>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Rationale:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {renderSafeContent(rec.rationale, `Recommendation ${index + 1} Rationale`, hideCitations)}
                </div>
              </div>
            </div>
          ))}
        </section>
        </div>
      )}

      {/* Renewal Recommendations */}
      {shouldShowSection('renewal-recommendations') && data.renewalRecommendations && data.renewalRecommendations.recommendations && data.renewalRecommendations.recommendations.length > 0 && (
        <div {...getSectionDataAttrs('renewal-recommendations')} style={getSectionStyles('renewal-recommendations')}>
        <section id="renewal-recommendations" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Renewal Recommendations
          </h2>
          {data.renewalRecommendations.summary && (
            <div className="mb-6 text-gray-700 dark:text-gray-300 text-lg italic bg-green-50 dark:bg-green-800 p-4 rounded-lg">
              {data.renewalRecommendations.summary}
            </div>
          )}
          {data.renewalRecommendations.recommendations?.map((rec, index) => (
            <div key={index} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6 mb-6 recommendation keep-together">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-xl text-gray-800 dark:text-white">{rec.category}</h4>
                <div className="flex items-center space-x-3">
                  <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                    rec.priority === 'critical' ? 'bg-red-200 text-red-900' :
                    rec.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                    rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {rec.priority} Priority
                  </span>
                </div>
              </div>
              
              <div className="mb-3">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Recommendation:</h5>
                <div className="text-gray-700 dark:text-gray-300">
                  {renderSafeContent(rec.recommendation, `Renewal Recommendation ${index + 1}`, hideCitations)}
                </div>
              </div>

              <div className="mb-3">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Rationale:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {renderSafeContent(rec.rationale, `Renewal Rationale ${index + 1}`, hideCitations)}
                </div>
              </div>

              <div className="mb-3">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Expected Outcome:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {renderSafeContent(rec.expectedOutcome, `Renewal Expected Outcome ${index + 1}`, hideCitations)}
                </div>
              </div>

              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Market Context:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {renderSafeContent(rec.marketContext, `Market Context ${index + 1}`, hideCitations)}
                </div>
              </div>
            </div>
          ))}
        </section>
        </div>
      )}

      {/* Market Analysis */}
      {shouldShowSection('market-analysis') && data.marketAnalysis && (data.marketAnalysis.marketConditions || data.marketAnalysis.competitivePosition) && (
        <div {...getSectionDataAttrs('market-analysis')} style={getSectionStyles('market-analysis')}>
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Market Analysis
          </h2>
          
          {data.marketAnalysis.marketConditions && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-6 keep-together">
              <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-4">Market Conditions</h4>
              
              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Assessment:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {data.marketAnalysis.marketConditions.assessment}
                </div>
              </div>

              {data.marketAnalysis.marketConditions.trends && data.marketAnalysis.marketConditions.trends.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Key Trends:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {data.marketAnalysis.marketConditions.trends.map((trend, index) => (
                      <li key={index}>{trend}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {data.marketAnalysis.competitivePosition && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-6 mb-6 keep-together">
              <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-4">Competitive Position</h4>
              
              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Assessment:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {data.marketAnalysis.competitivePosition.assessment}
                </div>
              </div>

              {data.marketAnalysis.competitivePosition.advantages && data.marketAnalysis.competitivePosition.advantages.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Competitive Advantages:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {data.marketAnalysis.competitivePosition.advantages.map((advantage, index) => (
                      <li key={index} className="text-green-700 dark:text-green-300">{advantage}</li>
                    ))}
                  </ul>
                </div>
              )}

              {data.marketAnalysis.competitivePosition.concerns && data.marketAnalysis.competitivePosition.concerns.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Areas of Concern:</h5>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {data.marketAnalysis.competitivePosition.concerns.map((concern, index) => (
                      <li key={index} className="text-orange-700 dark:text-orange-300">{concern}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
        </div>
      )}

      {/* Renewal Strategy */}
      {shouldShowSection('renewal-strategy') && data.renewalStrategy && (
        <div {...getSectionDataAttrs('renewal-strategy')} style={getSectionStyles('renewal-strategy')}>
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Renewal Strategy
          </h2>
          
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-6 mb-6 keep-together">
            {data.renewalStrategy.overallStrategy && (
              <div className="mb-4">
                <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-3">Overall Strategy</h4>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                  {data.renewalStrategy.overallStrategy}
                </div>
              </div>
            )}

            {data.renewalStrategy.keyActions && data.renewalStrategy.keyActions.length > 0 && (
              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Key Actions:</h5>
                <ul className="list-decimal list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  {data.renewalStrategy.keyActions.map((action, index) => (
                    <li key={index} className="leading-relaxed">{action}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.renewalStrategy.timeline && (
              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Timeline:</h5>
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed bg-white dark:bg-gray-800 p-3 rounded border-l-4 border-indigo-400">
                  {data.renewalStrategy.timeline}
                </div>
              </div>
            )}

            {data.renewalStrategy.riskConsiderations && data.renewalStrategy.riskConsiderations.length > 0 && (
              <div className="mb-4">
                <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Risk Considerations:</h5>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                  {data.renewalStrategy.riskConsiderations.map((risk, index) => (
                    <li key={index} className="text-red-700 dark:text-red-300">{risk}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
        </div>
      )}

      {/* Non-Standard Provisions */}
      {shouldShowSection('non-standard-provisions') && data.nonStandardProvisions && data.nonStandardProvisions.identified && data.nonStandardProvisions.provisions && data.nonStandardProvisions.provisions.length > 0 && (
        <div {...getSectionDataAttrs('non-standard-provisions')} style={getSectionStyles('non-standard-provisions')}>
        <section id="non-standard-provisions" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Non-Standard Provisions
          </h2>
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-3">
              <div className="text-orange-600 dark:text-orange-400 mr-2">
                ⚠️
              </div>
              <h3 className="font-semibold text-lg text-gray-800 dark:text-white">
                Non-Standard Policy Language Identified
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              The following provisions deviate from standard insurance industry language and require special attention.
            </p>
            
            {data.nonStandardProvisions.provisions.map((provision, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-700 rounded-lg p-6 mb-4 last:mb-0">
                <div className="mb-4">
                  <h4 className="font-bold text-lg text-gray-800 dark:text-white mb-2">
                    Provision #{index + 1}
                  </h4>
                  <div className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                    {provision.provision}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    <strong>Location:</strong> {provision.location}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded border-l-4 border-yellow-400">
                    <h5 className="font-semibold text-sm text-gray-800 dark:text-white mb-2">
                      Deviation from Standard
                    </h5>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {provision.deviation}
                    </div>
                  </div>
                  
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded border-l-4 border-red-400">
                    <h5 className="font-semibold text-sm text-gray-800 dark:text-white mb-2">
                      Risk Assessment
                    </h5>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {provision.riskAssessment}
                    </div>
                  </div>
                </div>
                
                {provision.citations && provision.citations.length > 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex flex-wrap items-center gap-1">
                      {(() => {
                        const order: string[] = [];
                        provision.citations.forEach((c: any) => {
                          const k = c.docId || c.docName || 'doc';
                          if (!order.includes(k)) order.push(k);
                        });
                        const letters = new Map<string, string>();
                        if (order.length > 1) order.forEach((k, i) => letters.set(k, String.fromCharCode(65 + (i % 26))));
                        return provision.citations.map((citation: any, citIndex: number) => (
                          <CitationMarker
                            key={citIndex}
                            citation={{
                              docName: citation.docName || citation.docId || 'Document',
                              docId: citation.docId,
                              page: citation.page,
                              confidence: citation.confidence,
                            }}
                            sourceLetter={letters.get(citation.docId || citation.docName || 'doc')}
                            onClick={() => onCitationClick({
                              type: 'page',
                              ...citation,
                              text: citation.quote || provision.deviation || provision.provision,
                            })}
                          />
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
        </div>
      )}

      {/* Policy Overview (Coverage Analysis specific) */}
      {shouldShowSection('policy-overview') && (data as any).policyOverview && (
        <div {...getSectionDataAttrs('policy-overview')} style={getSectionStyles('policy-overview')}>
        <section id="policy-overview" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Policy Overview
          </h2>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(data as any).policyOverview.policyType && (
                <div>
                  <h4 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Policy Type</h4>
                  <div className="text-gray-700 dark:text-gray-300">
                    {renderSafeContent((data as any).policyOverview.policyType, 'Policy Type', hideCitations)}
                  </div>
                </div>
              )}
              {(data as any).policyOverview.coverageLines && (
                <div>
                  <h4 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Coverage Lines</h4>
                  <div className="text-gray-700 dark:text-gray-300">
                    {renderSafeContent(
                      Array.isArray((data as any).policyOverview.coverageLines) 
                        ? (data as any).policyOverview.coverageLines.join(', ')
                        : (data as any).policyOverview.coverageLines,
                      'Coverage Lines', hideCitations
                    )}
                  </div>
                </div>
              )}
              {(data as any).policyOverview.policyPeriod && (
                <div>
                  <h4 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Policy Period</h4>
                  <div className="text-gray-700 dark:text-gray-300">
                    {renderSafeContent((data as any).policyOverview.policyPeriod, 'Policy Period', hideCitations)}
                  </div>
                </div>
              )}
            </div>
            
            {((data as any).policyOverview.namedInsured || (data as any).policyOverview.primaryLimits || (data as any).policyOverview.deductibles) && (
              <div className="mt-6 pt-6 border-t border-blue-200 dark:border-blue-600">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(data as any).policyOverview.namedInsured && (
                    <div>
                      <h4 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Named Insured</h4>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent((data as any).policyOverview.namedInsured, 'Named Insured', hideCitations)}
                      </div>
                    </div>
                  )}
                  {(data as any).policyOverview.primaryLimits && (
                    <div>
                      <h4 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Primary Limits</h4>
                      <div className="text-gray-700 dark:text-gray-300 space-y-1">
                        {typeof (data as any).policyOverview.primaryLimits === 'object' 
                          ? Object.entries((data as any).policyOverview.primaryLimits).map(([key, value]) => (
                            <div key={key}><strong>{key}:</strong> {renderSafeContent(String(value), `Primary Limits ${key}`, hideCitations)}</div>
                          ))
                          : renderSafeContent((data as any).policyOverview.primaryLimits, 'Primary Limits', hideCitations)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
        </div>
      )}

      {/* Coverage Analysis Matrices (rename from analysisMatrices for Coverage Analysis) */}
      {shouldShowSection('coverage-analysis-matrices') && (data as any).coverageAnalysisMatrices && Array.isArray((data as any).coverageAnalysisMatrices) && (data as any).coverageAnalysisMatrices.length > 0 && (
        <div {...getSectionDataAttrs('coverage-analysis-matrices')} style={getSectionStyles('coverage-analysis-matrices')}>
        <section id="coverage-analysis-matrices" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Coverage Analysis Matrices
          </h2>
          {(data as any).coverageAnalysisMatrices.map((matrix: any, index: number) => {
            try {
              return renderMatrix(matrix, index);
            } catch (error) {
              console.error(`🔍 Coverage Analysis Matrix ${index + 1}: Error rendering matrix:`, error, matrix);
              return <div key={index} className="bg-red-50 dark:bg-red-900/20 p-4 rounded border-l-4 border-red-400 mb-6">
                <div className="text-red-700 dark:text-red-300 font-medium mb-2">
                  Coverage Analysis Matrix {index + 1}: Rendering Error
                </div>
                <div className="text-sm text-red-600 dark:text-red-400 mb-2">
                  {error instanceof Error ? error.message : 'Unknown error occurred'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Matrix title: {(matrix as any)?.title || 'Unknown'}
                </div>
                <details>
                  <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400">Show raw matrix data</summary>
                  <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-auto max-h-40">
                    {JSON.stringify(matrix, null, 2)}
                  </pre>
                </details>
              </div>;
            }
          })}
        </section>
        </div>
      )}

      {/* Coverage Recommendations */}
      {shouldShowSection('coverage-recommendations') && (data as any).coverageRecommendations && Array.isArray((data as any).coverageRecommendations) && (data as any).coverageRecommendations.length > 0 && (
        <section id="coverage-recommendations" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Coverage Recommendations
          </h2>
          <div className="space-y-6">
            {(data as any).coverageRecommendations.map((rec: any, index: number) => (
              <div key={index} className={`border rounded-lg p-6 ${
                rec.priority === 'Critical' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' :
                rec.priority === 'High' ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700' :
                rec.priority === 'Medium' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700' :
                'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-xl text-gray-800 dark:text-white">{rec.category}</h4>
                  <div className="flex items-center space-x-3">
                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                      rec.priority === 'Critical' ? 'bg-red-200 text-red-900' :
                      rec.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                      rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {rec.priority} Priority
                    </span>
                    {rec.timeframe && (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {rec.timeframe}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Recommendation</h5>
                    <div className="text-gray-700 dark:text-gray-300">
                      {renderSafeContent(rec.recommendation?.text || rec.recommendation, `Recommendation ${index + 1}`, hideCitations)}
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Rationale</h5>
                    <div className="text-gray-700 dark:text-gray-300">
                      {renderSafeContent(rec.rationale, `Rationale ${index + 1}`, hideCitations)}
                    </div>
                  </div>
                </div>
                
                {rec.expectedOutcome && (
                  <div className="mb-4">
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Expected Outcome</h5>
                    <div className="text-gray-700 dark:text-gray-300">
                      {renderSafeContent(rec.expectedOutcome, `Expected Outcome ${index + 1}`, hideCitations)}
                    </div>
                  </div>
                )}
                
                {rec.implementationSteps && Array.isArray(rec.implementationSteps) && rec.implementationSteps.length > 0 && (
                  <div className="mb-4">
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Implementation Steps</h5>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {rec.implementationSteps.map((step: string, stepIndex: number) => (
                        <li key={stepIndex}>{renderSafeContent(step, `Implementation Step ${stepIndex + 1}`, hideCitations)}</li>
                      ))}
                    </ol>
                  </div>
                )}
                
                {(rec.costImpact || rec.timeframe) && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
                    {rec.costImpact && (
                      <div className="text-sm">
                        <span className="font-medium text-gray-600 dark:text-gray-400">Cost Impact:</span>
                        <span className={`ml-2 font-semibold ${
                          rec.costImpact === 'High' ? 'text-red-600' :
                          rec.costImpact === 'Medium' ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>{renderSafeContent(rec.costImpact, 'Cost Impact', hideCitations)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Coverage Gaps and Opportunities (Schema format: { summary, gaps[] }) */}
      {shouldShowSection('coverage-gaps-and-opportunities') && (data as any).coverageGapsAndOpportunities && typeof (data as any).coverageGapsAndOpportunities === 'object' && !Array.isArray((data as any).coverageGapsAndOpportunities) && (data as any).coverageGapsAndOpportunities.gaps && (
        <section id="coverage-gaps-and-opportunities" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Coverage Gaps and Opportunities
          </h2>
          {(data as any).coverageGapsAndOpportunities.summary && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300">{(data as any).coverageGapsAndOpportunities.summary}</p>
            </div>
          )}
          <div className="space-y-6">
            {(data as any).coverageGapsAndOpportunities.gaps.map((gap: any, index: number) => (
              <div key={index} className="border rounded-lg p-6 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-xl text-gray-800 dark:text-white">{gap.gapType}</h4>
                </div>

                <div className="mb-4">
                  <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Description</h5>
                  <div className="text-gray-700 dark:text-gray-300">
                    {renderSafeContent(gap.description, `Gap ${index + 1} Description`, hideCitations)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Impact</h5>
                    <div className="text-gray-700 dark:text-gray-300">
                      {renderSafeContent(gap.impact, `Gap ${index + 1} Impact`, hideCitations)}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Recommendation</h5>
                    <div className="text-gray-700 dark:text-gray-300">
                      {renderSafeContent(gap.recommendation, `Gap ${index + 1} Recommendation`, hideCitations)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Coverage Gaps and Opportunities (Legacy array format) */}
      {shouldShowSection('coverage-gaps-and-opportunities') && (data as any).coverageGapsAndOpportunities && Array.isArray((data as any).coverageGapsAndOpportunities) && (data as any).coverageGapsAndOpportunities.length > 0 && (
        <section id="coverage-gaps-and-opportunities" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Coverage Gaps and Opportunities
          </h2>
          <div className="space-y-6">
            {(data as any).coverageGapsAndOpportunities.map((gap: any, index: number) => (
              <div key={index} className={`border rounded-lg p-6 ${
                gap.priority === 'Critical' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' :
                gap.priority === 'High' ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700' :
                'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-xl text-gray-800 dark:text-white">{gap.gapArea}</h4>
                  <div className="flex items-center space-x-3">
                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                      gap.priority === 'Critical' ? 'bg-red-200 text-red-900' :
                      gap.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {gap.priority} Priority
                    </span>
                    {gap.gapSize && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        gap.gapSize === 'Large' ? 'bg-red-100 text-red-800' :
                        gap.gapSize === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {gap.gapSize} Gap
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Current State</h5>
                    <div className="text-gray-700 dark:text-gray-300">
                      {renderSafeContent(gap.currentState, `Gap ${index + 1} Current State`, hideCitations)}
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Ideal State</h5>
                    <div className="text-gray-700 dark:text-gray-300">
                      {renderSafeContent(gap.idealState, `Gap ${index + 1} Ideal State`, hideCitations)}
                    </div>
                  </div>
                </div>
                
                {gap.riskExposure && (
                  <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <h5 className="font-semibold text-lg mb-2 text-red-800 dark:text-red-200">Risk Exposure</h5>
                    <div className="text-red-700 dark:text-red-300">
                      {renderSafeContent(gap.riskExposure, `Gap ${index + 1} Risk Exposure`, hideCitations)}
                    </div>
                  </div>
                )}
                
                {gap.solutions && Array.isArray(gap.solutions) && gap.solutions.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-lg mb-2 text-gray-800 dark:text-white">Recommended Solutions</h5>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {gap.solutions.map((solution: string, solutionIndex: number) => (
                        <li key={solutionIndex}>{renderSafeContent(solution, `Gap ${index + 1} Solution ${solutionIndex + 1}`, hideCitations)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Scenario Testing */}
      {shouldShowSection('scenario-testing') && (data as any).scenarioTesting && (
        <section id="scenario-testing" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Scenario Testing
          </h2>
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-6">
            <div className="text-gray-700 dark:text-gray-300">
              {renderSafeContent((data as any).scenarioTesting, 'Scenario Testing Results', hideCitations)}
            </div>
          </div>
        </section>
      )}

      {/* Coverage Metrics */}
      {shouldShowSection('coverage-metrics') && (data as any).coverageMetrics && (
        <section id="coverage-metrics" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Coverage Metrics
          </h2>
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-6">
            <div className="text-gray-700 dark:text-gray-300">
              {renderSafeContent((data as any).coverageMetrics, 'Coverage Metrics Summary', hideCitations)}
            </div>
          </div>
        </section>
      )}

      {/* Analysis Summary (Coverage Analysis specific) */}
      {shouldShowSection('analysis-summary') && (data as any).analysisSummary && (
        <section id="analysis-summary" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Analysis Summary
          </h2>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
            {renderSafeContent((data as any).analysisSummary, 'Analysis Summary', hideCitations)}
          </div>
        </section>
      )}

      {/* Coverage Summary (Coverage Analysis specific) */}
      {shouldShowSection('coverage-summary') && (data as any).coverageSummary && (
        <section id="coverage-summary" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Coverage Summary
          </h2>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
            {renderSafeContent((data as any).coverageSummary, 'Coverage Summary', hideCitations)}
          </div>
        </section>
      )}

      {/* Summary (Generic) */}
      {(data as any).summary && (
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Summary
          </h2>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
            {renderSafeContent((data as any).summary, 'Summary', hideCitations)}
          </div>
        </section>
      )}

      {/* Cost Benefit Analysis Examples */}
      {(data as any).costBenefitAnalysisExamples && Array.isArray((data as any).costBenefitAnalysisExamples) && (data as any).costBenefitAnalysisExamples.length > 0 && (
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Cost Benefit Analysis Examples
          </h2>
          <div className="space-y-6">
            {(data as any).costBenefitAnalysisExamples.map((example: any, index: number) => (
              <div key={index} className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6">
                <div className="mb-4">
                  <h4 className="font-bold text-xl text-gray-800 dark:text-white mb-3">
                    Recommendation {index + 1}
                  </h4>
                  <div className="text-gray-700 dark:text-gray-300 mb-4">
                    {renderSafeContent(example.recommendation, `Cost Benefit Example ${index + 1} Recommendation`, hideCitations)}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                    <h5 className="font-semibold text-sm mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">Implementation Cost</h5>
                    <div className="text-gray-700 dark:text-gray-300 font-medium">
                      {renderSafeContent(example.implementationCost, `Cost Benefit Example ${index + 1} Implementation Cost`, hideCitations)}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                    <h5 className="font-semibold text-sm mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">Potential Savings</h5>
                    <div className="text-gray-700 dark:text-gray-300 font-medium">
                      {renderSafeContent(example.potentialSavings, `Cost Benefit Example ${index + 1} Potential Savings`, hideCitations)}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                    <h5 className="font-semibold text-sm mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">Payback Period</h5>
                    <div className="text-gray-700 dark:text-gray-300 font-medium">
                      {renderSafeContent(example.paybackPeriod, `Cost Benefit Example ${index + 1} Payback Period`, hideCitations)}
                    </div>
                  </div>
                </div>
                
                {example.implementationComplexity && (
                  <div className="mb-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <h5 className="font-semibold text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Implementation Complexity</h5>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        example.implementationComplexity === 'Low' ? 'bg-green-100 text-green-800' :
                        example.implementationComplexity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {example.implementationComplexity}
                      </span>
                    </div>
                  </div>
                )}
                
                {example.nonFinancialBenefits && Array.isArray(example.nonFinancialBenefits) && example.nonFinancialBenefits.length > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <h5 className="font-semibold text-sm mb-3 text-blue-800 dark:text-blue-200 uppercase tracking-wide">Non-Financial Benefits</h5>
                    <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
                      {example.nonFinancialBenefits.map((benefit: string, benefitIndex: number) => (
                        <li key={benefitIndex}>{renderSafeContent(benefit, `Cost Benefit Example ${index + 1} Benefit ${benefitIndex + 1}`, hideCitations)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Clause Wording Comparison */}
      {shouldShowSection('clause-wording-comparison') && data.clauseWordingComparison && (data.clauseWordingComparison.comparisons || data.clauseWordingComparison.analysis) && (
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Clause Wording Comparison
          </h2>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-3">
              <div className="text-blue-600 dark:text-blue-400 mr-2">
                🔍
              </div>
              <h3 className="font-semibold text-lg text-gray-800 dark:text-white">
                Policy Language Analysis
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {data.clauseWordingComparison.description || "Detailed comparison of clause wording between policies or against standard language."}
            </p>
            
            {data.clauseWordingComparison.analysis && (
              <div className="mb-6">
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {renderSafeContent(data.clauseWordingComparison.analysis, 'Clause Wording Analysis', hideCitations)}
                </div>
              </div>
            )}
            
            {data.clauseWordingComparison.comparisons && data.clauseWordingComparison.comparisons.length > 0 && (
              <div>
                <h4 className="font-semibold text-lg mb-4 text-gray-800 dark:text-white">Detailed Comparisons:</h4>
                {data.clauseWordingComparison.comparisons.map((comparison: any, index: number) => (
                  <div key={index} className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-4 last:mb-0">
                    <div className="mb-4">
                      <h5 className="font-bold text-lg text-gray-800 dark:text-white mb-2">
                        {comparison.clause || comparison.section || `Comparison ${index + 1}`}
                      </h5>
                      {comparison.description && (
                        <div className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                          {comparison.description}
                        </div>
                      )}
                    </div>
                    
                    {comparison.currentWording && (
                      <div className="mb-4">
                        <h6 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">Current Wording:</h6>
                        <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded border-l-4 border-blue-400">
                          <div className="text-gray-700 dark:text-gray-300 text-sm">
                            {renderSafeContent(comparison.currentWording, `Comparison ${index + 1} Current Wording`, hideCitations)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {comparison.proposedWording && (
                      <div className="mb-4">
                        <h6 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">Proposed/Alternative Wording:</h6>
                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded border-l-4 border-green-400">
                          <div className="text-gray-700 dark:text-gray-300 text-sm">
                            {renderSafeContent(comparison.proposedWording, `Comparison ${index + 1} Proposed Wording`, hideCitations)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {comparison.standardWording && (
                      <div className="mb-4">
                        <h6 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">Standard Industry Wording:</h6>
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded border-l-4 border-yellow-400">
                          <div className="text-gray-700 dark:text-gray-300 text-sm">
                            {renderSafeContent(comparison.standardWording, `Comparison ${index + 1} Standard Wording`, hideCitations)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {comparison.differences && (
                      <div className="mb-4">
                        <h6 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">Key Differences:</h6>
                        <div className="text-gray-700 dark:text-gray-300 text-sm">
                          {renderSafeContent(comparison.differences, `Comparison ${index + 1} Differences`, hideCitations)}
                        </div>
                      </div>
                    )}
                    
                    {comparison.impact && (
                      <div className="mb-4">
                        <h6 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">Impact Analysis:</h6>
                        <div className="text-gray-700 dark:text-gray-300 text-sm">
                          {renderSafeContent(comparison.impact, `Comparison ${index + 1} Impact`, hideCitations)}
                        </div>
                      </div>
                    )}
                    
                    {comparison.recommendation && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                        <h6 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">Recommendation:</h6>
                        <div className="text-gray-700 dark:text-gray-300 text-sm">
                          {renderSafeContent(comparison.recommendation, `Comparison ${index + 1} Recommendation`, hideCitations)}
                        </div>
                      </div>
                    )}
                    
                    {comparison.citations && comparison.citations.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <h6 className="font-semibold text-sm mb-2 text-gray-600 dark:text-gray-400">Supporting Documents:</h6>
                        <div className="flex flex-wrap gap-2">
                          {comparison.citations.map((citation: any, citIndex: number) => (
                            <button
                              key={citIndex}
                              onClick={() => onCitationClick({
                                type: 'page',
                                page: citation.page,
                                section: citation.section,
                                sourceDocId: citation.docId || citation.sourceDocId,
                                documentId: citation.docId || citation.sourceDocId,
                                documentName: citation.docName,
                                clickable: true,
                                url: citation.docId || citation.sourceDocId,
                                found: true
                              })}
                              className="inline-flex items-center justify-center px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                              title={`${citation.docName} - Page ${citation.page}${citation.section ? ` - ${citation.section}` : ''}`}
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {citation.docName} (p. {citation.page})
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Risk-Shift Navigator Section */}
      {shouldShowSection('risk-shift-navigator') && (data as any).riskShift && (
        <div {...getSectionDataAttrs('risk-shift-navigator')} style={getSectionStyles('risk-shift-navigator')}>
        <section id="risk-shift-navigator" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-red-500 pb-2">
            🔄 Risk-Shift Navigator
          </h2>

          {/* TL;DR - Executive Summary */}
          {(data as any).riskShift.tldr && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-6 mb-6 rounded-lg">
              <h3 className="text-xl font-bold mb-3 text-red-800 dark:text-red-300 flex items-center">
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Critical Summary (TL;DR)
              </h3>
              <div className="text-gray-800 dark:text-gray-200 text-lg leading-relaxed">
                {renderSafeContent((data as any).riskShift.tldr, 'Risk-Shift TL;DR', hideCitations)}
              </div>
            </div>
          )}

          {/* Key Talking Points */}
          {(data as any).riskShift.keyTalkingPoints && (data as any).riskShift.keyTalkingPoints.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold mb-4 text-blue-800 dark:text-blue-300">Key Talking Points</h3>
              <ul className="space-y-3">
                {(data as any).riskShift.keyTalkingPoints.map((point: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-gray-800 dark:text-gray-200 leading-relaxed flex-1">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Detailed Comparison Table */}
          {(data as any).riskShift.detailedComparisonTable && shouldShowSection('detailed-change-analysis') && (
            <div id="detailed-change-analysis" className="mb-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">Detailed Change Analysis</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      {(data as any).riskShift.detailedComparisonTable.headers?.map((header: string, index: number) => (
                        <th key={index} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-600">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data as any).riskShift.detailedComparisonTable.rows?.map((row: any[], rowIndex: number) => (
                      <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}>
                        {row.map((cell: any, cellIndex: number) => (
                          <td key={cellIndex} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                            {typeof cell === 'string' ? cell : renderSafeContent(cell, `Risk Shift Table ${rowIndex}-${cellIndex}`, hideCitations)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Meta Analysis */}
          {(data as any).riskShift.metaAnalysis && shouldShowSection('meta-analysis-strategic-insights') && (
            <div id="meta-analysis-strategic-insights" className="space-y-6">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white">Meta-Analysis & Strategic Insights</h3>

              {/* Suspicious Absences */}
              {(data as any).riskShift.metaAnalysis.suspiciousAbsences && (data as any).riskShift.metaAnalysis.suspiciousAbsences.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-5">
                  <h4 className="font-bold text-lg mb-3 text-yellow-800 dark:text-yellow-300 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Suspicious Absences / Missing Data
                  </h4>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                    {(data as any).riskShift.metaAnalysis.suspiciousAbsences.map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Interconnectivity Analysis */}
              {(data as any).riskShift.metaAnalysis.interconnectivityAnalysis && (data as any).riskShift.metaAnalysis.interconnectivityAnalysis.length > 0 && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-300 dark:border-purple-700 rounded-lg p-5">
                  <h4 className="font-bold text-lg mb-3 text-purple-800 dark:text-purple-300">Interconnected Changes & Compound Effects</h4>
                  <div className="space-y-3">
                    {(data as any).riskShift.metaAnalysis.interconnectivityAnalysis.map((item: any, index: number) => (
                      <div key={index} className="border-l-4 border-purple-400 pl-4">
                        <div className="font-semibold text-gray-800 dark:text-gray-200 mb-1">{item.interaction}</div>
                        <div className="text-gray-700 dark:text-gray-300 text-sm">{item.explanation}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strategic Actionability */}
              {(data as any).riskShift.metaAnalysis.strategicActionability && (data as any).riskShift.metaAnalysis.strategicActionability.length > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg p-5">
                  <h4 className="font-bold text-lg mb-3 text-green-800 dark:text-green-300">Strategic Action Items</h4>
                  <div className="space-y-3">
                    {(data as any).riskShift.metaAnalysis.strategicActionability.map((item: any, index: number) => (
                      <div key={index} className="bg-white dark:bg-gray-800 rounded p-4 border border-green-200 dark:border-green-700">
                        <div className="flex items-start justify-between mb-2">
                          <div className="font-semibold text-gray-800 dark:text-gray-200">{item.action}</div>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            item.priority === 'High' ? 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            item.priority === 'Medium' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}>
                            {item.priority}
                          </span>
                        </div>
                        <div className="text-gray-700 dark:text-gray-300 text-sm">{item.rationale}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Blind Spot Acknowledgment */}
              {(data as any).riskShift.metaAnalysis.blindSpotAcknowledgment && (data as any).riskShift.metaAnalysis.blindSpotAcknowledgment.length > 0 && (
                <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-5">
                  <h4 className="font-bold text-lg mb-3 text-gray-800 dark:text-gray-300">Limitations & Blind Spots</h4>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                    {(data as any).riskShift.metaAnalysis.blindSpotAcknowledgment.map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Professional Disclaimer */}
              {(data as any).riskShift.metaAnalysis.professionalDisclaimer && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-5 rounded">
                  <h4 className="font-bold text-sm mb-2 text-amber-800 dark:text-amber-300 uppercase tracking-wide">Professional Disclaimer</h4>
                  <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                    {(data as any).riskShift.metaAnalysis.professionalDisclaimer}
                  </p>
                </div>
              )}
            </div>
          )}
        </section>
        </div>
      )}

      {/* Insurance Requirements Analysis Section v2.0 */}
      {shouldShowSection('insurance-requirements') && data.responseMetadata?.workflowType === 'insurance_requirements' && (
        <div {...getSectionDataAttrs('insurance-requirements')} style={getSectionStyles('insurance-requirements')}>
        <section id="insurance-requirements" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-500 pb-2">
            Insurance Requirements Analysis
          </h2>

          {/* TIER 1: Executive Summary */}
          {shouldShowSection('insurance-executive-summary') && data.executiveSummary && (
            <div id="insurance-executive-summary" className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Executive Summary</h3>

              {/* Recommendation Badge */}
              {(data.executiveSummary as any).recommendation && (
                <div className="mb-4">
                  <span className={`inline-block px-4 py-2 rounded-lg text-lg font-bold ${
                    (data.executiveSummary as any).recommendation === 'APPROVE' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                    (data.executiveSummary as any).recommendation === 'DECLINE' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                    'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                  }`}>
                    {(data.executiveSummary as any).recommendation}
                  </span>
                </div>
              )}

              {/* Overall Status (2-4 paragraph narrative) */}
              {((data.executiveSummary as any).overallStatus || (data.executiveSummary as any).summary) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 mb-4 rounded-lg">
                  <div className="text-gray-800 dark:text-gray-200 leading-relaxed">
                    {renderSafeContent(
                      (data.executiveSummary as any).overallStatus || (data.executiveSummary as any).summary,
                      'Overall Status',
                      hideCitations
                    )}
                  </div>
                </div>
              )}

              {/* Recommendation Rationale */}
              {(data.executiveSummary as any).recommendationRationale && (
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">Recommendation Rationale</h4>
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="text-gray-800 dark:text-gray-200">
                      {renderSafeContent(
                        (data.executiveSummary as any).recommendationRationale,
                        'Recommendation Rationale',
                        hideCitations
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Critical Deficiencies */}
              {(data.executiveSummary as any).criticalDeficiencies && Array.isArray((data.executiveSummary as any).criticalDeficiencies) && (data.executiveSummary as any).criticalDeficiencies.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-red-700 dark:text-red-400">Critical Deficiencies</h4>
                  <div className="space-y-3">
                    {(data.executiveSummary as any).criticalDeficiencies.map((item: any, index: number) => (
                      <div key={index} className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <div className="text-gray-800 dark:text-gray-200">
                          {renderSafeContent(item, `Critical Deficiency ${index + 1}`, hideCitations)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Legacy: Key Talking Points (v1 compatibility) */}
              {(data.executiveSummary as any).keyTalkingPoints && Array.isArray((data.executiveSummary as any).keyTalkingPoints) && (data.executiveSummary as any).keyTalkingPoints.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-xl font-semibold mb-3 text-gray-800 dark:text-white">Key Talking Points</h4>
                  <div className="space-y-4">
                    {(data.executiveSummary as any).keyTalkingPoints.map((item: any, index: number) => (
                      <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                        <h5 className="font-bold text-lg text-indigo-700 dark:text-indigo-300 mb-2">{item.title}</h5>
                        <div className="mb-2">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Point: </span>
                          <span className="text-gray-800 dark:text-gray-200">{item.point}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Action: </span>
                          <span className="text-gray-800 dark:text-gray-200">{item.action}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Legacy: TL;DR Section (v1 compatibility) */}
          {shouldShowSection('tldr') && (data as any).tldr && (
            <div id="tldr" className="bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-500 p-6 mb-6 rounded-lg">
              <h3 className="text-xl font-bold mb-3 text-indigo-800 dark:text-indigo-300 flex items-center">
                TL;DR
              </h3>
              <div className="text-gray-800 dark:text-gray-200 text-lg leading-relaxed">
                {renderSafeContent((data as any).tldr, 'Insurance Requirements TL;DR', hideCitations)}
              </div>
            </div>
          )}

          {/* TIER 2: Compliance Matrix (v2.0) */}
          {shouldShowSection('compliance-matrix') && (data as any).complianceMatrix && Array.isArray((data as any).complianceMatrix) && (data as any).complianceMatrix.length > 0 && (
            <div id="compliance-matrix" className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Compliance Matrix</h3>
              <div className="space-y-4">
                {(data as any).complianceMatrix.map((item: any, index: number) => (
                  <div key={index} className={`border rounded-lg p-5 ${
                    item.complianceStatus === 'PRECISELY MET' ? 'border-green-300 bg-green-50 dark:bg-green-900/20' :
                    item.complianceStatus === 'NOT MET' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' :
                    'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="font-bold text-lg text-gray-800 dark:text-gray-200">
                        Requirement {item.requirementNumber}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        item.complianceStatus === 'PRECISELY MET' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                        item.complianceStatus === 'NOT MET' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                        'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                      }`}>
                        {item.complianceStatus}
                      </span>
                    </div>

                    <div className="mb-3">
                      <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Contractual Requirement:</h4>
                      <p className="text-gray-800 dark:text-gray-200 italic">"{item.requirement}"</p>
                    </div>

                    {item.policyProvision && (
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Policy Provision:</h4>
                        <p className="text-gray-800 dark:text-gray-200 text-sm bg-gray-100 dark:bg-gray-700 p-2 rounded">{item.policyProvision}</p>
                      </div>
                    )}

                    {item.rationale && (
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Rationale:</h4>
                        <div className="text-gray-800 dark:text-gray-200">
                          {renderSafeContent(item.rationale, `Compliance Rationale ${index}`, hideCitations)}
                        </div>
                      </div>
                    )}

                    {item.actionRequired && item.actionRequired !== 'None' && (
                      <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                        <h4 className="font-semibold text-orange-700 dark:text-orange-400 mb-1">Action Required:</h4>
                        <p className="text-gray-800 dark:text-gray-200">{item.actionRequired}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Legacy: Compliance Table (v1 compatibility) */}
          {shouldShowSection('compliance-table') && (data as any).complianceTable && Array.isArray((data as any).complianceTable) && (data as any).complianceTable.length > 0 && (
            <div id="compliance-table" className="mb-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">Compliance Analysis</h3>
              <div className="space-y-4">
                {(data as any).complianceTable.map((item: any, index: number) => (
                  <div key={index} className={`border rounded-lg p-5 ${
                    item.complianceStatus === 'Precisely Met' ? 'border-green-300 bg-green-50 dark:bg-green-900/20' :
                    item.complianceStatus === 'Not Met' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' :
                    'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="font-bold text-lg text-gray-800 dark:text-gray-200">
                        Requirement {item.requirementNumber}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        item.complianceStatus === 'Precisely Met' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                        item.complianceStatus === 'Not Met' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                        'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                      }`}>
                        {item.complianceStatus}
                      </span>
                    </div>

                    <div className="mb-3">
                      <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Contractual Requirement:</h4>
                      <p className="text-gray-800 dark:text-gray-200">{item.contractualRequirement}</p>
                    </div>

                    {item.analysisAndReasoning && (
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Analysis & Reasoning:</h4>
                        <div className="text-gray-800 dark:text-gray-200">
                          {renderSafeContent(item.analysisAndReasoning, `Compliance Analysis ${index}`, hideCitations)}
                        </div>
                      </div>
                    )}

                    {item.riskPerspective && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                        {item.riskPerspective.contractual && (
                          <div>
                            <h5 className="font-semibold text-sm text-gray-600 dark:text-gray-400 mb-1">Contractual Risk:</h5>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{item.riskPerspective.contractual}</p>
                          </div>
                        )}
                        {item.riskPerspective.insurance && (
                          <div>
                            <h5 className="font-semibold text-sm text-gray-600 dark:text-gray-400 mb-1">Insurance Risk:</h5>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{item.riskPerspective.insurance}</p>
                          </div>
                        )}
                        {item.riskPerspective.financial && (
                          <div>
                            <h5 className="font-semibold text-sm text-gray-600 dark:text-gray-400 mb-1">Financial Risk:</h5>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{item.riskPerspective.financial}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TIER 3: Interconnected Analysis (v2.0) */}
          {shouldShowSection('interconnected-analysis') && (data as any).interconnectedAnalysis && (
            <div id="interconnected-analysis" className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Interconnected Risk Analysis</h3>

              {/* Operating Model Identified */}
              {(data as any).interconnectedAnalysis.operatingModelIdentified && (
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">Operating Model Identified</h4>
                  <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4 rounded-r-lg">
                    <div className="text-gray-800 dark:text-gray-200">
                      {renderSafeContent(
                        (data as any).interconnectedAnalysis.operatingModelIdentified,
                        'Operating Model',
                        hideCitations
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Deficiency Synthesis */}
              {(data as any).interconnectedAnalysis.deficiencySynthesis && Array.isArray((data as any).interconnectedAnalysis.deficiencySynthesis) && (data as any).interconnectedAnalysis.deficiencySynthesis.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">Deficiency Synthesis</h4>
                  <div className="space-y-3">
                    {(data as any).interconnectedAnalysis.deficiencySynthesis.map((item: any, index: number) => (
                      <div key={index} className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 p-4 rounded-lg">
                        <div className="text-gray-800 dark:text-gray-200">
                          {renderSafeContent(item, `Deficiency Synthesis ${index + 1}`, hideCitations)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Escalations */}
              {(data as any).interconnectedAnalysis.riskEscalations && Array.isArray((data as any).interconnectedAnalysis.riskEscalations) && (data as any).interconnectedAnalysis.riskEscalations.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2 text-red-700 dark:text-red-400">Risk Escalations</h4>
                  <div className="space-y-3">
                    {(data as any).interconnectedAnalysis.riskEscalations.map((item: any, index: number) => (
                      <div key={index} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-4 rounded-lg">
                        <div className="font-semibold text-gray-800 dark:text-gray-200 mb-2">{item.deficiency}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-600 dark:text-gray-400">Original Risk: </span>
                            <span className="text-gray-800 dark:text-gray-200">{item.originalRisk}</span>
                          </div>
                          <div>
                            <span className="font-medium text-red-600 dark:text-red-400">Compound Risk: </span>
                            <span className="text-gray-800 dark:text-gray-200">{item.compoundRisk}</span>
                          </div>
                        </div>
                        {item.reasoning && (
                          <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium">Reasoning: </span>{item.reasoning}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Coverage Philosophy Assessment */}
              {(data as any).interconnectedAnalysis.coveragePhilosophyAssessment && (
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">Coverage Philosophy Assessment</h4>
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                    <div className="text-gray-800 dark:text-gray-200">
                      {renderSafeContent(
                        (data as any).interconnectedAnalysis.coveragePhilosophyAssessment,
                        'Coverage Philosophy',
                        hideCitations
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Synthesis Conclusion */}
              {(data as any).interconnectedAnalysis.synthesisConclusion && (
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">Synthesis Conclusion</h4>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-500 p-4 rounded-r-lg">
                    <div className="text-gray-800 dark:text-gray-200">
                      {renderSafeContent(
                        (data as any).interconnectedAnalysis.synthesisConclusion,
                        'Synthesis Conclusion',
                        hideCitations
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* NYTPAO Analysis (Optional - NY projects only) */}
          {shouldShowSection('nytpao-analysis') && (data as any).nytpaoAnalysis && (
            <div id="nytpao-analysis" className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">NY Labor Law (NYTPAO) Analysis</h3>

              {/* Tower Assessment */}
              {(data as any).nytpaoAnalysis.towerAssessment && Array.isArray((data as any).nytpaoAnalysis.towerAssessment) && (data as any).nytpaoAnalysis.towerAssessment.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">Coverage Tower Assessment</h4>
                  <div className="space-y-3">
                    {(data as any).nytpaoAnalysis.towerAssessment.map((layer: any, index: number) => (
                      <div key={index} className={`border rounded-lg p-4 ${
                        layer.nytpaoStatus === 'COVERED' ? 'border-green-300 bg-green-50 dark:bg-green-900/20' :
                        'border-red-300 bg-red-50 dark:bg-red-900/20'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-bold text-gray-800 dark:text-gray-200">{layer.layer}</div>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                            layer.nytpaoStatus === 'COVERED' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                            'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                          }`}>
                            {layer.nytpaoStatus}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                          <div><span className="font-medium">Carrier:</span> {layer.carrier}</div>
                          <div><span className="font-medium">Limit:</span> {layer.limit}</div>
                        </div>
                        {layer.checkpointsFailed && layer.checkpointsFailed.length > 0 && (
                          <div className="mt-2">
                            <div className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Failed Checkpoints:</div>
                            <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                              {layer.checkpointsFailed.map((cp: any, cpIndex: number) => (
                                <li key={cpIndex}>
                                  <span className="font-medium">#{cp.checkpointNumber}:</span> {cp.description}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {layer.impact && (
                          <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                            {renderSafeContent(layer.impact, `NYTPAO Impact ${index}`, hideCitations)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Overall Assessment */}
              {(data as any).nytpaoAnalysis.overallNytpaoAssessment && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-white">Overall NYTPAO Assessment</h4>
                  <div className="text-gray-800 dark:text-gray-200">
                    {renderSafeContent(
                      (data as any).nytpaoAnalysis.overallNytpaoAssessment,
                      'Overall NYTPAO Assessment',
                      hideCitations
                    )}
                  </div>
                </div>
              )}

              {/* Summary Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(data as any).nytpaoAnalysis.totalCoverageWithNytpao && (
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Coverage</div>
                    <div className="font-bold text-gray-800 dark:text-white">{(data as any).nytpaoAnalysis.totalCoverageWithNytpao}</div>
                  </div>
                )}
                {(data as any).nytpaoAnalysis.gapLocation && (
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Gap Location</div>
                    <div className="font-bold text-red-700 dark:text-red-400">{(data as any).nytpaoAnalysis.gapLocation}</div>
                  </div>
                )}
                {(data as any).nytpaoAnalysis.riskLevel && (
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Risk Level</div>
                    <div className="font-bold text-gray-800 dark:text-white">{(data as any).nytpaoAnalysis.riskLevel}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stacking Analysis (Optional - excess/umbrella only) */}
          {shouldShowSection('stacking-analysis') && (data as any).stackingAnalysis && Array.isArray((data as any).stackingAnalysis) && (data as any).stackingAnalysis.length > 0 && (
            <div id="stacking-analysis" className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Coverage Stacking Analysis</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700">
                      <th className="text-left p-3 font-semibold text-gray-800 dark:text-white">Coverage Line</th>
                      <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">Required M.U.L.</th>
                      <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">Actual Primary</th>
                      <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">Gap/Surplus</th>
                      <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">Total Tower</th>
                      <th className="text-center p-3 font-semibold text-gray-800 dark:text-white">Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data as any).stackingAnalysis.map((item: any, index: number) => (
                      <tr key={index} className="border-b border-gray-200 dark:border-gray-600">
                        <td className="p-3 font-medium text-gray-800 dark:text-white">{item.coverageLine}</td>
                        <td className="p-3 text-right text-gray-600 dark:text-gray-400">{item.requiredMUL}</td>
                        <td className="p-3 text-right text-gray-600 dark:text-gray-400">{item.actualPrimaryLimit}</td>
                        <td className={`p-3 text-right font-medium ${
                          item.gapOrSurplus?.includes('-') || item.gapOrSurplus?.toLowerCase().includes('gap') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                        }`}>{item.gapOrSurplus}</td>
                        <td className="p-3 text-right font-bold text-gray-800 dark:text-white">{item.totalTower}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            item.compliance === 'MET' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                            'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                          }`}>
                            {item.compliance}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Hard Hammer Analysis (Optional) */}
          {shouldShowSection('hard-hammer-analysis') && (data as any).hardHammerAnalysis && (
            <div id="hard-hammer-analysis" className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-red-700 dark:text-red-400">Hard Hammer Compliance Analysis</h3>

              {(data as any).hardHammerAnalysis.endorsementFormNumber && (
                <div className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Endorsement Form:</span> {(data as any).hardHammerAnalysis.endorsementFormNumber}
                </div>
              )}

              {/* Requirements/Traps */}
              {(data as any).hardHammerAnalysis.requirements && Array.isArray((data as any).hardHammerAnalysis.requirements) && (data as any).hardHammerAnalysis.requirements.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">Compliance Requirements & Traps</h4>
                  <div className="space-y-3">
                    {(data as any).hardHammerAnalysis.requirements.map((req: any, index: number) => (
                      <div key={index} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 p-4 rounded-lg">
                        <div className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                          ({req.id}) {req.language}
                        </div>
                        {req.complianceTrap && (
                          <div className="text-sm mb-1">
                            <span className="font-medium text-orange-700 dark:text-orange-400">Compliance Trap: </span>
                            <span className="text-gray-700 dark:text-gray-300">{req.complianceTrap}</span>
                          </div>
                        )}
                        {req.consequence && (
                          <div className="text-sm">
                            <span className="font-medium text-red-700 dark:text-red-400">Consequence: </span>
                            <span className="text-gray-700 dark:text-gray-300">{req.consequence}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Extrinsic Evidence */}
              {(data as any).hardHammerAnalysis.extrinsicEvidenceClause && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-4 rounded-lg">
                  <div className="font-semibold text-red-800 dark:text-red-300 mb-2">Extrinsic Evidence Clause Detected</div>
                  {(data as any).hardHammerAnalysis.extrinsicEvidenceLanguage && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 italic">"{(data as any).hardHammerAnalysis.extrinsicEvidenceLanguage}"</p>
                  )}
                </div>
              )}

              {/* Overall Assessment & Recommendation */}
              {(data as any).hardHammerAnalysis.overallAssessment && (
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg mb-3">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-white">Overall Assessment</h4>
                  <div className="text-gray-800 dark:text-gray-200">
                    {renderSafeContent(
                      (data as any).hardHammerAnalysis.overallAssessment?.text || (data as any).hardHammerAnalysis.overallAssessment,
                      'Hard Hammer Assessment',
                      hideCitations
                    )}
                  </div>
                </div>
              )}
              {(data as any).hardHammerAnalysis.recommendation && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <h4 className="font-semibold mb-2 text-gray-800 dark:text-white">Recommendation</h4>
                  <div className="text-gray-800 dark:text-gray-200">
                    {renderSafeContent(
                      (data as any).hardHammerAnalysis.recommendation?.text || (data as any).hardHammerAnalysis.recommendation,
                      'Hard Hammer Recommendation',
                      hideCitations
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Decision Pathway */}
          {shouldShowSection('decision-pathway') && (data as any).decisionPathway && (
            <div id="decision-pathway" className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Decision Pathway</h3>
              <div className={`border-l-4 p-5 rounded-r-lg ${
                (data as any).decisionPathway.pathway === '1' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' :
                (data as any).decisionPathway.pathway === '7' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl font-bold text-gray-800 dark:text-gray-200">
                    Pathway {(data as any).decisionPathway.pathway}
                  </span>
                  <span className={`px-3 py-1 rounded-full font-bold ${
                    (data as any).decisionPathway.pathway === '1' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                    (data as any).decisionPathway.pathway === '7' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                    'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                  }`}>
                    {(data as any).decisionPathway.pathwayName}
                  </span>
                </div>
                {(data as any).decisionPathway.rationale && (
                  <div className="mb-3">
                    <h4 className="font-semibold mb-1 text-gray-700 dark:text-gray-300">Rationale</h4>
                    <div className="text-gray-800 dark:text-gray-200">
                      {renderSafeContent(
                        (data as any).decisionPathway.rationale?.text || (data as any).decisionPathway.rationale,
                        'Decision Pathway Rationale',
                        hideCitations
                      )}
                    </div>
                  </div>
                )}
                {(data as any).decisionPathway.specificActions && Array.isArray((data as any).decisionPathway.specificActions) && (data as any).decisionPathway.specificActions.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 text-gray-700 dark:text-gray-300">Specific Actions</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {(data as any).decisionPathway.specificActions.map((action: any, index: number) => (
                        <li key={index} className="text-gray-800 dark:text-gray-200">
                          {renderSafeContent(action?.text || action, `Specific Action ${index + 1}`, hideCitations)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Blind Spot Acknowledgement */}
          {shouldShowSection('blind-spot-acknowledgement') && (data as any).blindSpotAcknowledgement && (
            <div id="blind-spot-acknowledgement" className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-5 mb-6">
              <h4 className="font-bold text-lg mb-3 text-gray-800 dark:text-gray-300">Limitations & Blind Spots</h4>

              {/* Document Completeness Assessment (v2) */}
              {(data as any).blindSpotAcknowledgement.documentCompletenessAssessment && (
                <div className="text-gray-700 dark:text-gray-300 mb-3">
                  {renderSafeContent(
                    (data as any).blindSpotAcknowledgement.documentCompletenessAssessment?.text || (data as any).blindSpotAcknowledgement.documentCompletenessAssessment,
                    'Document Completeness',
                    hideCitations
                  )}
                </div>
              )}

              {/* Limitations Due to Missing Info (v2) */}
              {(data as any).blindSpotAcknowledgement.limitationsDueToMissingInfo && (
                <div className="text-gray-700 dark:text-gray-300 mb-3">
                  {renderSafeContent(
                    (data as any).blindSpotAcknowledgement.limitationsDueToMissingInfo?.text || (data as any).blindSpotAcknowledgement.limitationsDueToMissingInfo,
                    'Limitations',
                    hideCitations
                  )}
                </div>
              )}

              {/* Legacy v1 support */}
              {typeof (data as any).blindSpotAcknowledgement === 'string' && (
                <div className="text-gray-700 dark:text-gray-300 mb-3">
                  {renderSafeContent((data as any).blindSpotAcknowledgement, 'Blind Spot Acknowledgement', hideCitations)}
                </div>
              )}

              {/* Missing Documents */}
              {(data as any).blindSpotAcknowledgement.missingDocuments && Array.isArray((data as any).blindSpotAcknowledgement.missingDocuments) && (data as any).blindSpotAcknowledgement.missingDocuments.length > 0 && (
                <div className="mt-3">
                  <h5 className="font-semibold text-sm mb-2">Missing Documents:</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {(data as any).blindSpotAcknowledgement.missingDocuments.map((doc: string, index: number) => (
                      <li key={index} className="text-sm">{doc}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Caveat */}
              {(data as any).blindSpotAcknowledgement.caveat && (
                <div className="mt-3 text-sm italic text-gray-600 dark:text-gray-400">
                  {(data as any).blindSpotAcknowledgement.caveat}
                </div>
              )}
            </div>
          )}

          {/* Confidence Assessment (v2.0) */}
          {shouldShowSection('confidence-assessment') && (data as any).confidenceAssessment && (
            <div id="confidence-assessment" className="mb-6">
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-lg font-semibold text-gray-800 dark:text-white">Analysis Confidence:</span>
                  <span className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
                    {(data as any).confidenceAssessment.percentage}
                  </span>
                </div>
                {(data as any).confidenceAssessment.sourceOfConfidence && (
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {renderSafeContent(
                      (data as any).confidenceAssessment.sourceOfConfidence?.text || (data as any).confidenceAssessment.sourceOfConfidence,
                      'Confidence Source',
                      hideCitations
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        </section>
        </div>
      )}

      {/* Forensic Loss Run Analysis Sections */}

      {/* Category Breakdown */}
      {(data as any).categoryBreakdown && Object.keys((data as any).categoryBreakdown).length > 0 && (
        <section id="category-breakdown" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Category Breakdown
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="space-y-3">
              {Object.entries((data as any).categoryBreakdown).map(([category, details]: [string, any]) => (
                <div key={category} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div>
                    <div className="font-medium text-gray-800 dark:text-white">{category}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {details.count || '0'} claims ({details.percentage || '0%'})
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-white">{details.totalIncurred || '$0'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Severity Breakdown */}
      {(data as any).severityBreakdown && Object.keys((data as any).severityBreakdown).length > 0 && (
        <section id="severity-breakdown" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Severity Breakdown
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="space-y-3">
              {Object.entries((data as any).severityBreakdown).map(([severity, details]: [string, any]) => {
                const severityColors: Record<string, string> = {
                  'No Loss': 'bg-gray-100 dark:bg-gray-600',
                  'Minor': 'bg-green-100 dark:bg-green-900/30',
                  'Moderate': 'bg-yellow-100 dark:bg-yellow-900/30',
                  'Severe': 'bg-orange-100 dark:bg-orange-900/30',
                  'Catastrophic': 'bg-red-100 dark:bg-red-900/30'
                };
                return (
                  <div key={severity} className={`p-3 rounded ${severityColors[severity] || 'bg-gray-50 dark:bg-gray-700'}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-gray-800 dark:text-white">{severity}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {details.count || '0'} claims ({details.percentage || '0%'})
                        </div>
                      </div>
                      <div className="font-semibold text-gray-900 dark:text-white">{details.totalIncurred || '$0'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Risk Heat Map */}
      {(data as any).riskHeatMap && Object.keys((data as any).riskHeatMap).length > 0 && (
        <section id="risk-heat-map" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Risk Heat Map (Location × Category)
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 overflow-x-auto print:overflow-visible">
            <table className="w-full print:text-xs print:table-fixed">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="text-left p-3 font-semibold text-gray-800 dark:text-white print:p-1">Location</th>
                  {/* Get all unique categories from all locations */}
                  {Array.from(new Set(
                    Object.values((data as any).riskHeatMap).flatMap((loc: any) => Object.keys(loc))
                  )).map((category: string) => (
                    <th key={category} className="text-right p-3 font-semibold text-gray-800 dark:text-white print:p-1 print:text-xs">{category}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries((data as any).riskHeatMap).map(([location, categories]: [string, any]) => (
                  <tr key={location} className="border-b border-gray-200 dark:border-gray-600">
                    <td className="p-3 font-medium text-gray-800 dark:text-white print:p-1 print:text-xs">{location}</td>
                    {Array.from(new Set(
                      Object.values((data as any).riskHeatMap).flatMap((loc: any) => Object.keys(loc))
                    )).map((category: string) => (
                      <td key={category} className="p-3 text-right text-gray-600 dark:text-gray-400 print:p-1 print:text-xs">
                        {categories[category] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Critical Claims */}
      {(data as any).criticalClaims && (data as any).criticalClaims.length > 0 && (
        <section id="critical-claims" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-red-700 dark:text-red-400 border-b-2 border-red-200 dark:border-red-800 pb-2">
            Critical Claims (Over $50,000)
          </h2>
          <div className="space-y-4">
            {(data as any).criticalClaims.map((claim: any, index: number) => (
              <div key={index} className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/10">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="font-mono font-bold text-gray-900 dark:text-white">{claim.claim_number}</span>
                    {claim.carrier && (
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">({claim.carrier})</span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-red-700 dark:text-red-400">{claim.total_incurred}</div>
                    {claim.status && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        claim.status === 'Open' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                        claim.litigation_flag === 'Yes' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                        'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                      }`}>
                        {claim.status}
                      </span>
                    )}
                  </div>
                </div>
                {claim.primary_category && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <span className="font-medium">Category:</span> {claim.primary_category}
                  </div>
                )}
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {claim.description}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Processing Log */}
      {(data as any).processingLog && (data as any).processingLog.length > 0 && (
        <section id="processing-log" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Processing Log
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="text-left p-3 font-semibold text-gray-800 dark:text-white">Filename</th>
                  <th className="text-left p-3 font-semibold text-gray-800 dark:text-white">Carrier</th>
                  <th className="text-left p-3 font-semibold text-gray-800 dark:text-white">Method</th>
                  <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">Claims</th>
                  <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">Total Incurred</th>
                  <th className="text-center p-3 font-semibold text-gray-800 dark:text-white">Quality</th>
                  <th className="text-left p-3 font-semibold text-gray-800 dark:text-white">Errors</th>
                </tr>
              </thead>
              <tbody>
                {(data as any).processingLog.map((log: any, index: number) => (
                  <tr key={index} className="border-b border-gray-200 dark:border-gray-600">
                    <td className="p-3 text-gray-800 dark:text-white font-mono text-sm">{log.filename}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400">{log.carrier || 'Unknown'}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400">{log.method || 'Generic'}</td>
                    <td className="p-3 text-right text-gray-800 dark:text-white">{log.claimsFound}</td>
                    <td className="p-3 text-right font-semibold text-gray-900 dark:text-white">{log.totalIncurred || '$0'}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        log.quality === 'HIGH' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                        log.quality === 'MEDIUM' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                        'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                      }`}>
                        {log.quality}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-gray-600 dark:text-gray-400">{log.errors || 'None'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Detailed Claims List */}
      {(data as any).detailedClaimsList && (data as any).detailedClaimsList.length > 0 && (
        <section id="detailed-claims-list" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Detailed Claims List ({(data as any).detailedClaimsList.length} claims)
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 overflow-x-auto print:overflow-visible">
            <table className="w-full text-sm print:text-xs">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Claim #</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Carrier</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Loss Date</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Status</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Category</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Severity</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white">Total Incurred</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Description</th>
                </tr>
              </thead>
              <tbody>
                {(data as any).detailedClaimsList.map((claim: any, index: number) => (
                  <tr
                    key={index}
                    className={`border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      !showAllClaims && index >= 50 ? 'hidden print:table-row' : ''
                    }`}
                  >
                    <td className="p-2 font-mono text-gray-800 dark:text-white">{claim.claim_number}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400">{claim.carrier || '-'}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400">{claim.loss_date || '-'}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        claim.status === 'Open' ? 'bg-yellow-200 text-yellow-800' :
                        claim.status === 'Closed' ? 'bg-green-200 text-green-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {claim.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-2 text-gray-600 dark:text-gray-400">{claim.primary_category}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        claim.severity === 'Catastrophic' ? 'bg-red-200 text-red-800' :
                        claim.severity === 'Severe' ? 'bg-orange-200 text-orange-800' :
                        claim.severity === 'Moderate' ? 'bg-yellow-200 text-yellow-800' :
                        claim.severity === 'Minor' ? 'bg-green-200 text-green-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {claim.severity}
                      </span>
                    </td>
                    <td className="p-2 text-right font-semibold text-gray-900 dark:text-white">{formatCurrency(claim.total_incurred)}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 max-w-md whitespace-normal break-words">{claim.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(data as any).detailedClaimsList.length > 50 && (
              <div className="mt-4 text-center print:hidden">
                {!showAllClaims ? (
                  <button
                    onClick={() => setShowAllClaims(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Show All {(data as any).detailedClaimsList.length} Claims (currently showing 50)
                  </button>
                ) : (
                  <button
                    onClick={() => setShowAllClaims(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Show Less (showing all {(data as any).detailedClaimsList.length} claims)
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Claims Summary (Simplified Loss Run Schema) */}
      {(data as any).claimsSummary && Array.isArray((data as any).claimsSummary) && (data as any).claimsSummary.length > 0 && (
        <section id="claims-summary" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Top Claims Summary ({(data as any).claimsSummary.length} claims)
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 overflow-x-auto print:overflow-visible">
            <table className="w-full text-sm print:text-xs">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Claim #</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Loss Date</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Status</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Category</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Severity</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white">Total Incurred</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Description</th>
                </tr>
              </thead>
              <tbody>
                {(data as any).claimsSummary.map((claim: any, index: number) => (
                  <tr key={index} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="p-2 font-mono text-gray-800 dark:text-white">{claim.claimNumber}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400">{claim.lossDate || '-'}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        claim.status === 'Open' ? 'bg-yellow-200 text-yellow-800' :
                        claim.status === 'Closed' ? 'bg-green-200 text-green-800' :
                        claim.status === 'Reopened' ? 'bg-orange-200 text-orange-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {claim.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-2 text-gray-600 dark:text-gray-400">{claim.category}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        claim.severity === 'Catastrophic' ? 'bg-red-200 text-red-800' :
                        claim.severity === 'Severe' ? 'bg-orange-200 text-orange-800' :
                        claim.severity === 'Moderate' ? 'bg-yellow-200 text-yellow-800' :
                        claim.severity === 'Minor' ? 'bg-green-200 text-green-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {claim.severity || 'N/A'}
                      </span>
                    </td>
                    <td className="p-2 text-right font-semibold text-gray-900 dark:text-white">{formatCurrency(claim.totalIncurred)}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 max-w-md whitespace-normal break-words">{claim.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Category Analysis (Simplified Loss Run Schema) */}
      {(data as any).categoryAnalysis && Array.isArray((data as any).categoryAnalysis) && (data as any).categoryAnalysis.length > 0 && (
        <section id="category-analysis" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Category Analysis
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data as any).categoryAnalysis.map((cat: any, index: number) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border-l-4 border-blue-500">
                <h3 className="font-semibold text-lg text-gray-800 dark:text-white mb-2">{cat.category}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Claims:</span>
                    <span className="font-semibold text-gray-800 dark:text-white">{cat.claimCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total Incurred:</span>
                    <span className="font-semibold text-red-700 dark:text-red-300">{formatCurrency(cat.totalIncurred)}</span>
                  </div>
                  {cat.percentageOfTotal && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">% of Total:</span>
                      <span className="font-semibold text-blue-700 dark:text-blue-300">{cat.percentageOfTotal}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Severity Analysis (Simplified Loss Run Schema) */}
      {(data as any).severityAnalysis && Array.isArray((data as any).severityAnalysis) && (data as any).severityAnalysis.length > 0 && (
        <section id="severity-analysis" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Severity Analysis
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(data as any).severityAnalysis.map((sev: any, index: number) => (
              <div key={index} className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border-l-4 ${
                sev.severity === 'Catastrophic' ? 'border-red-500' :
                sev.severity === 'Severe' ? 'border-orange-500' :
                sev.severity === 'Moderate' ? 'border-yellow-500' :
                'border-green-500'
              }`}>
                <h3 className={`font-semibold text-lg mb-2 ${
                  sev.severity === 'Catastrophic' ? 'text-red-700 dark:text-red-300' :
                  sev.severity === 'Severe' ? 'text-orange-700 dark:text-orange-300' :
                  sev.severity === 'Moderate' ? 'text-yellow-700 dark:text-yellow-300' :
                  'text-green-700 dark:text-green-300'
                }`}>{sev.severity}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Claims:</span>
                    <span className="font-semibold text-gray-800 dark:text-white">{sev.claimCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total Incurred:</span>
                    <span className="font-semibold text-gray-800 dark:text-white">{formatCurrency(sev.totalIncurred)}</span>
                  </div>
                  {sev.percentageOfTotal && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">% of Total:</span>
                      <span className="font-semibold text-gray-800 dark:text-white">{sev.percentageOfTotal}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Cross-Foot Check (Loss Run Schema) */}
      {(data as any).crossFootCheck && typeof (data as any).crossFootCheck === 'object' && (
        <section id="cross-foot-check" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Financial Cross-Foot Verification
          </h2>
          <div className={`rounded-lg p-6 border ${(data as any).crossFootCheck.isBalanced ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'}`}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                <div className="text-sm text-gray-600 dark:text-gray-400">Paid Sum</div>
                <div className="text-lg font-bold text-blue-700 dark:text-blue-300">{formatCurrency((data as any).crossFootCheck.paidSum)}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                <div className="text-sm text-gray-600 dark:text-gray-400">Reserve Sum</div>
                <div className="text-lg font-bold text-orange-700 dark:text-orange-300">{formatCurrency((data as any).crossFootCheck.reserveSum)}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                <div className="text-sm text-gray-600 dark:text-gray-400">Calculated Total</div>
                <div className="text-lg font-bold text-purple-700 dark:text-purple-300">{formatCurrency((data as any).crossFootCheck.calculatedTotal)}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                <div className="text-sm text-gray-600 dark:text-gray-400">Incurred Sum</div>
                <div className="text-lg font-bold text-red-700 dark:text-red-300">{formatCurrency((data as any).crossFootCheck.incurredSum)}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                <div className="text-sm text-gray-600 dark:text-gray-400">Variance</div>
                <div className={`text-lg font-bold ${(data as any).crossFootCheck.isBalanced ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {(data as any).crossFootCheck.variance}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                <div className="text-sm text-gray-600 dark:text-gray-400">Status</div>
                <div className={`text-lg font-bold ${(data as any).crossFootCheck.isBalanced ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {(data as any).crossFootCheck.isBalanced ? 'Balanced' : 'Discrepancy'}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Claim Line Items (Loss Run Schema) */}
      {(data as any).claimLineItems && Array.isArray((data as any).claimLineItems) && (data as any).claimLineItems.length > 0 && (
        <section id="claim-line-items" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            All Claim Line Items ({(data as any).claimLineItems.length} items)
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 overflow-x-auto print:overflow-visible">
            <table className="w-full text-sm print:text-xs">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Claim #</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Loss Date</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Status</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Category</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Severity</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white">Paid</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white">Reserve</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white">Incurred</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Description</th>
                </tr>
              </thead>
              <tbody>
                {(data as any).claimLineItems.map((claim: any, index: number) => (
                  <tr key={index} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="p-2 font-mono text-gray-800 dark:text-white">{claim.claimNumber}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400">{claim.lossDate || '-'}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        claim.status === 'Open' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                        claim.status === 'Closed' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                        claim.status === 'Reopened' ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' :
                        'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                      }`}>
                        {claim.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-2 text-gray-600 dark:text-gray-400">{claim.category}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        claim.severity === 'Catastrophic' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                        claim.severity === 'Severe' ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' :
                        claim.severity === 'Moderate' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                        claim.severity === 'Minor' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                        'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                      }`}>
                        {claim.severity || 'N/A'}
                      </span>
                    </td>
                    <td className="p-2 text-right font-semibold text-blue-700 dark:text-blue-300">{formatCurrency(claim.paid)}</td>
                    <td className="p-2 text-right font-semibold text-orange-700 dark:text-orange-300">{formatCurrency(claim.reserve)}</td>
                    <td className="p-2 text-right font-semibold text-red-700 dark:text-red-300">{formatCurrency(claim.incurred)}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 max-w-xs whitespace-normal break-words">{claim.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Top Claims (Loss Run Schema) */}
      {(data as any).topClaims && Array.isArray((data as any).topClaims) && (data as any).topClaims.length > 0 && (
        <section id="top-claims" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Top {(data as any).topClaims.length} Largest Claims
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 overflow-x-auto print:overflow-visible">
            <table className="w-full text-sm print:text-xs">
              <thead>
                <tr className="bg-red-100 dark:bg-red-900/30">
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Rank</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Claim #</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Loss Date</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Status</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white">Incurred</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Description</th>
                </tr>
              </thead>
              <tbody>
                {(data as any).topClaims.map((claim: any, index: number) => (
                  <tr key={index} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="p-2 font-bold text-lg text-red-700 dark:text-red-300">#{claim.rank || index + 1}</td>
                    <td className="p-2 font-mono text-gray-800 dark:text-white">{claim.claimNumber}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400">{claim.dateOfLoss || claim.lossDate || '-'}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        (claim.status === 'Open' || claim.status === 'OPEN') ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                        (claim.status === 'Closed' || claim.status === 'CLOSED') ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                        'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                      }`}>
                        {claim.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-2 text-right font-bold text-xl text-red-700 dark:text-red-300">{formatCurrency(claim.totalIncurred || claim.incurred)}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 max-w-md whitespace-normal break-words">{claim.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Risk Heat Map (Loss Run Schema - New Format) */}
      {(data as any).riskHeatMap && typeof (data as any).riskHeatMap === 'object' && (data as any).riskHeatMap.highPriorityConcerns && (
        <section id="risk-heat-map-new" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Risk Heat Map
          </h2>
          <div className="space-y-4">
            {/* High Priority Concerns */}
            {(data as any).riskHeatMap.highPriorityConcerns && (data as any).riskHeatMap.highPriorityConcerns.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-5 border border-red-200 dark:border-red-700">
                <h3 className="font-semibold text-lg text-red-800 dark:text-red-300 mb-3">High Priority Concerns</h3>
                <ul className="list-disc list-inside space-y-1">
                  {(data as any).riskHeatMap.highPriorityConcerns.map((concern: string, index: number) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300">{concern}</li>
                  ))}
                </ul>
              </div>
            )}
            {/* Trend Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border-l-4 border-purple-500">
                <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">Frequency Trend</h4>
                <div className={`text-xl font-bold ${
                  (data as any).riskHeatMap.frequencyTrend === 'Increasing' ? 'text-red-600' :
                  (data as any).riskHeatMap.frequencyTrend === 'Decreasing' ? 'text-green-600' :
                  'text-yellow-600'
                }`}>
                  {(data as any).riskHeatMap.frequencyTrend === 'Increasing' ? '↑' : (data as any).riskHeatMap.frequencyTrend === 'Decreasing' ? '↓' : '→'} {(data as any).riskHeatMap.frequencyTrend}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border-l-4 border-orange-500">
                <h4 className="font-semibold text-orange-800 dark:text-orange-300 mb-2">Severity Trend</h4>
                <div className={`text-xl font-bold ${
                  (data as any).riskHeatMap.severityTrend === 'Increasing' ? 'text-red-600' :
                  (data as any).riskHeatMap.severityTrend === 'Decreasing' ? 'text-green-600' :
                  'text-yellow-600'
                }`}>
                  {(data as any).riskHeatMap.severityTrend === 'Increasing' ? '↑' : (data as any).riskHeatMap.severityTrend === 'Decreasing' ? '↓' : '→'} {(data as any).riskHeatMap.severityTrend}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Primary Risk Driver</h4>
                <div className="text-lg font-bold text-gray-800 dark:text-white">{(data as any).riskHeatMap.primaryRiskDriver}</div>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* Analyst Notes (Loss Run Schema) */}
      {(data as any).analystNotes && typeof (data as any).analystNotes === 'string' && (data as any).analystNotes.trim() && (
        <section id="analyst-notes" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Analyst Notes
          </h2>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-700">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{(data as any).analystNotes}</p>
          </div>
        </section>
      )}

      {/* Document Manifest (Loss Run Analyzer) */}
      {(data as any).documentManifest && Array.isArray((data as any).documentManifest) && (data as any).documentManifest.length > 0 && (
        <section id="document-manifest" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Analyzed Documents
          </h2>
          <div className="space-y-4">
            {(data as any).documentManifest.map((doc: any, index: number) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-sm font-mono">
                      {doc.documentId || `doc_${index + 1}`}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      doc.formatPattern === 'A' ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200' :
                      doc.formatPattern === 'B' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200' :
                      doc.formatPattern === 'C' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200' :
                      doc.formatPattern === 'D' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200' :
                      doc.formatPattern === 'E' ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}>
                      Pattern {doc.formatPattern || 'Unknown'}
                    </span>
                  </div>
                  {doc.valuationDate && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Valuation: {doc.valuationDate}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {doc.carrier && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Carrier:</span>
                      <div className="font-semibold text-gray-800 dark:text-white">{doc.carrier}</div>
                    </div>
                  )}
                  {doc.insuredName && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Insured:</span>
                      <div className="font-semibold text-gray-800 dark:text-white">{doc.insuredName}</div>
                    </div>
                  )}
                  {doc.policyNumber && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Policy #:</span>
                      <div className="font-mono font-semibold text-gray-800 dark:text-white">{doc.policyNumber}</div>
                    </div>
                  )}
                  {doc.policyPeriod && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Policy Period:</span>
                      <div className="font-semibold text-gray-800 dark:text-white">{doc.policyPeriod}</div>
                    </div>
                  )}
                  {doc.lineOfBusiness && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">LOB:</span>
                      <div className="font-semibold text-gray-800 dark:text-white">{doc.lineOfBusiness}</div>
                    </div>
                  )}
                </div>
                {doc.claimNumberCensus && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Format:</span>
                        <div className="font-mono text-gray-800 dark:text-white">{doc.claimNumberCensus.claimNumberFormat || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Unique Claims:</span>
                        <div className="font-semibold text-blue-700 dark:text-blue-300">{doc.claimNumberCensus.uniqueClaimNumbersFound || 0}</div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Grid Rows:</span>
                        <div className="font-semibold text-gray-800 dark:text-white">{doc.claimNumberCensus.visualRowsInGrid || 0}</div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Extraction Target:</span>
                        <div className="font-semibold text-green-700 dark:text-green-300">{doc.claimNumberCensus.extractionTarget || 0}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Claims Table (Loss Run Analyzer - CSV Compatible) */}
      {(() => {
        const claims = (data as any).claims;
        const workflowType = data.responseMetadata?.workflowType;
        const isLossRunAnalyzer = workflowType === 'loss_run_analyzer' || workflowType?.toLowerCase()?.includes('loss_run_analyzer');
        console.log('🔍 Claims Table Check:', { hasClaims: !!claims, isArray: Array.isArray(claims), length: claims?.length, workflowType, isLossRunAnalyzer });
        return null;
      })()}
      {(data as any).claims && Array.isArray((data as any).claims) && (data as any).claims.length > 0 && (data.responseMetadata?.workflowType === 'loss_run_analyzer' || data.responseMetadata?.workflowType?.toLowerCase()?.includes('loss_run_analyzer')) && (
        <section id="claims-table" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            All Claims ({(data as any).claims.length} records)
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 overflow-x-auto print:overflow-visible">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">Claim #</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">LOB</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">Status</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">Date of Loss</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">Location</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">Cause</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">Paid</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">Reserve</th>
                  <th className="text-right p-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">Incurred</th>
                  <th className="text-left p-2 font-semibold text-gray-800 dark:text-white">Description</th>
                </tr>
              </thead>
              <tbody>
                {(data as any).claims.map((claim: any, index: number) => (
                  <tr key={index} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="p-2 font-mono text-gray-800 dark:text-white whitespace-nowrap">{claim.claimNumber}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{claim.lob || '-'}</td>
                    <td className="p-2 whitespace-nowrap">
                      <span className={`text-xs px-2 py-1 rounded ${
                        claim.claimStatus === 'OPEN' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                        claim.claimStatus === 'CLOSED' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                        'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                      }`}>
                        {claim.claimStatus || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{claim.dateOfLoss || '-'}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{claim.lossState || '-'}{claim.lossCity ? `, ${claim.lossCity}` : ''}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 max-w-[150px] truncate" title={claim.causeOfLoss}>{claim.causeOfLoss || '-'}</td>
                    <td className="p-2 text-right font-semibold text-blue-700 dark:text-blue-300 whitespace-nowrap">{claim.totalPaid ? formatCurrency(claim.totalPaid) : '-'}</td>
                    <td className="p-2 text-right font-semibold text-orange-700 dark:text-orange-300 whitespace-nowrap">{claim.totalReserve ? formatCurrency(claim.totalReserve) : '-'}</td>
                    <td className="p-2 text-right font-bold text-red-700 dark:text-red-300 whitespace-nowrap">{claim.totalIncurred ? formatCurrency(claim.totalIncurred) : '-'}</td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 max-w-xs whitespace-normal break-words text-xs">{claim.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Validation Results (Loss Run Analyzer) */}
      {(data as any).validationResults && Array.isArray((data as any).validationResults) && (data as any).validationResults.length > 0 && (
        <section id="validation-results" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Cross-Foot Validation Results
          </h2>
          <div className="space-y-4">
            {(data as any).validationResults.map((result: any, index: number) => (
              <div key={index} className={`rounded-lg p-5 border ${
                result.status === 'PASS'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{result.documentId}</span>
                  <span className={`px-3 py-1 rounded font-bold ${
                    result.status === 'PASS'
                      ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                      : 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                  }`}>
                    {result.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Claim Census:</span>
                    <div className="font-semibold text-gray-800 dark:text-white">{result.claimNumberCensus || 0}</div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Extracted:</span>
                    <div className={`font-semibold ${result.recordCountMatch ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                      {result.extractedRecordCount || 0}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Source Total:</span>
                    <div className="font-semibold text-gray-800 dark:text-white">{result.sourceTotalIncurred || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Extracted Sum:</span>
                    <div className="font-semibold text-gray-800 dark:text-white">{result.extractedSum || 'N/A'}</div>
                  </div>
                  {result.financialVariance && (
                    <div className="col-span-2">
                      <span className="text-gray-500 dark:text-gray-400">Variance:</span>
                      <div className={`font-semibold ${
                        result.financialVariance === '$0.00' || result.financialVariance === '0'
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-red-700 dark:text-red-300'
                      }`}>
                        {result.financialVariance}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Claim Development (Loss Run Analyzer) */}
      {(data as any).claimDevelopment && Array.isArray((data as any).claimDevelopment) && (data as any).claimDevelopment.length > 0 && (
        <section id="claim-development" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Claim Development Tracking
          </h2>
          <div className="space-y-4">
            {(data as any).claimDevelopment.map((dev: any, index: number) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="font-mono text-lg font-bold text-gray-800 dark:text-white">{dev.claimNumber}</span>
                    {dev.dateOfLoss && (
                      <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loss Date: {dev.dateOfLoss}</span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Total Development</div>
                    <div className={`text-lg font-bold ${
                      parseFloat(dev.percentageChange?.replace('%', '') || '0') > 0
                        ? 'text-red-700 dark:text-red-300'
                        : parseFloat(dev.percentageChange?.replace('%', '') || '0') < 0
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {dev.totalDevelopment || 'N/A'} ({dev.percentageChange || '0%'})
                    </div>
                  </div>
                </div>
                {dev.periods && dev.periods.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700">
                          <th className="text-left p-2 text-gray-600 dark:text-gray-400">Policy Period</th>
                          <th className="text-left p-2 text-gray-600 dark:text-gray-400">Valuation Date</th>
                          <th className="text-right p-2 text-gray-600 dark:text-gray-400">Incurred</th>
                          <th className="text-left p-2 text-gray-600 dark:text-gray-400">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dev.periods.map((period: any, pIdx: number) => (
                          <tr key={pIdx} className="border-t border-gray-200 dark:border-gray-600">
                            <td className="p-2 text-gray-800 dark:text-white">{period.policyPeriod || '-'}</td>
                            <td className="p-2 text-gray-600 dark:text-gray-400">{period.valuationDate || '-'}</td>
                            <td className="p-2 text-right font-semibold text-red-700 dark:text-red-300">{period.incurred || '-'}</td>
                            <td className="p-2">
                              <span className={`text-xs px-2 py-1 rounded ${
                                period.status === 'OPEN' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                                period.status === 'CLOSED' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                                'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                              }`}>
                                {period.status || '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Frequency Analysis (Loss Run Analyzer) */}
      {(data as any).frequencyAnalysis && typeof (data as any).frequencyAnalysis === 'object' && (
        <section id="frequency-analysis" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Frequency Analysis
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Claims Per Period */}
            {(data as any).frequencyAnalysis.claimsPerPeriod && (data as any).frequencyAnalysis.claimsPerPeriod.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border border-blue-200 dark:border-blue-700">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-4">Claims Per Period</h3>
                <div className="space-y-2">
                  {(data as any).frequencyAnalysis.claimsPerPeriod.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400 text-sm">{item.period}</span>
                      <span className="font-bold text-blue-700 dark:text-blue-300">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Claims By LOB */}
            {(data as any).frequencyAnalysis.claimsByLOB && (data as any).frequencyAnalysis.claimsByLOB.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border border-purple-200 dark:border-purple-700">
                <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-4">Claims By Line of Business</h3>
                <div className="space-y-2">
                  {(data as any).frequencyAnalysis.claimsByLOB.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400 text-sm">{item.lob}</span>
                      <span className="font-bold text-purple-700 dark:text-purple-300">{item.count} <span className="text-xs font-normal">({item.percentage})</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Claims By State */}
            {(data as any).frequencyAnalysis.claimsByState && (data as any).frequencyAnalysis.claimsByState.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border border-green-200 dark:border-green-700">
                <h3 className="font-semibold text-green-800 dark:text-green-300 mb-2">Claims By State</h3>
                {/* v4.1 attribution coverage */}
                {(data as any).frequencyAnalysis.attributionCoverage && (
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-3 italic">
                    {(data as any).frequencyAnalysis.attributionCoverage}
                  </div>
                )}
                <div className="space-y-2">
                  {(data as any).frequencyAnalysis.claimsByState.map((item: any, idx: number) => (
                    <div key={idx}>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400 text-sm">{item.state}</span>
                        <span className="font-bold text-green-700 dark:text-green-300">{item.count} <span className="text-xs font-normal">({item.percentage})</span></span>
                      </div>
                      {item.stateSourceBreakdown && (
                        <div className="text-[10px] font-mono text-gray-500 dark:text-gray-500 mt-0.5">{item.stateSourceBreakdown}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Enhanced Severity Analysis (Loss Run Analyzer) */}
      {(data as any).severityAnalysis && typeof (data as any).severityAnalysis === 'object' && (data as any).severityAnalysis.distribution && (
        <section id="severity-analysis" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Severity Analysis
          </h2>
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-4">
              {(data as any).severityAnalysis.averageClaimSize && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Average Claim Size</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{(data as any).severityAnalysis.averageClaimSize}</div>
                </div>
              )}
              {(data as any).severityAnalysis.medianClaimSize && (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Median Claim Size</div>
                  <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">{(data as any).severityAnalysis.medianClaimSize}</div>
                </div>
              )}
            </div>

            {/* v4.1 § 11.3 — Median evidence audit trail */}
            {(data as any).severityAnalysis.medianEvidence && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 border-l-4 border-orange-300 dark:border-orange-600">
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Median &amp; Mean Audit Trail</div>
                <div className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                  {(data as any).severityAnalysis.medianEvidence}
                </div>
              </div>
            )}

            {/* Distribution */}
            {(data as any).severityAnalysis.distribution && (data as any).severityAnalysis.distribution.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700">
                      <th className="text-left p-3 font-semibold text-gray-800 dark:text-white">Category</th>
                      <th className="text-left p-3 font-semibold text-gray-800 dark:text-white">Threshold</th>
                      <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">Count</th>
                      <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">Total Incurred</th>
                      <th className="text-right p-3 font-semibold text-gray-800 dark:text-white">% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data as any).severityAnalysis.distribution.map((dist: any, idx: number) => (
                      <tr key={idx} className={`border-t border-gray-200 dark:border-gray-600 ${
                        dist.category === 'Catastrophic' ? 'bg-red-50 dark:bg-red-900/10' :
                        dist.category === 'Severe' ? 'bg-orange-50 dark:bg-orange-900/10' :
                        dist.category === 'Moderate' ? 'bg-yellow-50 dark:bg-yellow-900/10' :
                        ''
                      }`}>
                        <td className={`p-3 font-semibold ${
                          dist.category === 'Catastrophic' ? 'text-red-700 dark:text-red-300' :
                          dist.category === 'Severe' ? 'text-orange-700 dark:text-orange-300' :
                          dist.category === 'Moderate' ? 'text-yellow-700 dark:text-yellow-300' :
                          'text-green-700 dark:text-green-300'
                        }`}>{dist.category}</td>
                        <td className="p-3 text-gray-600 dark:text-gray-400">{dist.threshold || '-'}</td>
                        <td className="p-3 text-right font-semibold text-gray-800 dark:text-white">{dist.count}</td>
                        <td className="p-3 text-right font-semibold text-red-700 dark:text-red-300">{dist.totalIncurred}</td>
                        <td className="p-3 text-right text-gray-600 dark:text-gray-400">{dist.percentage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Risk Assessment (Simplified Loss Run Schema) */}
      {(data as any).riskAssessment && typeof (data as any).riskAssessment === 'object' && (
        <section id="risk-assessment-simplified" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Risk Assessment
          </h2>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-700">
            {(data as any).riskAssessment.overallRiskLevel && (
              <div className="mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">Overall Risk Level:</span>
                <span className={`ml-2 text-lg font-bold ${
                  (data as any).riskAssessment.overallRiskLevel.toLowerCase().includes('high') ? 'text-red-700 dark:text-red-300' :
                  (data as any).riskAssessment.overallRiskLevel.toLowerCase().includes('moderate') ? 'text-yellow-700 dark:text-yellow-300' :
                  'text-green-700 dark:text-green-300'
                }`}>
                  {(data as any).riskAssessment.overallRiskLevel}
                </span>
              </div>
            )}
            {(data as any).riskAssessment.primaryRiskDrivers && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-800 dark:text-white mb-2">Primary Risk Drivers</h4>
                <p className="text-gray-700 dark:text-gray-300">{(data as any).riskAssessment.primaryRiskDrivers}</p>
              </div>
            )}
            {(data as any).riskAssessment.openClaimExposure && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-800 dark:text-white mb-2">Open Claim Exposure</h4>
                <p className="text-gray-700 dark:text-gray-300">{formatCurrency((data as any).riskAssessment.openClaimExposure)}</p>
              </div>
            )}
            {(data as any).riskAssessment.recommendations && (
              <div>
                <h4 className="font-semibold text-gray-800 dark:text-white mb-2">Recommendations</h4>
                <p className="text-gray-700 dark:text-gray-300">{(data as any).riskAssessment.recommendations}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Trend Analysis (Simplified Loss Run Schema) */}
      {(data as any).trendAnalysis && typeof (data as any).trendAnalysis === 'object' && (
        <section id="trend-analysis-simplified" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Trend Analysis
          </h2>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(data as any).trendAnalysis.yearOverYearChange && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2 flex items-center">
                    <span className="mr-2">📊</span> Year-over-Year Change
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">{(data as any).trendAnalysis.yearOverYearChange}</p>
                </div>
              )}
              {(data as any).trendAnalysis.frequencyTrend && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2 flex items-center">
                    <span className="mr-2">📈</span> Frequency Trend
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">{(data as any).trendAnalysis.frequencyTrend}</p>
                </div>
              )}
              {(data as any).trendAnalysis.severityTrend && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2 flex items-center">
                    <span className="mr-2">⚠️</span> Severity Trend
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">{(data as any).trendAnalysis.severityTrend}</p>
                </div>
              )}
              {(data as any).trendAnalysis.concerningPatterns && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-red-800 dark:text-red-300 mb-2 flex items-center">
                    <span className="mr-2">🚨</span> Concerning Patterns
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">{(data as any).trendAnalysis.concerningPatterns}</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Narrative (Simplified Loss Run Schema) */}
      {(data as any).narrative && typeof (data as any).narrative === 'object' && (
        <section id="narrative" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Analysis Narrative
          </h2>
          <div className="space-y-6">
            {(data as any).narrative.summary && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-5 border-l-4 border-blue-500">
                <h3 className="font-semibold text-lg text-blue-800 dark:text-blue-300 mb-2">Summary</h3>
                <p className="text-gray-700 dark:text-gray-300">{(data as any).narrative.summary}</p>
              </div>
            )}
            {(data as any).narrative.keyFindings && Array.isArray((data as any).narrative.keyFindings) && (data as any).narrative.keyFindings.length > 0 && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-5 border-l-4 border-green-500">
                <h3 className="font-semibold text-lg text-green-800 dark:text-green-300 mb-2">Key Findings</h3>
                <ul className="list-disc list-inside space-y-1">
                  {(data as any).narrative.keyFindings.map((finding: string, index: number) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300">{finding}</li>
                  ))}
                </ul>
              </div>
            )}
            {(data as any).narrative.concerns && Array.isArray((data as any).narrative.concerns) && (data as any).narrative.concerns.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-5 border-l-4 border-red-500">
                <h3 className="font-semibold text-lg text-red-800 dark:text-red-300 mb-2">Concerns</h3>
                <ul className="list-disc list-inside space-y-1">
                  {(data as any).narrative.concerns.map((concern: string, index: number) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300">{concern}</li>
                  ))}
                </ul>
              </div>
            )}
            {(data as any).narrative.positiveIndicators && Array.isArray((data as any).narrative.positiveIndicators) && (data as any).narrative.positiveIndicators.length > 0 && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-5 border-l-4 border-emerald-500">
                <h3 className="font-semibold text-lg text-emerald-800 dark:text-emerald-300 mb-2">Positive Indicators</h3>
                <ul className="list-disc list-inside space-y-1">
                  {(data as any).narrative.positiveIndicators.map((indicator: string, index: number) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300">{indicator}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Top-Level Citations (Loss Run Analyzer) */}
      {!hideCitations && (data as any).citations && Array.isArray((data as any).citations) && (data as any).citations.length > 0 && (
        <section id="citations-list" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Source Citations
          </h2>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-5 border border-blue-200 dark:border-blue-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Click on any citation to view the source document at the referenced location.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(data as any).citations.map((citation: any, idx: number) => {
                const page = citation.page || citation.pageNumber || '?';
                const docName = citation.docName || citation.docId || 'Document';
                return (
                  <button
                    key={citation.id || idx}
                    onClick={() => onCitationClick({
                      type: 'page',
                      page: page,
                      section: citation.section,
                      sourceDocId: citation.docId,
                      documentId: citation.docId,
                      documentName: docName,
                      quote: citation.quote,
                      claimNumber: citation.claimNumber
                    })}
                    className="text-left bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-2">
                      <span className="flex-shrink-0 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-mono px-2 py-1 rounded">
                        {citation.id || `cite_${idx + 1}`}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {docName}{page !== '?' ? ` • Page ${page}` : ''}{citation.section ? ` • ${citation.section}` : ''}
                        </div>
                        {citation.quote && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 truncate group-hover:text-blue-700 dark:group-hover:text-blue-300">
                            "{citation.quote}"
                          </p>
                        )}
                        {citation.claimNumber && (
                          <span className="inline-block mt-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                            Claim: {citation.claimNumber}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Strategic Action Plan - YOY Renewal */}
      {shouldShowSection('strategic-action-plan') && (data as any).strategicActionPlan && (
        <section id="strategic-action-plan" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Strategic Action Plan
          </h2>
          <div className="space-y-6">
            {/* Selected Pathway */}
            {(data as any).strategicActionPlan.selectedPathway && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-500 rounded-r-lg p-4 flex items-center gap-3">
                <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <div className="text-xs font-medium text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">Selected Pathway</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{(data as any).strategicActionPlan.selectedPathway}</div>
                </div>
              </div>
            )}

            {/* Immediate Actions */}
            {(data as any).strategicActionPlan.immediateActions && (data as any).strategicActionPlan.immediateActions.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="text-lg font-semibold mb-3 text-red-800 dark:text-red-300">Immediate Actions</h3>
                <ul className="space-y-2">
                  {(data as any).strategicActionPlan.immediateActions.map((item: any, idx: number) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded">
                      <span className="text-red-500 font-bold">{idx + 1}.</span>
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 dark:text-gray-200">{item.action}</div>
                        {item.owner && <div className="text-sm text-gray-500">Owner: {item.owner}</div>}
                        {item.deadline && <div className="text-sm text-gray-500">Deadline: {item.deadline}</div>}
                        {item.priority && <div className="text-xs bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 px-2 py-0.5 rounded inline-block mt-1">{item.priority}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Short Term Actions */}
            {(data as any).strategicActionPlan.shortTermActions && (data as any).strategicActionPlan.shortTermActions.length > 0 && (
              <div className="bg-orange-50 dark:bg-orange-900/20 p-5 rounded-lg border-l-4 border-orange-500">
                <h3 className="text-lg font-semibold mb-3 text-orange-800 dark:text-orange-300">Short Term Actions</h3>
                <ul className="space-y-2">
                  {(data as any).strategicActionPlan.shortTermActions.map((item: any, idx: number) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded">
                      <span className="text-orange-500 font-bold">{idx + 1}.</span>
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 dark:text-gray-200">{item.action}</div>
                        {item.owner && <div className="text-sm text-gray-500">Owner: {item.owner}</div>}
                        {item.deadline && <div className="text-sm text-gray-500">Deadline: {item.deadline}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Medium Term Actions */}
            {(data as any).strategicActionPlan.mediumTermActions && (data as any).strategicActionPlan.mediumTermActions.length > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-5 rounded-lg border-l-4 border-yellow-500">
                <h3 className="text-lg font-semibold mb-3 text-yellow-800 dark:text-yellow-300">Medium Term Actions</h3>
                <ul className="space-y-2">
                  {(data as any).strategicActionPlan.mediumTermActions.map((item: any, idx: number) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded">
                      <span className="text-yellow-600 font-bold">{idx + 1}.</span>
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 dark:text-gray-200">{item.action}</div>
                        {item.owner && <div className="text-sm text-gray-500">Owner: {item.owner}</div>}
                        {item.deadline && <div className="text-sm text-gray-500">Deadline: {item.deadline}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Long Term Initiatives */}
            {(data as any).strategicActionPlan.longTermInitiatives && (data as any).strategicActionPlan.longTermInitiatives.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border-l-4 border-blue-500">
                <h3 className="text-lg font-semibold mb-3 text-blue-800 dark:text-blue-300">Long Term Initiatives</h3>
                <ul className="space-y-2">
                  {(data as any).strategicActionPlan.longTermInitiatives.map((item: any, idx: number) => (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded">
                      <span className="text-blue-500 font-bold">{idx + 1}.</span>
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 dark:text-gray-200">{item.action}</div>
                        {item.owner && <div className="text-sm text-gray-500">Owner: {item.owner}</div>}
                        {item.deadline && <div className="text-sm text-gray-500">Deadline: {item.deadline}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Success Metrics */}
            {(data as any).strategicActionPlan.successMetrics && (data as any).strategicActionPlan.successMetrics.length > 0 && (
              <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border-l-4 border-green-500">
                <h3 className="text-lg font-semibold mb-3 text-green-800 dark:text-green-300">Success Metrics</h3>
                <ul className="list-disc list-inside space-y-1">
                  {(data as any).strategicActionPlan.successMetrics.map((metric: string, idx: number) => (
                    <li key={idx} className="text-gray-700 dark:text-gray-300">{metric}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Next Review Date */}
            {(data as any).strategicActionPlan.nextReviewDate && (
              <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg text-center">
                <span className="text-gray-600 dark:text-gray-400">Next Review Date: </span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{(data as any).strategicActionPlan.nextReviewDate}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Confidence Statement - YOY Renewal */}
      {shouldShowSection('confidence-statement') && (data as any).confidenceStatement && (
        <section id="confidence-statement" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
            Confidence Statement
          </h2>
          <div className="space-y-4">
            {/* Overall Confidence */}
            {(data as any).confidenceStatement.overallConfidence && (
              <div className="bg-indigo-100 dark:bg-indigo-900/30 p-5 rounded-lg border-l-4 border-indigo-500">
                <h3 className="text-lg font-semibold mb-2 text-indigo-800 dark:text-indigo-300">Overall Confidence</h3>
                <div className="text-xl font-bold text-indigo-900 dark:text-indigo-200">{(data as any).confidenceStatement.overallConfidence}</div>
              </div>
            )}

            {/* Basis for Confidence */}
            {(data as any).confidenceStatement.basisForConfidence && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-blue-800 dark:text-blue-300">Basis for Confidence</h3>
                <div className="text-gray-700 dark:text-gray-300">
                  {renderSafeContent((data as any).confidenceStatement.basisForConfidence, 'Basis for Confidence', hideCitations)}
                </div>
              </div>
            )}

            {/* Specific Limitations */}
            {(data as any).confidenceStatement.specificLimitations && (data as any).confidenceStatement.specificLimitations.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 p-5 rounded-lg border-l-4 border-amber-500">
                <h3 className="text-lg font-semibold mb-3 text-amber-800 dark:text-amber-300">Specific Limitations</h3>
                <ul className="space-y-2">
                  {(data as any).confidenceStatement.specificLimitations.map((limitation: any, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {renderSafeContent(limitation, `Limitation ${idx + 1}`, hideCitations)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Verification Needed */}
            {(data as any).confidenceStatement.verificationNeeded && (data as any).confidenceStatement.verificationNeeded.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="text-lg font-semibold mb-3 text-red-800 dark:text-red-300">Verification Needed</h3>
                <ul className="list-disc list-inside space-y-1">
                  {(data as any).confidenceStatement.verificationNeeded.map((item: string, idx: number) => (
                    <li key={idx} className="text-gray-700 dark:text-gray-300">{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Commercial Lines Executive Renewal Section */}
      {shouldShowSection('commercial-lines-exec-renewal') && data.responseMetadata?.workflowType !== 'client_renewal_presentation' && (data.responseMetadata?.workflowType === 'commercial_lines_exec_renewal' || (data as any).executiveRenewalSummary || (data as any).riskIntelligenceReport) && (
        <div {...getSectionDataAttrs('commercial-lines-exec-renewal')} style={getSectionStyles('commercial-lines-exec-renewal')}>
        <section id="commercial-lines-exec-renewal" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-amber-500 pb-2">
            Commercial Lines Executive Renewal Analysis
          </h2>

          {/* Executive Renewal Summary */}
          {(data as any).executiveRenewalSummary && (
            <div id="executive-renewal-summary" className="mb-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">Executive Renewal Summary</h3>
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-6 rounded-lg">
                <div className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                  {renderSafeContent(
                    (data as any).executiveRenewalSummary?.text || (data as any).executiveRenewalSummary,
                    'Executive Renewal Summary',
                    hideCitations
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Risk Intelligence Report */}
          {(data as any).riskIntelligenceReport && (
            <div id="risk-intelligence-report" className="mb-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">Risk Intelligence Report</h3>

              {/* Entity Overview */}
              {(data as any).riskIntelligenceReport.entityOverview && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-5 mb-6">
                  <h4 className="font-bold text-lg mb-3 text-blue-800 dark:text-blue-300">Entity Overview</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(data as any).riskIntelligenceReport.entityOverview.entityName && (
                      <div>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Entity Name:</span>
                        <div className="text-gray-800 dark:text-gray-200">{(data as any).riskIntelligenceReport.entityOverview.entityName}</div>
                      </div>
                    )}
                    {(data as any).riskIntelligenceReport.entityOverview.industry && (
                      <div>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Industry:</span>
                        <div className="text-gray-800 dark:text-gray-200">{(data as any).riskIntelligenceReport.entityOverview.industry}</div>
                      </div>
                    )}
                    {(data as any).riskIntelligenceReport.entityOverview.employeeCount && (
                      <div>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Employees:</span>
                        <div className="text-gray-800 dark:text-gray-200">{(data as any).riskIntelligenceReport.entityOverview.employeeCount}</div>
                      </div>
                    )}
                    {(data as any).riskIntelligenceReport.entityOverview.operatingJurisdictions && (
                      <div>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Operating Jurisdictions:</span>
                        <div className="text-gray-800 dark:text-gray-200">{(data as any).riskIntelligenceReport.entityOverview.operatingJurisdictions}</div>
                      </div>
                    )}
                  </div>
                  {(data as any).riskIntelligenceReport.entityOverview.businessDescription && (
                    <div className="mt-4">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Business Description:</span>
                      <div className="text-gray-800 dark:text-gray-200 mt-1">{(data as any).riskIntelligenceReport.entityOverview.businessDescription}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Critical Path Priorities */}
              {(data as any).riskIntelligenceReport.criticalPathPriorities && Array.isArray((data as any).riskIntelligenceReport.criticalPathPriorities) && (data as any).riskIntelligenceReport.criticalPathPriorities.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">Critical Path Priorities</h4>
                  <div className="space-y-4">
                    {(data as any).riskIntelligenceReport.criticalPathPriorities.map((item: any, index: number) => (
                      <div key={index} className={`border rounded-lg p-5 ${
                        item.priorityLevel === 'Critical' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' :
                        item.priorityLevel === 'High' ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20' :
                        'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20'
                      }`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="font-bold text-lg text-gray-800 dark:text-gray-200">
                            #{item.priorityRank}: {item.domain}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                            item.priorityLevel === 'Critical' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                            item.priorityLevel === 'High' ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' :
                            'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                          }`}>
                            {item.priorityLevel}
                          </span>
                        </div>
                        {item.whyCritical && (
                          <div className="mb-3">
                            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Why Critical:</h5>
                            <p className="text-gray-800 dark:text-gray-200">{item.whyCritical}</p>
                          </div>
                        )}
                        {item.immediateAction && (
                          <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                            <h5 className="font-semibold text-orange-700 dark:text-orange-400 mb-1">Immediate Action Required:</h5>
                            <p className="text-gray-800 dark:text-gray-200">{item.immediateAction}</p>
                          </div>
                        )}
                        {/* Citations for Critical Path Priority */}
                        {!hideCitations && item.citations && item.citations.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {item.citations.map((citation: any, citIdx: number) => {
                              const resolvedId = resolveDocumentId(citation.docId || 'unknown');
                              return (
                                <button
                                  key={citIdx}
                                  onClick={() => {
                                    if (resolvedId) {
                                      onCitationClick({
                                        id: `priority-${index}-${citIdx}`,
                                        sourceDocId: resolvedId,
                                        section: citation.section,
                                        page: citation.page,
                                        documentName: citation.docName,
                                        clickable: true,
                                        url: '',
                                        type: 'page'
                                      });
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                  title={`${citation.docName} - Page ${citation.page}${citation.section ? ` - ${citation.section}` : ''}${citation.quote ? `\n"${citation.quote}"` : ''}`}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <span>{citation.docName || 'Source'} - p.{citation.page}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comprehensive Risk Assessment */}
              {(data as any).riskIntelligenceReport.comprehensiveRiskAssessment && Array.isArray((data as any).riskIntelligenceReport.comprehensiveRiskAssessment) && (data as any).riskIntelligenceReport.comprehensiveRiskAssessment.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">Comprehensive Risk Assessment</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-700">
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Risk Domain</th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Risk Level</th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Confidence</th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Rationale</th>
                          {!hideCitations && <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Citations</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(data as any).riskIntelligenceReport.comprehensiveRiskAssessment.map((item: any, index: number) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 font-semibold">{item.domain}</td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                              <span className={`px-2 py-1 rounded text-sm font-medium ${
                                item.inherentRiskLevel === 'Critical' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                                item.inherentRiskLevel === 'High' ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' :
                                item.inherentRiskLevel === 'Medium' ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                                'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                              }`}>
                                {item.inherentRiskLevel}
                              </span>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">{item.confidence}</td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm">{item.rationale}</td>
                            {!hideCitations && (
                              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                {item.citations && item.citations.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {item.citations.map((citation: any, citIdx: number) => {
                                      const resolvedId = resolveDocumentId(citation.docId || 'unknown');
                                      return (
                                        <button
                                          key={citIdx}
                                          onClick={() => {
                                            if (resolvedId) {
                                              onCitationClick({
                                                id: `risk-${index}-${citIdx}`,
                                                sourceDocId: resolvedId,
                                                section: citation.section,
                                                page: citation.page,
                                                documentName: citation.docName,
                                                clickable: true,
                                                url: '',
                                                type: 'page'
                                              });
                                            }
                                          }}
                                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                          title={`${citation.docName} - Page ${citation.page}${citation.section ? ` - ${citation.section}` : ''}${citation.quote ? `\n"${citation.quote}"` : ''}`}
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                          <span>{citation.docName || 'Source'} - p.{citation.page}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Coverage Gaps Identified */}
              {(data as any).riskIntelligenceReport.coverageApplicabilityAnalysis?.coverageGapsIdentified && Array.isArray((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.coverageGapsIdentified) && (data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.coverageGapsIdentified.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-bold text-lg mb-3 text-red-700 dark:text-red-400">Coverage Gaps Identified</h4>
                  <div className="space-y-4">
                    {(data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.coverageGapsIdentified.map((gap: any, index: number) => (
                      <div key={index} className="border border-red-300 bg-red-50 dark:bg-red-900/20 rounded-lg p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="font-bold text-lg text-gray-800 dark:text-gray-200">{gap.riskDomain}</div>
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200">
                            {gap.gapType}
                          </span>
                        </div>
                        {gap.whyCritical && (
                          <div className="mb-3">
                            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Why Critical:</h5>
                            <p className="text-gray-800 dark:text-gray-200">{gap.whyCritical}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 pt-3 border-t border-red-200 dark:border-red-700">
                          {gap.estimatedExposure && (
                            <div>
                              <span className="font-semibold text-gray-700 dark:text-gray-300">Estimated Exposure:</span>
                              <div className="text-red-700 dark:text-red-300 font-bold">{gap.estimatedExposure}</div>
                            </div>
                          )}
                          {gap.estimatedInvestment && (
                            <div>
                              <span className="font-semibold text-gray-700 dark:text-gray-300">Estimated Investment:</span>
                              <div className="text-gray-800 dark:text-gray-200">{gap.estimatedInvestment}</div>
                            </div>
                          )}
                        </div>
                        {gap.recommendedApproach && (
                          <div className="mt-3">
                            <h5 className="font-semibold text-green-700 dark:text-green-400 mb-1">Recommended Approach:</h5>
                            <p className="text-gray-800 dark:text-gray-200">{gap.recommendedApproach}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risks Adequately Addressed */}
              {(data as any).riskIntelligenceReport.coverageApplicabilityAnalysis?.risksAdequatelyAddressed && Array.isArray((data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksAdequatelyAddressed) && (data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksAdequatelyAddressed.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-bold text-lg mb-3 text-green-700 dark:text-green-400">Risks Adequately Addressed</h4>
                  <div className="space-y-3">
                    {(data as any).riskIntelligenceReport.coverageApplicabilityAnalysis.risksAdequatelyAddressed.map((item: any, index: number) => (
                      <div key={index} className="border border-green-300 bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="font-bold text-gray-800 dark:text-gray-200">{item.riskDomain}</div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Confidence: {item.confidence}</span>
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-semibold">Covered By:</span> {item.coveredBy}
                        </div>
                        {item.assessment && (
                          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">{item.assessment}</div>
                        )}
                        {/* Citations */}
                        {!hideCitations && item.citations && item.citations.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700 flex flex-wrap gap-1">
                            {item.citations.map((citation: any, citIdx: number) => {
                              const resolvedId = resolveDocumentId(citation.docId || 'unknown');
                              return (
                                <button
                                  key={citIdx}
                                  onClick={() => {
                                    if (resolvedId) {
                                      onCitationClick({
                                        id: `adequate-${index}-${citIdx}`,
                                        sourceDocId: resolvedId,
                                        section: citation.section,
                                        page: citation.page,
                                        documentName: citation.docName,
                                        clickable: true,
                                        url: '',
                                        type: 'page'
                                      });
                                    }
                                  }}
                                  className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                  title={`${citation.docName} - Page ${citation.page}`}
                                >
                                  {citation.docName || 'Source'} - p.{citation.page}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Mitigation Framework */}
              {(data as any).riskIntelligenceReport.riskMitigationFramework && Array.isArray((data as any).riskIntelligenceReport.riskMitigationFramework) && (data as any).riskIntelligenceReport.riskMitigationFramework.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">Risk Mitigation Framework</h4>
                  <div className="space-y-4">
                    {(data as any).riskIntelligenceReport.riskMitigationFramework.map((item: any, index: number) => (
                      <div key={index} className="border border-indigo-200 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="font-bold text-lg text-indigo-800 dark:text-indigo-300">{item.topic}</div>
                          {item.priority && (
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              item.priority === 'Critical' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                              item.priority === 'High' ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' :
                              'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                            }`}>
                              {item.priority}
                            </span>
                          )}
                        </div>
                        {item.talkingPoints && (
                          <div className="mb-3">
                            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Talking Points:</h5>
                            <p className="text-gray-800 dark:text-gray-200">{item.talkingPoints}</p>
                          </div>
                        )}
                        {item.recommendedActions && (
                          <div>
                            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Recommended Actions:</h5>
                            <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{item.recommendedActions}</p>
                          </div>
                        )}
                        {/* Citations for Risk Mitigation Framework */}
                        {!hideCitations && item.citations && item.citations.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-indigo-200 dark:border-indigo-700 flex flex-wrap gap-1">
                            {item.citations.map((citation: any, citIdx: number) => {
                              const resolvedId = resolveDocumentId(citation.docId || 'unknown');
                              return (
                                <button
                                  key={citIdx}
                                  onClick={() => {
                                    if (resolvedId) {
                                      onCitationClick({
                                        id: `mitigation-${index}-${citIdx}`,
                                        sourceDocId: resolvedId,
                                        section: citation.section,
                                        page: citation.page,
                                        documentName: citation.docName,
                                        clickable: true,
                                        url: '',
                                        type: 'page'
                                      });
                                    }
                                  }}
                                  className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                  title={`${citation.docName} - Page ${citation.page}`}
                                >
                                  {citation.docName || 'Source'} - p.{citation.page}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Correlation Map */}
              {(data as any).riskIntelligenceReport.riskCorrelationMap && Array.isArray((data as any).riskIntelligenceReport.riskCorrelationMap) && (data as any).riskIntelligenceReport.riskCorrelationMap.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">Risk Correlation Analysis</h4>
                  <div className="space-y-4">
                    {(data as any).riskIntelligenceReport.riskCorrelationMap.map((item: any, index: number) => (
                      <div key={index} className="border border-purple-200 bg-purple-50 dark:bg-purple-900/20 rounded-lg p-5">
                        <div className="font-bold text-lg text-purple-800 dark:text-purple-300 mb-3">Scenario: {item.scenario}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Primary Risk:</span>
                            <div className="text-gray-800 dark:text-gray-200">{item.primaryRisk}</div>
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Connected Risks:</span>
                            <div className="text-gray-800 dark:text-gray-200">{item.connectedRisks}</div>
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Cumulative Impact:</span>
                            <div className="text-gray-800 dark:text-gray-200">{item.cumulativeImpact}</div>
                          </div>
                        </div>
                        {item.cascade && (
                          <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Cascade Effect:</span>
                            <p className="text-gray-800 dark:text-gray-200 mt-1">{item.cascade}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Research Methodology */}
              {(data as any).riskIntelligenceReport.researchMethodology && (() => {
                const rm = (data as any).riskIntelligenceReport.researchMethodology;
                // Only render if there's actual content
                const hasContent = rm.overallConfidence ||
                  (rm.insuranceDocumentQuality && Object.keys(rm.insuranceDocumentQuality).length > 0) ||
                  (rm.webResearchQuality && Object.keys(rm.webResearchQuality).length > 0) ||
                  (rm.limitations && rm.limitations.length > 0) ||
                  (rm.recommendationsForImprovement && rm.recommendationsForImprovement.length > 0);
                if (!hasContent) return null;
                return (
                <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-5 mb-6">
                  <h4 className="font-bold text-lg mb-3 text-gray-800 dark:text-gray-300">Research Methodology & Limitations</h4>

                  {/* Overall Confidence */}
                  {rm.overallConfidence && (
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Overall Confidence: </span>
                      <span className="text-gray-800 dark:text-gray-200">{rm.overallConfidence}</span>
                    </div>
                  )}

                  {/* Insurance Document Quality */}
                  {rm.insuranceDocumentQuality && typeof rm.insuranceDocumentQuality === 'object' && (
                    <div className="mb-4">
                      <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Insurance Document Quality</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {rm.insuranceDocumentQuality.expiringPolicyCompleteness && (
                          <div className="p-2 bg-white dark:bg-gray-700 rounded">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Expiring Policy: </span>
                            <span className="text-gray-800 dark:text-gray-200">{rm.insuranceDocumentQuality.expiringPolicyCompleteness}</span>
                          </div>
                        )}
                        {rm.insuranceDocumentQuality.renewalDocumentCompleteness && (
                          <div className="p-2 bg-white dark:bg-gray-700 rounded">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Renewal Document: </span>
                            <span className="text-gray-800 dark:text-gray-200">{rm.insuranceDocumentQuality.renewalDocumentCompleteness}</span>
                          </div>
                        )}
                        {rm.insuranceDocumentQuality.overallDocumentConfidence && (
                          <div className="p-2 bg-white dark:bg-gray-700 rounded col-span-2">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Document Confidence: </span>
                            <span className="text-gray-800 dark:text-gray-200">{rm.insuranceDocumentQuality.overallDocumentConfidence}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Web Research Quality */}
                  {rm.webResearchQuality && typeof rm.webResearchQuality === 'object' && (
                    <div className="mb-4">
                      <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Web Research Quality</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {rm.webResearchQuality.overallWebConfidence && (
                          <div className="p-2 bg-white dark:bg-gray-700 rounded">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Web Confidence: </span>
                            <span className="text-gray-800 dark:text-gray-200">{rm.webResearchQuality.overallWebConfidence}</span>
                          </div>
                        )}
                        {rm.webResearchQuality.websiteSophistication && (
                          <div className="p-2 bg-white dark:bg-gray-700 rounded">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Website Sophistication: </span>
                            <span className="text-gray-800 dark:text-gray-200">{rm.webResearchQuality.websiteSophistication}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Limitations */}
                  {rm.limitations && Array.isArray(rm.limitations) && rm.limitations.length > 0 && (
                    <div className="mb-4">
                      <h5 className="font-semibold text-orange-700 dark:text-orange-400 mb-2">Analysis Limitations</h5>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {rm.limitations.map((limitation: string, idx: number) => (
                          <li key={idx}>{limitation}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendations for Improvement */}
                  {rm.recommendationsForImprovement && Array.isArray(rm.recommendationsForImprovement) && rm.recommendationsForImprovement.length > 0 && (
                    <div className="mb-4">
                      <h5 className="font-semibold text-green-700 dark:text-green-400 mb-2">Recommendations for Improvement</h5>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {rm.recommendationsForImprovement.map((rec: string, idx: number) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                );
              })()}
            </div>
          )}

          {/* Confidence and Limitations - handles both confidenceAndLimitations and analysisConfidence */}
          {(() => {
            const confData = (data as any).confidenceAndLimitations || (data as any).analysisConfidence;
            if (!confData) return null;

            // Handle simple string format
            if (typeof confData === 'string') {
              return (
                <div id="confidence-and-limitations" className="mb-6">
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-semibold text-gray-800 dark:text-white">Analysis Confidence:</span>
                      <span className="text-2xl font-bold text-amber-700 dark:text-amber-300">{confData}</span>
                    </div>
                  </div>
                </div>
              );
            }

            // Handle full object format
            return (
              <div id="confidence-and-limitations" className="mb-6">
                <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">Analysis Confidence & Limitations</h3>
                <div className="space-y-4">
                  {/* Overall Confidence */}
                  {confData.overallConfidence && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                      <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Overall Confidence</div>
                      <div className="text-gray-800 dark:text-gray-200">{confData.overallConfidence}</div>
                    </div>
                  )}

                  {/* Insurance Document Quality */}
                  {confData.insuranceDocumentQuality && typeof confData.insuranceDocumentQuality === 'object' && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                      <div className="font-semibold text-blue-700 dark:text-blue-300 mb-2">Insurance Document Quality</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {confData.insuranceDocumentQuality.expiringPolicyCompleteness && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Expiring Policy: </span>
                            <span className="text-gray-800 dark:text-gray-200">{confData.insuranceDocumentQuality.expiringPolicyCompleteness}</span>
                          </div>
                        )}
                        {confData.insuranceDocumentQuality.renewalDocumentCompleteness && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Renewal Document: </span>
                            <span className="text-gray-800 dark:text-gray-200">{confData.insuranceDocumentQuality.renewalDocumentCompleteness}</span>
                          </div>
                        )}
                        {confData.insuranceDocumentQuality.overallDocumentConfidence && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded col-span-full">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Document Confidence: </span>
                            <span className="text-gray-800 dark:text-gray-200">{confData.insuranceDocumentQuality.overallDocumentConfidence}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Web Research Quality */}
                  {confData.webResearchQuality && typeof confData.webResearchQuality === 'object' && (
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                      <div className="font-semibold text-purple-700 dark:text-purple-300 mb-2">Web Research Quality</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {confData.webResearchQuality.overallWebConfidence && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Web Confidence: </span>
                            <span className="text-gray-800 dark:text-gray-200">{confData.webResearchQuality.overallWebConfidence}</span>
                          </div>
                        )}
                        {confData.webResearchQuality.websiteSophistication && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Website Sophistication: </span>
                            <span className="text-gray-800 dark:text-gray-200">{confData.webResearchQuality.websiteSophistication}</span>
                          </div>
                        )}
                        {(confData.webResearchQuality.tier1Sources || confData.webResearchQuality.tier2Sources || confData.webResearchQuality.tier3Sources) && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded col-span-full">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Sources: </span>
                            <span className="text-gray-800 dark:text-gray-200">
                              Tier 1: {confData.webResearchQuality.tier1Sources || '0'},
                              Tier 2: {confData.webResearchQuality.tier2Sources || '0'},
                              Tier 3: {confData.webResearchQuality.tier3Sources || '0'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Limitations */}
                  {confData.limitations && Array.isArray(confData.limitations) && confData.limitations.length > 0 && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                      <div className="font-semibold text-orange-700 dark:text-orange-300 mb-2">Limitations</div>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                        {confData.limitations.map((limitation: string, idx: number) => (
                          <li key={idx}>{limitation}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendations for Improvement */}
                  {confData.recommendationsForImprovement && Array.isArray(confData.recommendationsForImprovement) && confData.recommendationsForImprovement.length > 0 && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                      <div className="font-semibold text-green-700 dark:text-green-300 mb-2">Recommendations for Improvement</div>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                        {confData.recommendationsForImprovement.map((rec: string, idx: number) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

        </section>
        </div>
      )}

      {/* ===== CLIENT RENEWAL PRESENTATION SECTIONS ===== */}
      {(data.responseMetadata?.workflowType === 'client_renewal_presentation' || ((data as any).coverageHighlights && (data as any).restrictiveEndorsements)) && (
        <>
          {/* Company Context */}
          {(data as any).companyContext && (
            <section id="company-context" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-blue-500 pb-2">
                Company Context
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data as any).companyContext.entityName && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                    <div className="text-sm text-blue-600 dark:text-blue-400">Entity Name</div>
                    <div className="text-lg font-bold text-blue-800 dark:text-blue-200">{(data as any).companyContext.entityName}</div>
                  </div>
                )}
                {(data as any).companyContext.industrySector && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Industry Sector</div>
                    <div className="font-semibold text-gray-800 dark:text-gray-200">{(data as any).companyContext.industrySector}</div>
                  </div>
                )}
              </div>
              {(data as any).companyContext.primaryBusiness && (
                <div className="mt-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Primary Business</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{(data as any).companyContext.primaryBusiness}</p>
                </div>
              )}
              {(data as any).companyContext.keyOperations && (
                <div className="mt-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Key Operations</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{(data as any).companyContext.keyOperations}</p>
                </div>
              )}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data as any).companyContext.clientBase && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Client Base</div>
                    <div className="text-gray-800 dark:text-gray-200">{(data as any).companyContext.clientBase}</div>
                  </div>
                )}
                {(data as any).companyContext.geographicFootprint && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Geographic Footprint</div>
                    <div className="text-gray-800 dark:text-gray-200">{(data as any).companyContext.geographicFootprint}</div>
                  </div>
                )}
                {(data as any).companyContext.scaleIndicators && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Scale Indicators</div>
                    <div className="text-gray-800 dark:text-gray-200">{(data as any).companyContext.scaleIndicators}</div>
                  </div>
                )}
                {(data as any).companyContext.riskManagementSophistication && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Risk Management Sophistication</div>
                    <div className="text-gray-800 dark:text-gray-200">{(data as any).companyContext.riskManagementSophistication}</div>
                  </div>
                )}
              </div>
              {(data as any).companyContext.likelyAudience && (
                <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-700">
                  <h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">Likely Audience</h4>
                  <p className="text-amber-700 dark:text-amber-400 text-sm">{(data as any).companyContext.likelyAudience}</p>
                </div>
              )}
            </section>
          )}

          {/* Document Inventory */}
          {(data as any).documentInventory && Array.isArray((data as any).documentInventory) && (data as any).documentInventory.length > 0 && (
            <section id="document-inventory" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-400 pb-2">
                Document Inventory
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Document Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Policy Line</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Carrier</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Period</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Completeness</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(data as any).documentInventory.map((doc: any, idx: number) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700/50'}>
                        <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">{doc.documentType || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{doc.policyLine || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{doc.carrier || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{doc.policyPeriod || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            doc.completenessLevel?.toLowerCase().includes('complete') ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
                            doc.completenessLevel?.toLowerCase().includes('partial') ? 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-100' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-100'
                          }`}>
                            {doc.completenessLevel || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Executive Summary for Client Renewal Presentation */}
          {data.executiveSummary && (
            <section id="executive-summary" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-emerald-500 pb-2">
                Executive Summary
              </h2>
              {(data.executiveSummary as any).text && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-6 rounded-lg border border-emerald-200 dark:border-emerald-700 mb-4">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{(data.executiveSummary as any).text}</p>
                </div>
              )}
              {(data.executiveSummary as any).keyHighlights && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                  <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Key Highlights</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{(data.executiveSummary as any).keyHighlights}</p>
                </div>
              )}
              {(data.executiveSummary as any).overallAssessment && (
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Overall Assessment</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{(data.executiveSummary as any).overallAssessment}</p>
                </div>
              )}
              {(data.executiveSummary as any).criticalAttentionItems && (
                <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-700 mb-4">
                  <h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">Critical Attention Items</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{(data.executiveSummary as any).criticalAttentionItems}</p>
                </div>
              )}
              {/* Citations */}
              {(data.executiveSummary as any).citations && Array.isArray((data.executiveSummary as any).citations) && (data.executiveSummary as any).citations.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap gap-1">
                    {(data.executiveSummary as any).citations.map((citation: any, idx: number) => (
                      <button
                        key={idx}
                        className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        onClick={() => {
                          const resolvedDocId = resolveDocumentId(citation.docId || 'unknown');
                          if (resolvedDocId) {
                            onCitationClick({
                              id: `exec-summary-${idx}`,
                              sourceDocId: resolvedDocId,
                              section: citation.section,
                              page: citation.page,
                              documentName: citation.docName,
                              clickable: true,
                              url: '',
                              type: 'page'
                            });
                          }
                        }}
                        title={`Click to view: ${citation.docName} - Page ${citation.page}`}
                      >
                        {citation.docName || citation.docId} - p.{citation.page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Program Overview */}
          {(data as any).programOverview && (
            <section id="program-overview" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-500 pb-2">
                Program Overview
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {(data as any).programOverview.totalPolicies && (
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-700 text-center">
                    <div className="text-sm text-indigo-600 dark:text-indigo-400">Total Policies</div>
                    <div className="text-2xl font-bold text-indigo-800 dark:text-indigo-200">{(data as any).programOverview.totalPolicies}</div>
                  </div>
                )}
                {(data as any).programOverview.totalPremium && (
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700 text-center">
                    <div className="text-sm text-green-600 dark:text-green-400">Total Premium</div>
                    <div className="text-2xl font-bold text-green-800 dark:text-green-200">{(data as any).programOverview.totalPremium}</div>
                  </div>
                )}
                {(data as any).programOverview.expiringPremium && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Expiring Premium</div>
                    <div className="text-xl font-bold text-gray-800 dark:text-gray-200">{(data as any).programOverview.expiringPremium}</div>
                  </div>
                )}
                {(data as any).programOverview.renewalPremium && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700 text-center">
                    <div className="text-sm text-blue-600 dark:text-blue-400">Renewal Premium</div>
                    <div className="text-xl font-bold text-blue-800 dark:text-blue-200">{(data as any).programOverview.renewalPremium}</div>
                  </div>
                )}
              </div>
              {((data as any).programOverview.premiumChange || (data as any).programOverview.premiumChangePercent) && (
                <div className={`p-4 rounded-lg border mb-4 ${
                  (data as any).programOverview.premiumChange?.includes('-') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' :
                  'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                }`}>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Premium Change</div>
                  <div className="text-xl font-bold">
                    {(data as any).programOverview.premiumChange} {(data as any).programOverview.premiumChangePercent && `(${(data as any).programOverview.premiumChangePercent})`}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data as any).programOverview.carriersInvolved && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Carriers Involved</div>
                    <div className="text-gray-800 dark:text-gray-200">{(data as any).programOverview.carriersInvolved}</div>
                  </div>
                )}
                {(data as any).programOverview.programComplexity && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Program Complexity</div>
                    <div className="text-gray-800 dark:text-gray-200">{(data as any).programOverview.programComplexity}</div>
                  </div>
                )}
              </div>
              {(data as any).programOverview.overallAssessment && (
                <div className="mt-4 bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-700">
                  <h4 className="font-semibold text-indigo-800 dark:text-indigo-300 mb-2">Overall Assessment</h4>
                  <p className="text-gray-700 dark:text-gray-300">{(data as any).programOverview.overallAssessment}</p>
                </div>
              )}
              {/* Citations */}
              {(data as any).programOverview.citations && Array.isArray((data as any).programOverview.citations) && (data as any).programOverview.citations.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap gap-1">
                    {(data as any).programOverview.citations.map((citation: any, idx: number) => (
                      <button
                        key={idx}
                        className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        onClick={() => {
                          const resolvedDocId = resolveDocumentId(citation.docId || 'unknown');
                          if (resolvedDocId) {
                            onCitationClick({
                              id: `program-overview-${idx}`,
                              sourceDocId: resolvedDocId,
                              section: citation.section,
                              page: citation.page,
                              documentName: citation.docName,
                              clickable: true,
                              url: '',
                              type: 'page'
                            });
                          }
                        }}
                        title={`Click to view: ${citation.docName} - Page ${citation.page}`}
                      >
                        {citation.docName || citation.docId} - p.{citation.page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Coverage Highlights */}
          {(data as any).coverageHighlights && Array.isArray((data as any).coverageHighlights) && (data as any).coverageHighlights.length > 0 && (
            <section id="coverage-highlights" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-violet-500 pb-2">
                Coverage Highlights
              </h2>
              <div className="space-y-4">
                {(data as any).coverageHighlights.map((coverage: any, idx: number) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className={`px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between ${
                      coverage.changeAssessment === 'IMPROVEMENT' ? 'bg-green-50 dark:bg-green-900/20' :
                      coverage.changeAssessment === 'REDUCTION' ? 'bg-red-50 dark:bg-red-900/20' :
                      'bg-gray-50 dark:bg-gray-700/50'
                    }`}>
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{coverage.coverageName}</h3>
                      {coverage.changeAssessment && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          coverage.changeAssessment === 'IMPROVEMENT' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
                          coverage.changeAssessment === 'REDUCTION' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-100'
                        }`}>
                          {coverage.changeAssessment}
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {coverage.carrier && (
                          <div><span className="text-gray-500 dark:text-gray-400 text-sm">Carrier:</span> <span className="font-medium text-gray-900 dark:text-white">{coverage.carrier}</span></div>
                        )}
                        {coverage.limits && (
                          <div><span className="text-gray-500 dark:text-gray-400 text-sm">Limits:</span> <span className="font-medium text-gray-900 dark:text-white">{coverage.limits}</span></div>
                        )}
                        {coverage.deductible && (
                          <div><span className="text-gray-500 dark:text-gray-400 text-sm">Deductible:</span> <span className="font-medium text-gray-900 dark:text-white">{coverage.deductible}</span></div>
                        )}
                        {coverage.premium && (
                          <div><span className="text-gray-500 dark:text-gray-400 text-sm">Premium:</span> <span className="font-medium text-gray-900 dark:text-white">{coverage.premium}</span></div>
                        )}
                        {coverage.policyForm && (
                          <div><span className="text-gray-500 dark:text-gray-400 text-sm">Form:</span> <span className="font-medium text-gray-900 dark:text-white">{coverage.policyForm}</span></div>
                        )}
                      </div>
                      {coverage.whatItProtects && (
                        <div className="mb-3">
                          <div className="text-sm font-medium text-violet-600 dark:text-violet-400 mb-1">What It Protects</div>
                          <p className="text-gray-700 dark:text-gray-300 text-sm">{coverage.whatItProtects}</p>
                        </div>
                      )}
                      {coverage.businessRelevance && (
                        <div className="mb-3 bg-violet-50 dark:bg-violet-900/20 rounded p-3">
                          <div className="text-sm font-medium text-violet-600 dark:text-violet-400 mb-1">Business Relevance</div>
                          <p className="text-gray-700 dark:text-gray-300 text-sm">{coverage.businessRelevance}</p>
                        </div>
                      )}
                      {coverage.changeFromExpiring && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Change from Expiring</div>
                          <p className="text-gray-700 dark:text-gray-300 text-sm">{coverage.changeFromExpiring}</p>
                        </div>
                      )}
                      {/* Source Document (simple format) */}
                      {coverage.sourceDocument && coverage.sourcePage && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-1">
                          <button
                            className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                            onClick={() => {
                              // Try to resolve document by name
                              const docName = coverage.sourceDocument;
                              const matchingDoc = documents?.find((d: any) =>
                                d.name === docName || d.originalName === docName || d.fileName === docName
                              );
                              if (matchingDoc) {
                                onCitationClick({
                                  id: `coverage-${idx}-source`,
                                  sourceDocId: matchingDoc.id,
                                  page: coverage.sourcePage,
                                  documentName: docName,
                                  clickable: true,
                                  url: '',
                                  type: 'page'
                                });
                              }
                            }}
                            title={`Click to view: ${coverage.sourceDocument} - Page ${coverage.sourcePage}`}
                          >
                            {coverage.sourceDocument} - p.{coverage.sourcePage}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Restrictive Endorsements */}
          {(data as any).restrictiveEndorsements && (
            <section id="restrictive-endorsements" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-red-500 pb-2">
                Restrictive Endorsements
              </h2>

              {/* Subcontractor Warning */}
              {(data as any).restrictiveEndorsements.subcontractorWarningPresent && (
                <div className="mb-6 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r">
                  <div className="flex items-start">
                    <span className="text-red-500 text-xl mr-3">⚠️</span>
                    <div>
                      <h4 className="font-semibold text-red-800 dark:text-red-300 mb-1">Subcontractor Warning</h4>
                      <p className="text-red-700 dark:text-red-400 text-sm">{(data as any).restrictiveEndorsements.subcontractorWarningDetails}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Critical Restrictions */}
              {(data as any).restrictiveEndorsements.criticalRestrictions && Array.isArray((data as any).restrictiveEndorsements.criticalRestrictions) && (data as any).restrictiveEndorsements.criticalRestrictions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-3">Critical Restrictions</h3>
                  <div className="space-y-3">
                    {(data as any).restrictiveEndorsements.criticalRestrictions.map((restriction: any, idx: number) => (
                      <div key={idx} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-red-800 dark:text-red-300">{restriction.restrictionName}</h4>
                          {restriction.priority && (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              restriction.priority === 'CRITICAL' ? 'bg-red-600 text-white' :
                              restriction.priority === 'HIGH' ? 'bg-red-500 text-white' :
                              'bg-red-200 text-red-800'
                            }`}>
                              {restriction.priority}
                            </span>
                          )}
                        </div>
                        {restriction.formNumber && <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Form: {restriction.formNumber}</div>}
                        <div className="mb-2">
                          <div className="text-sm font-medium text-red-700 dark:text-red-400">What Is NOT Covered:</div>
                          <p className="text-gray-700 dark:text-gray-300 text-sm">{restriction.whatIsNotCovered}</p>
                        </div>
                        {restriction.businessImpact && (
                          <div>
                            <div className="text-sm font-medium text-red-700 dark:text-red-400">Business Impact:</div>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{restriction.businessImpact}</p>
                          </div>
                        )}
                        {/* Source Document (simple format) */}
                        {restriction.sourceDocument && restriction.sourcePage && (
                          <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-700 flex flex-wrap gap-1">
                            <button
                              className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                              onClick={() => {
                                const docName = restriction.sourceDocument;
                                const matchingDoc = documents?.find((d: any) =>
                                  d.name === docName || d.originalName === docName || d.fileName === docName
                                );
                                if (matchingDoc) {
                                  onCitationClick({
                                    id: `restriction-${idx}-source`,
                                    sourceDocId: matchingDoc.id,
                                    page: restriction.sourcePage,
                                    documentName: docName,
                                    clickable: true,
                                    url: '',
                                    type: 'page'
                                  });
                                }
                              }}
                              title={`Click to view: ${restriction.sourceDocument} - Page ${restriction.sourcePage}`}
                            >
                              {restriction.sourceDocument} - p.{restriction.sourcePage}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Important Restrictions */}
              {(data as any).restrictiveEndorsements.importantRestrictions && Array.isArray((data as any).restrictiveEndorsements.importantRestrictions) && (data as any).restrictiveEndorsements.importantRestrictions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-amber-700 dark:text-amber-400 mb-3">Important Restrictions</h3>
                  <div className="space-y-3">
                    {(data as any).restrictiveEndorsements.importantRestrictions.map((restriction: any, idx: number) => (
                      <div key={idx} className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">{restriction.restrictionName}</h4>
                        {restriction.formNumber && <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Form: {restriction.formNumber}</div>}
                        <div className="mb-2">
                          <div className="text-sm font-medium text-amber-700 dark:text-amber-400">What Is NOT Covered:</div>
                          <p className="text-gray-700 dark:text-gray-300 text-sm">{restriction.whatIsNotCovered}</p>
                        </div>
                        {restriction.businessImpact && (
                          <div>
                            <div className="text-sm font-medium text-amber-700 dark:text-amber-400">Business Impact:</div>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{restriction.businessImpact}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Positive Endorsements */}
              {(data as any).restrictiveEndorsements.positiveEndorsements && Array.isArray((data as any).restrictiveEndorsements.positiveEndorsements) && (data as any).restrictiveEndorsements.positiveEndorsements.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-green-700 dark:text-green-400 mb-3">Positive Endorsements</h3>
                  <div className="space-y-3">
                    {(data as any).restrictiveEndorsements.positiveEndorsements.map((endorsement: any, idx: number) => (
                      <div key={idx} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 dark:text-green-300 mb-2">{endorsement.endorsementName}</h4>
                        {endorsement.formNumber && <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Form: {endorsement.formNumber}</div>}
                        <div className="mb-2">
                          <div className="text-sm font-medium text-green-700 dark:text-green-400">What It Adds:</div>
                          <p className="text-gray-700 dark:text-gray-300 text-sm">{endorsement.whatItAdds}</p>
                        </div>
                        {endorsement.businessBenefit && (
                          <div>
                            <div className="text-sm font-medium text-green-700 dark:text-green-400">Business Benefit:</div>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{endorsement.businessBenefit}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Premium Analysis */}
          {(data as any).premiumAnalysis && (
            <section id="premium-analysis" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-green-500 pb-2">
                Premium Analysis
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {(data as any).premiumAnalysis.totalExpiringPremium && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Expiring Premium</div>
                    <div className="text-xl font-bold text-gray-800 dark:text-gray-200">{(data as any).premiumAnalysis.totalExpiringPremium}</div>
                  </div>
                )}
                {(data as any).premiumAnalysis.totalRenewalPremium && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700 text-center">
                    <div className="text-sm text-blue-600 dark:text-blue-400">Renewal Premium</div>
                    <div className="text-xl font-bold text-blue-800 dark:text-blue-200">{(data as any).premiumAnalysis.totalRenewalPremium}</div>
                  </div>
                )}
                {((data as any).premiumAnalysis.totalChange || (data as any).premiumAnalysis.totalChangePercent) && (
                  <div className={`p-4 rounded-lg border text-center ${
                    (data as any).premiumAnalysis.totalChange?.includes('-') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' :
                    'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                  }`}>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Change</div>
                    <div className="text-xl font-bold">
                      {(data as any).premiumAnalysis.totalChange} {(data as any).premiumAnalysis.totalChangePercent && `(${(data as any).premiumAnalysis.totalChangePercent})`}
                    </div>
                  </div>
                )}
              </div>
              {(data as any).premiumAnalysis.primaryDrivers && (
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Primary Drivers</div>
                  <p className="text-gray-800 dark:text-gray-200">{(data as any).premiumAnalysis.primaryDrivers}</p>
                </div>
              )}
              {(data as any).premiumAnalysis.marketContext && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                  <div className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">Market Context</div>
                  <p className="text-gray-700 dark:text-gray-300">{(data as any).premiumAnalysis.marketContext}</p>
                </div>
              )}
              {(data as any).premiumAnalysis.valueAssessment && (
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700 mb-4">
                  <div className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">Value Assessment</div>
                  <p className="text-gray-700 dark:text-gray-300">{(data as any).premiumAnalysis.valueAssessment}</p>
                </div>
              )}
              {(data as any).premiumAnalysis.policyBreakdown && Array.isArray((data as any).premiumAnalysis.policyBreakdown) && (data as any).premiumAnalysis.policyBreakdown.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Policy Breakdown</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Coverage</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expiring</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Renewal</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Change</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Driver</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {(data as any).premiumAnalysis.policyBreakdown.map((pb: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-200">{pb.coverageName || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{pb.expiringPremium || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{pb.renewalPremium || '-'}</td>
                            <td className={`px-4 py-3 text-sm font-medium ${
                              pb.changePercent?.includes('-') ? 'text-green-600 dark:text-green-400' :
                              pb.changePercent?.includes('+') ? 'text-red-600 dark:text-red-400' :
                              'text-gray-600 dark:text-gray-400'
                            }`}>{pb.changePercent || pb.change || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{pb.driver || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Citations */}
              {(data as any).premiumAnalysis.citations && Array.isArray((data as any).premiumAnalysis.citations) && (data as any).premiumAnalysis.citations.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap gap-1">
                    {(data as any).premiumAnalysis.citations.map((citation: any, idx: number) => (
                      <button
                        key={idx}
                        className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        onClick={() => {
                          const resolvedDocId = resolveDocumentId(citation.docId || 'unknown');
                          if (resolvedDocId) {
                            onCitationClick({
                              id: `premium-analysis-${idx}`,
                              sourceDocId: resolvedDocId,
                              section: citation.section,
                              page: citation.page,
                              documentName: citation.docName,
                              clickable: true,
                              url: '',
                              type: 'page'
                            });
                          }
                        }}
                        title={`Click to view: ${citation.docName} - Page ${citation.page}`}
                      >
                        {citation.docName || citation.docId} - p.{citation.page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Coverage Changes */}
          {(data as any).coverageChanges && Array.isArray((data as any).coverageChanges) && (data as any).coverageChanges.length > 0 && (
            <section id="coverage-changes" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-orange-500 pb-2">
                Coverage Changes
              </h2>
              <div className="space-y-4">
                {(data as any).coverageChanges.map((change: any, idx: number) => (
                  <div key={idx} className={`rounded-lg border p-4 ${
                    change.changeType === 'IMPROVEMENT' || change.assessment === 'POSITIVE' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' :
                    change.changeType === 'REDUCTION' || change.assessment === 'NEGATIVE' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' :
                    'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{change.coverageName}</h4>
                      <div className="flex gap-2">
                        {change.changeType && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            change.changeType === 'IMPROVEMENT' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
                            change.changeType === 'REDUCTION' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-100'
                          }`}>
                            {change.changeType}
                          </span>
                        )}
                        {change.materialityLevel && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                            {change.materialityLevel}
                          </span>
                        )}
                      </div>
                    </div>
                    {change.changeDescription && (
                      <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{change.changeDescription}</p>
                    )}
                    {(change.expiringValue || change.renewalValue) && (
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <span className="text-gray-500 dark:text-gray-400">Change:</span>
                        <span className="text-gray-700 dark:text-gray-300">{change.expiringValue || 'N/A'}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-medium text-gray-900 dark:text-white">{change.renewalValue || 'N/A'}</span>
                      </div>
                    )}
                    {change.clientImpact && (
                      <div className="bg-white dark:bg-gray-700 rounded p-3 mt-2">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Client Impact</div>
                        <p className="text-gray-700 dark:text-gray-300 text-sm">{change.clientImpact}</p>
                      </div>
                    )}
                    {/* Source Document (simple format) */}
                    {change.sourceDocument && change.sourcePage && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 flex flex-wrap gap-1">
                        <button
                          className="inline-flex items-center text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                          onClick={() => {
                            const docName = change.sourceDocument;
                            const matchingDoc = documents?.find((d: any) =>
                              d.name === docName || d.originalName === docName || d.fileName === docName
                            );
                            if (matchingDoc) {
                              onCitationClick({
                                id: `change-${idx}-source`,
                                sourceDocId: matchingDoc.id,
                                page: change.sourcePage,
                                documentName: docName,
                                clickable: true,
                                url: '',
                                type: 'page'
                              });
                            }
                          }}
                          title={`Click to view: ${change.sourceDocument} - Page ${change.sourcePage}`}
                        >
                          {change.sourceDocument} - p.{change.sourcePage}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Action Items */}
          {(data as any).actionItems && Array.isArray((data as any).actionItems) && (data as any).actionItems.length > 0 && (
            <section id="action-items" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-blue-500 pb-2">
                Action Items
              </h2>
              <div className="space-y-4">
                {(data as any).actionItems.map((item: any, idx: number) => (
                  <div key={idx} className={`rounded-lg border-l-4 p-4 ${
                    item.priority === 'HIGH' || item.priority === 'CRITICAL' ? 'bg-red-50 dark:bg-red-900/20 border-red-500' :
                    item.priority === 'MEDIUM' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500' :
                    'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{item.action}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.priority === 'HIGH' || item.priority === 'CRITICAL' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' :
                        item.priority === 'MEDIUM' ? 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-100' :
                        'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
                      }`}>
                        {item.priority}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm mb-2">
                      {item.category && (
                        <div><span className="text-gray-500 dark:text-gray-400">Category:</span> <span className="font-medium">{item.category.replace(/_/g, ' ')}</span></div>
                      )}
                      {item.deadline && (
                        <div><span className="text-gray-500 dark:text-gray-400">Deadline:</span> <span className="font-medium">{item.deadline}</span></div>
                      )}
                      {item.responsibleParty && (
                        <div><span className="text-gray-500 dark:text-gray-400">Responsible:</span> <span className="font-medium">{item.responsibleParty}</span></div>
                      )}
                    </div>
                    {item.details && (
                      <p className="text-gray-700 dark:text-gray-300 text-sm">{item.details}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Red Flags */}
          {(data as any).redFlags && Array.isArray((data as any).redFlags) && (data as any).redFlags.length > 0 && (
            <section id="red-flags" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-red-500 pb-2">
                Red Flags
              </h2>
              <div className="space-y-3">
                {(data as any).redFlags.map((flag: any, idx: number) => (
                  <div key={idx} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start">
                      <span className="text-red-500 text-lg mr-3">🚩</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {flag.category && <span className="text-xs bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100 px-2 py-0.5 rounded">{flag.category}</span>}
                          {flag.severity && <span className="text-xs bg-red-200 dark:bg-red-700 text-red-800 dark:text-red-100 px-2 py-0.5 rounded">{flag.severity}</span>}
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-medium mb-1">{flag.description}</p>
                        {flag.clientTranslation && (
                          <p className="text-gray-600 dark:text-gray-400 text-sm italic">"{flag.clientTranslation}"</p>
                        )}
                        {flag.actionNeeded && (
                          <p className="text-red-700 dark:text-red-400 text-sm mt-2">Action: {flag.actionNeeded}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Positive Signals */}
          {(data as any).positiveSignals && Array.isArray((data as any).positiveSignals) && (data as any).positiveSignals.length > 0 && (
            <section id="positive-signals" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-green-500 pb-2">
                Positive Signals
              </h2>
              <div className="space-y-3">
                {(data as any).positiveSignals.map((signal: any, idx: number) => (
                  <div key={idx} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-start">
                      <span className="text-green-500 text-lg mr-3">✅</span>
                      <div className="flex-1">
                        {signal.category && <span className="text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 px-2 py-0.5 rounded mb-1 inline-block">{signal.category}</span>}
                        <p className="text-gray-800 dark:text-gray-200 font-medium mb-1">{signal.description}</p>
                        {signal.clientBenefit && (
                          <p className="text-green-700 dark:text-green-400 text-sm">Benefit: {signal.clientBenefit}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Conversation Points */}
          {(data as any).conversationPoints && Array.isArray((data as any).conversationPoints) && (data as any).conversationPoints.length > 0 && (
            <section id="conversation-points" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-purple-500 pb-2">
                Conversation Points
              </h2>
              <div className="space-y-4">
                {(data as any).conversationPoints.map((point: any, idx: number) => (
                  <div key={idx} className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">{point.topic}</h4>
                    {point.context && <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{point.context}</p>}
                    {point.considerations && (
                      <div className="bg-white dark:bg-gray-800 rounded p-3 mb-2">
                        <div className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-1">Key Considerations</div>
                        <p className="text-gray-700 dark:text-gray-300 text-sm">{point.considerations}</p>
                      </div>
                    )}
                    {point.questionsToAsk && (
                      <div className="text-sm text-purple-700 dark:text-purple-400">
                        <span className="font-medium">Questions to Ask:</span> {point.questionsToAsk}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Glossary */}
          {(data as any).glossary && Array.isArray((data as any).glossary) && (data as any).glossary.length > 0 && (
            <section id="glossary" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-400 pb-2">
                Glossary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data as any).glossary.map((item: any, idx: number) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{item.term}</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{item.definition}</p>
                    {item.whyItMatters && (
                      <p className="text-gray-500 dark:text-gray-400 text-xs italic">Why it matters: {item.whyItMatters}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Confidence & Limitations */}
          {(data as any).confidenceAndLimitations && (
            <section id="confidence-limitations" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-400 pb-2">
                Confidence & Limitations
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(data as any).confidenceAndLimitations.overallConfidence && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                    <div className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">Overall Confidence</div>
                    <div className="text-2xl font-bold text-blue-800 dark:text-blue-300">{(data as any).confidenceAndLimitations.overallConfidence}</div>
                  </div>
                )}
                {(data as any).confidenceAndLimitations.documentQuality && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Document Quality</div>
                    <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">{(data as any).confidenceAndLimitations.documentQuality}</div>
                  </div>
                )}
              </div>
              {(data as any).confidenceAndLimitations.limitations && Array.isArray((data as any).confidenceAndLimitations.limitations) && (data as any).confidenceAndLimitations.limitations.length > 0 && (
                <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
                  <div className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">Limitations</div>
                  <ul className="list-disc list-inside space-y-1">
                    {(data as any).confidenceAndLimitations.limitations.map((limitation: string, idx: number) => (
                      <li key={idx} className="text-gray-700 dark:text-gray-300 text-sm">{limitation}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(data as any).confidenceAndLimitations.missingDocuments && Array.isArray((data as any).confidenceAndLimitations.missingDocuments) && (data as any).confidenceAndLimitations.missingDocuments.length > 0 && (
                <div className="mt-4 bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700">
                  <div className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">Missing Documents</div>
                  <ul className="list-disc list-inside space-y-1">
                    {(data as any).confidenceAndLimitations.missingDocuments.map((doc: string, idx: number) => (
                      <li key={idx} className="text-gray-700 dark:text-gray-300 text-sm">{doc}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* Professional Disclaimer */}
          {(data as any).professionalDisclaimer && (
            <section id="professional-disclaimer" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-400 pb-2">
                Professional Disclaimer
              </h2>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <p className="text-gray-600 dark:text-gray-400 text-sm italic">
                  {typeof (data as any).professionalDisclaimer === 'string'
                    ? (data as any).professionalDisclaimer
                    : (data as any).professionalDisclaimer.text || ''}
                </p>
              </div>
            </section>
          )}
        </>
      )}

      {/* ===== FINANCIAL ANALYSIS SECTIONS ===== */}

      {/* Financial Risk Assessment */}
      {(data as any).financialRiskAssessment && (
        <section id="financial-risk-assessment" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-emerald-500 pb-2">
            Financial Risk Assessment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Overall Score Card */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg p-6 border border-emerald-200 dark:border-emerald-700">
              <div className="text-center">
                <div className="text-sm font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-2">Overall Risk Score</div>
                <div className="text-4xl font-bold text-emerald-700 dark:text-emerald-300 mb-2">
                  {(data as any).financialRiskAssessment.overallScore || 'N/A'}
                </div>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  (data as any).financialRiskAssessment.riskCategory?.toLowerCase().includes('low') ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                  (data as any).financialRiskAssessment.riskCategory?.toLowerCase().includes('high') ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                }`}>
                  {(data as any).financialRiskAssessment.riskCategory || 'Unknown'}
                </div>
              </div>
            </div>

            {/* Risk Trajectory */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Risk Trajectory</div>
              <div className={`text-xl font-semibold mb-2 ${
                (data as any).financialRiskAssessment.riskTrajectory?.toLowerCase().includes('improv') ? 'text-green-600 dark:text-green-400' :
                (data as any).financialRiskAssessment.riskTrajectory?.toLowerCase().includes('deterior') ? 'text-red-600 dark:text-red-400' :
                'text-gray-700 dark:text-gray-300'
              }`}>
                {(data as any).financialRiskAssessment.riskTrajectory || 'N/A'}
              </div>
              {(data as any).financialRiskAssessment.trajectoryRationale && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {(data as any).financialRiskAssessment.trajectoryRationale}
                </p>
              )}
            </div>

            {/* Individual Risk Scores */}
            {['liquidityRisk', 'profitabilityRisk', 'leverageRisk'].map((riskType) => {
              const risk = (data as any).financialRiskAssessment[riskType];
              if (!risk) return null;
              const label = riskType.replace('Risk', '').replace(/([A-Z])/g, ' $1').trim();
              return (
                <div key={riskType} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{label} Risk</span>
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      risk.category?.toLowerCase().includes('low') ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                      risk.category?.toLowerCase().includes('high') ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                    }`}>
                      {risk.score || 'N/A'} - {risk.category || 'Unknown'}
                    </span>
                  </div>
                  {risk.rationale && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">{risk.rationale}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Period Comparison */}
      {(data as any).periodComparison && (
        <section id="period-comparison" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-blue-500 pb-2">
            Period-Over-Period Comparison
          </h2>

          {/* Periods Analyzed */}
          {(data as any).periodComparison.periodsAnalyzed && (
            <div className="mb-4 flex flex-wrap gap-2">
              {(data as any).periodComparison.periodsAnalyzed.map((period: string, idx: number) => (
                <span key={idx} className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
                  {period}
                </span>
              ))}
            </div>
          )}

          {/* Metrics Tables */}
          {['revenueMetrics', 'profitabilityMetrics', 'liquidityMetrics', 'leverageMetrics'].map((metricType) => {
            const metrics = (data as any).periodComparison[metricType];
            if (!metrics || !Array.isArray(metrics) || metrics.length === 0) return null;
            const label = metricType.replace('Metrics', '').replace(/([A-Z])/g, ' $1').trim();

            return (
              <div key={metricType} className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 capitalize">{label} Metrics</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Metric</th>
                        {(data as any).periodComparison.periodsAnalyzed?.map((period: string, idx: number) => (
                          <th key={idx} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">{period}</th>
                        ))}
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Change</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Trend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {metrics.map((metric: any, idx: number) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-200">{metric.metric}</td>
                          {metric.values?.map((val: string, vIdx: number) => (
                            <td key={vIdx} className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{val}</td>
                          ))}
                          <td className={`px-4 py-3 text-sm font-medium ${
                            metric.percentChange?.includes('+') ? 'text-green-600 dark:text-green-400' :
                            metric.percentChange?.includes('-') ? 'text-red-600 dark:text-red-400' :
                            'text-gray-600 dark:text-gray-400'
                          }`}>
                            {metric.percentChange || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              metric.trend?.toLowerCase().includes('increas') ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                              metric.trend?.toLowerCase().includes('decreas') ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {metric.trend || 'Stable'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* Detailed Analysis */}
      {(data as any).detailedAnalysis && (
        <section id="detailed-analysis" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-purple-500 pb-2">
            Detailed Financial Analysis
          </h2>
          <div className="space-y-6">
            {['revenueAnalysis', 'profitabilityAnalysis', 'liquidityAnalysis', 'leverageAnalysis', 'cashFlowAnalysis', 'industrySpecificAnalysis', 'footnoteAnalysis'].map((analysisType) => {
              const analysis = (data as any).detailedAnalysis[analysisType];
              if (!analysis || !analysis.text) return null;
              const label = analysisType.replace('Analysis', '').replace(/([A-Z])/g, ' $1').trim();
              // Create ID from analysisType: revenueAnalysis -> revenue-analysis
              const sectionId = analysisType.replace(/([A-Z])/g, '-$1').toLowerCase();

              return (
                <div key={analysisType} id={sectionId} className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-purple-700 dark:text-purple-400 mb-3 capitalize">{label} Analysis</h3>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{analysis.text}</p>

                  {/* Citations */}
                  {!hideCitations && analysis.citations && analysis.citations.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {analysis.citations.map((citation: any, citIdx: number) => (
                        <button
                          key={citIdx}
                          onClick={() => onCitationClick({
                            ...citation,
                            type: 'page',
                            sourceDocId: citation.docId,
                            documentId: citation.docId,
                            documentName: citation.docName,
                            clickable: true,
                            url: citation.docId,
                            found: true
                          })}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                          title={`${citation.docName} - Page ${citation.page}${citation.section ? ` - ${citation.section}` : ''}${citation.quote ? `\n"${citation.quote}"` : ''}`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>p.{citation.page}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Footnote specific fields */}
                  {analysisType === 'footnoteAnalysis' && analysis.criticalFindings && analysis.criticalFindings.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Critical Findings</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {analysis.criticalFindings.map((finding: string, idx: number) => (
                          <li key={idx}>{finding}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {analysisType === 'footnoteAnalysis' && analysis.redFlags && analysis.redFlags.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Red Flags</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-red-600 dark:text-red-400">
                        {analysis.redFlags.map((flag: string, idx: number) => (
                          <li key={idx}>{flag}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ERM Risk Mapping */}
      {(data as any).ermRiskMapping && (
        <section id="erm-risk-mapping" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-orange-500 pb-2">
            ERM Risk Mapping
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {['strategicRisks', 'operationalRisks', 'financialRisks', 'complianceRisks'].map((riskCategory) => {
              const risks = (data as any).ermRiskMapping[riskCategory];
              if (!risks || !Array.isArray(risks) || risks.length === 0) return null;
              const label = riskCategory.replace('Risks', '').replace(/([A-Z])/g, ' $1').trim();
              // Create ID: strategicRisks -> strategic-risks
              const sectionId = riskCategory.replace(/([A-Z])/g, '-$1').toLowerCase();
              const colorMap: Record<string, string> = {
                strategic: 'blue',
                operational: 'purple',
                financial: 'emerald',
                compliance: 'orange'
              };
              const color = colorMap[label.toLowerCase()] || 'gray';

              return (
                <div key={riskCategory} id={sectionId} className={`bg-${color}-50 dark:bg-${color}-900/20 rounded-lg p-4 border border-${color}-200 dark:border-${color}-700`}>
                  <h3 className={`text-lg font-semibold text-${color}-700 dark:text-${color}-400 mb-3 capitalize`}>{label} Risks</h3>
                  <div className="space-y-3">
                    {risks.map((risk: any, idx: number) => (
                      <div key={idx} className="bg-white dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-gray-800 dark:text-gray-200">{risk.risk}</span>
                          {risk.severity && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              risk.severity.toLowerCase().includes('high') ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                              risk.severity.toLowerCase().includes('low') ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                            }`}>
                              {risk.severity}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{risk.indicator}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Underwriting Recommendation */}
      {(data as any).underwritingRecommendation && (
        <section id="underwriting-recommendation" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-500 pb-2">
            Underwriting Recommendation
          </h2>
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg p-6 border border-indigo-200 dark:border-indigo-700">
            {/* Decision */}
            <div className="text-center mb-6">
              <div className="text-sm font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-2">Decision</div>
              <div className={`text-2xl font-bold px-6 py-2 rounded-full inline-block ${
                (data as any).underwritingRecommendation.decision?.toLowerCase().includes('accept') ||
                (data as any).underwritingRecommendation.decision?.toLowerCase().includes('approve') ?
                'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                (data as any).underwritingRecommendation.decision?.toLowerCase().includes('decline') ||
                (data as any).underwritingRecommendation.decision?.toLowerCase().includes('reject') ?
                'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
              }`}>
                {(data as any).underwritingRecommendation.decision}
              </div>
            </div>

            {/* Rationale */}
            {(data as any).underwritingRecommendation.rationale && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Rationale</h3>
                <p className="text-gray-600 dark:text-gray-400">{(data as any).underwritingRecommendation.rationale}</p>
              </div>
            )}

            {/* Conditions */}
            {(data as any).underwritingRecommendation.conditions && (data as any).underwritingRecommendation.conditions.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Conditions</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                  {(data as any).underwritingRecommendation.conditions.map((condition: string, idx: number) => (
                    <li key={idx}>{condition}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pricing/Retention Adjustments */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {(data as any).underwritingRecommendation.pricingAdjustments && (
                <div className="bg-white dark:bg-gray-800 rounded p-4">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1">Pricing Adjustments</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{(data as any).underwritingRecommendation.pricingAdjustments}</p>
                </div>
              )}
              {(data as any).underwritingRecommendation.retentionAdjustments && (
                <div className="bg-white dark:bg-gray-800 rounded p-4">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1">Retention Adjustments</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{(data as any).underwritingRecommendation.retentionAdjustments}</p>
                </div>
              )}
            </div>

            {/* Monitoring */}
            {((data as any).underwritingRecommendation.monitoringRequirements || (data as any).underwritingRecommendation.monitoringFrequency) && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Monitoring Requirements</h3>
                <div className="bg-white dark:bg-gray-800 rounded p-4">
                  {(data as any).underwritingRecommendation.monitoringFrequency && (
                    <div className="mb-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Frequency: </span>
                      <span className="text-gray-600 dark:text-gray-400">{(data as any).underwritingRecommendation.monitoringFrequency}</span>
                    </div>
                  )}
                  {(data as any).underwritingRecommendation.monitoringRequirements && (
                    <p className="text-gray-600 dark:text-gray-400">{(data as any).underwritingRecommendation.monitoringRequirements}</p>
                  )}
                </div>
              </div>
            )}

            {/* Metrics to Track */}
            {(data as any).underwritingRecommendation.metricsToTrack && (data as any).underwritingRecommendation.metricsToTrack.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Metrics to Track</h3>
                <div className="flex flex-wrap gap-2">
                  {(data as any).underwritingRecommendation.metricsToTrack.map((metric: string, idx: number) => (
                    <span key={idx} className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded-full text-sm">
                      {metric}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Reevaluation Triggers */}
            {(data as any).underwritingRecommendation.reevaluationTriggers && (data as any).underwritingRecommendation.reevaluationTriggers.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Reevaluation Triggers</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                  {(data as any).underwritingRecommendation.reevaluationTriggers.map((trigger: string, idx: number) => (
                    <li key={idx}>{trigger}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Financial Analysis - Confidence & Limitations */}
      {((data.responseMetadata?.workflowType?.toLowerCase()?.includes('financial_analysis') ||
        data.responseMetadata?.workflowType?.toLowerCase()?.includes('financial analysis') ||
        ((data as any).financialRiskAssessment && (data as any).ermRiskMapping)) && (data as any).confidenceAndLimitations) && (
        <section id="financial-confidence-limitations" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-400 pb-2">
            Confidence & Limitations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(data as any).confidenceAndLimitations.overallConfidence && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
                <div className="text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-1">Confidence Level</div>
                <div className="text-2xl font-bold text-emerald-800 dark:text-emerald-300">{(data as any).confidenceAndLimitations.overallConfidence}</div>
              </div>
            )}
            {(data as any).confidenceAndLimitations.confidenceTier && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Confidence Tier</div>
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">{(data as any).confidenceAndLimitations.confidenceTier}</div>
              </div>
            )}
          </div>
          {(data as any).confidenceAndLimitations.basis && (
            <div className="mt-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Basis</div>
              <p className="text-gray-700 dark:text-gray-300">{(data as any).confidenceAndLimitations.basis}</p>
            </div>
          )}
          {(data as any).confidenceAndLimitations.limitations && Array.isArray((data as any).confidenceAndLimitations.limitations) && (data as any).confidenceAndLimitations.limitations.length > 0 && (
            <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
              <div className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">Limitations</div>
              <ul className="list-disc list-inside space-y-1">
                {(data as any).confidenceAndLimitations.limitations.map((limitation: string, idx: number) => (
                  <li key={idx} className="text-gray-700 dark:text-gray-300 text-sm">{limitation}</li>
                ))}
              </ul>
            </div>
          )}
          {(data as any).confidenceAndLimitations.humanVerificationRequired && Array.isArray((data as any).confidenceAndLimitations.humanVerificationRequired) && (data as any).confidenceAndLimitations.humanVerificationRequired.length > 0 && (
            <div className="mt-4 bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700">
              <div className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">Human Verification Required</div>
              <ul className="list-disc list-inside space-y-1">
                {(data as any).confidenceAndLimitations.humanVerificationRequired.map((item: string, idx: number) => (
                  <li key={idx} className="text-gray-700 dark:text-gray-300 text-sm">{item}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Financial Analysis - Professional Disclaimer */}
      {((data.responseMetadata?.workflowType?.toLowerCase()?.includes('financial_analysis') ||
        data.responseMetadata?.workflowType?.toLowerCase()?.includes('financial analysis') ||
        ((data as any).financialRiskAssessment && (data as any).ermRiskMapping)) && (data as any).professionalDisclaimer) && (
        <section id="financial-professional-disclaimer" className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-400 pb-2">
            Professional Disclaimer
          </h2>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400 text-sm italic">
              {typeof (data as any).professionalDisclaimer === 'string'
                ? (data as any).professionalDisclaimer
                : (data as any).professionalDisclaimer.text || ''}
            </p>
          </div>
        </section>
      )}

      {/* ===== RATE ANALYSIS SECTIONS ===== */}
      {(data.responseMetadata?.workflowType === 'rate_analysis' || (data as any).analysisTables || (data as any).driverHierarchy) && (
        <>
          {/* Rate Analysis Executive Summary */}
          {(data as any).executiveSummary && (
            <section id="rate-analysis-executive-summary" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Executive Summary
              </h2>

              {/* TL;DR */}
              {(data as any).executiveSummary.tldr && (
                <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border-l-4 border-indigo-500 mb-6">
                  <h3 className="font-semibold text-indigo-800 dark:text-indigo-200 mb-2">TL;DR</h3>
                  {renderSafeContent((data as any).executiveSummary.tldr, 'TL;DR', hideCitations)}
                </div>
              )}

              {/* Analysis Tier and Policy Identification */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {(data as any).executiveSummary.analysisTier && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Analysis Tier</div>
                    <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">{(data as any).executiveSummary.analysisTier}</div>
                  </div>
                )}
                {(data as any).executiveSummary.periodsAnalyzed && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Periods Analyzed</div>
                    <div className="text-gray-800 dark:text-gray-200">
                      {Array.isArray((data as any).executiveSummary.periodsAnalyzed)
                        ? (data as any).executiveSummary.periodsAnalyzed.join(', ')
                        : (data as any).executiveSummary.periodsAnalyzed}
                    </div>
                  </div>
                )}
              </div>

              {/* Policy Identification */}
              {(data as any).executiveSummary.policyIdentification && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-6">
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Policy Identification</h3>
                  {renderSafeContent((data as any).executiveSummary.policyIdentification, 'Policy Identification', hideCitations)}
                </div>
              )}

              {/* Total Premium Change */}
              {(data as any).executiveSummary.totalPremiumChange && (
                <div className={`rounded-lg p-6 mb-6 ${
                  (data as any).executiveSummary.totalPremiumChange.direction === 'increase'
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
                    : (data as any).executiveSummary.totalPremiumChange.direction === 'decrease'
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
                    : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                }`}>
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Total Premium Change</h3>
                  <div className="flex items-center gap-4">
                    <div className={`text-3xl font-bold ${
                      (data as any).executiveSummary.totalPremiumChange.direction === 'increase'
                        ? 'text-red-600 dark:text-red-400'
                        : (data as any).executiveSummary.totalPremiumChange.direction === 'decrease'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {(data as any).executiveSummary.totalPremiumChange.direction === 'increase' ? '↑' :
                       (data as any).executiveSummary.totalPremiumChange.direction === 'decrease' ? '↓' : '→'}
                      {' '}
                      {typeof (data as any).executiveSummary.totalPremiumChange.percentage === 'number'
                        ? `${(data as any).executiveSummary.totalPremiumChange.percentage.toFixed(1)}%`
                        : (data as any).executiveSummary.totalPremiumChange.percentage}
                    </div>
                    <div className="text-lg text-gray-600 dark:text-gray-400">
                      ${typeof (data as any).executiveSummary.totalPremiumChange.amount === 'number'
                        ? (data as any).executiveSummary.totalPremiumChange.amount.toLocaleString()
                        : (data as any).executiveSummary.totalPremiumChange.amount}
                    </div>
                  </div>
                </div>
              )}

              {/* Primary Driver */}
              {(data as any).executiveSummary.primaryDriver && (
                <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700 mb-4">
                  <h3 className="font-semibold text-indigo-800 dark:text-indigo-200 mb-2">Primary Driver</h3>
                  {renderSafeContent((data as any).executiveSummary.primaryDriver, 'Primary Driver', hideCitations)}
                </div>
              )}

              {/* Secondary Drivers */}
              {(data as any).executiveSummary.secondaryDrivers && Array.isArray((data as any).executiveSummary.secondaryDrivers) && (data as any).executiveSummary.secondaryDrivers.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-6">
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Secondary Drivers</h3>
                  <ul className="space-y-2">
                    {(data as any).executiveSummary.secondaryDrivers.map((driver: any, idx: number) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-indigo-500 mt-1">•</span>
                        <span>{renderSafeContent(driver, `Secondary Driver ${idx + 1}`, hideCitations)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Executive Narrative */}
              {(data as any).executiveSummary.executiveNarrative && (
                <div className="prose dark:prose-invert max-w-none">
                  {renderSafeContent((data as any).executiveSummary.executiveNarrative, 'Executive Narrative', hideCitations)}
                </div>
              )}
            </section>
          )}

          {/* Analysis Tables */}
          {(data as any).analysisTables && Array.isArray((data as any).analysisTables) && (data as any).analysisTables.length > 0 && (
            <section id="rate-analysis-tables" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Analysis Tables
              </h2>
              {(data as any).analysisTables.map((table: any, tableIdx: number) => (
                <div key={tableIdx} className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 border-b border-indigo-200 dark:border-indigo-700">
                    <h3 className="text-lg font-semibold text-indigo-800 dark:text-indigo-200">{table.title}</h3>
                    {table.description && (
                      <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">{table.description}</p>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-700">
                          {table.headers && table.headers.map((header: string, headerIdx: number) => (
                            <th key={headerIdx} className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {table.rows && table.rows.map((row: any, rowIdx: number) => (
                          <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}>
                            {table.headers && table.headers.map((header: string, cellIdx: number) => {
                              const cellValue = row[header] || row[header.toLowerCase()] || Object.values(row)[cellIdx];
                              return (
                                <td key={cellIdx} className="px-4 py-3 text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                                  {typeof cellValue === 'object' && cellValue !== null
                                    ? renderSafeContent(cellValue, `Table Cell ${rowIdx}-${cellIdx}`, hideCitations)
                                    : cellValue?.toString() || '-'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {table.totals && (
                          <tr className="bg-indigo-50 dark:bg-indigo-900/30 font-semibold">
                            {table.headers && table.headers.map((header: string, cellIdx: number) => {
                              const totalValue = table.totals[header] || table.totals[header.toLowerCase()] || '';
                              return (
                                <td key={cellIdx} className="px-4 py-3 text-indigo-800 dark:text-indigo-200 border-t-2 border-indigo-300 dark:border-indigo-600">
                                  {cellIdx === 0 ? 'Total' : totalValue?.toString() || '-'}
                                </td>
                              );
                            })}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {table.insights && Array.isArray(table.insights) && table.insights.length > 0 && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-700">
                      <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">Key Insights</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                        {table.insights.map((insight: string, insightIdx: number) => (
                          <li key={insightIdx}>{insight}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </section>
          )}

          {/* Driver Hierarchy */}
          {(data as any).driverHierarchy && (
            <section id="rate-analysis-driver-hierarchy" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Driver Hierarchy
              </h2>
              <div className="space-y-4">
                {/* Primary Driver */}
                {(data as any).driverHierarchy.primaryDriver && (
                  <div className="bg-indigo-100 dark:bg-indigo-900/40 rounded-lg p-6 border-2 border-indigo-300 dark:border-indigo-600">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-indigo-800 dark:text-indigo-200">
                        Primary Driver: {(data as any).driverHierarchy.primaryDriver.component}
                      </h3>
                      <span className="px-3 py-1 bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-semibold">
                        {(data as any).driverHierarchy.primaryDriver.percentageOfTotal}% of total
                      </span>
                    </div>
                    {renderSafeContent((data as any).driverHierarchy.primaryDriver.impact, 'Primary Driver Impact', hideCitations)}
                  </div>
                )}

                {/* Secondary Drivers */}
                {(data as any).driverHierarchy.secondaryDrivers && Array.isArray((data as any).driverHierarchy.secondaryDrivers) && (data as any).driverHierarchy.secondaryDrivers.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(data as any).driverHierarchy.secondaryDrivers.map((driver: any, idx: number) => (
                      <div key={idx} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-800 dark:text-gray-200">{driver.component}</h4>
                          <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                            {driver.percentageOfTotal}%
                          </span>
                        </div>
                        {renderSafeContent(driver.impact, `Secondary Driver ${idx + 1} Impact`, hideCitations)}
                      </div>
                    ))}
                  </div>
                )}

                {/* Interaction Effects */}
                {(data as any).driverHierarchy.interactionEffects && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700 mt-4">
                    <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">Interaction Effects</h4>
                    {renderSafeContent((data as any).driverHierarchy.interactionEffects, 'Interaction Effects', hideCitations)}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Weak Signals */}
          {(data as any).weakSignals && Array.isArray((data as any).weakSignals) && (data as any).weakSignals.length > 0 && (
            <section id="rate-analysis-weak-signals" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-amber-400 pb-2">
                Weak Signals Detected
              </h2>
              <div className="space-y-4">
                {(data as any).weakSignals.map((signal: any, idx: number) => (
                  <div key={idx} className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border-l-4 border-amber-500">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-1 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded text-xs font-medium">
                        {signal.category}
                      </span>
                      <h3 className="font-semibold text-amber-800 dark:text-amber-200">{signal.indicator}</h3>
                    </div>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                      <span className="font-medium">Detected in:</span> {signal.whereDetected}
                    </p>
                    <div className="mb-2">
                      <span className="font-medium text-amber-800 dark:text-amber-200">Pattern:</span>
                      {renderSafeContent(signal.pattern, `Weak Signal Pattern ${idx}`, hideCitations)}
                    </div>
                    <div>
                      <span className="font-medium text-amber-800 dark:text-amber-200">Significance:</span>
                      {renderSafeContent(signal.significance, `Weak Signal Significance ${idx}`, hideCitations)}
                    </div>
                    {signal.context && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 italic">{signal.context}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Trend Analysis */}
          {(data as any).trendAnalysis && (
            <section id="rate-analysis-trend" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Trend Analysis
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* CAGR Metrics */}
                {(data as any).trendAnalysis.cagr && (
                  <>
                    {(data as any).trendAnalysis.cagr.premium !== undefined && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                        <div className="text-sm font-medium text-blue-600 dark:text-blue-400">Premium CAGR</div>
                        <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                          {typeof (data as any).trendAnalysis.cagr.premium === 'number'
                            ? `${((data as any).trendAnalysis.cagr.premium * 100).toFixed(1)}%`
                            : (data as any).trendAnalysis.cagr.premium}
                        </div>
                      </div>
                    )}
                    {(data as any).trendAnalysis.cagr.rate !== undefined && (
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
                        <div className="text-sm font-medium text-green-600 dark:text-green-400">Rate CAGR</div>
                        <div className="text-2xl font-bold text-green-800 dark:text-green-200">
                          {typeof (data as any).trendAnalysis.cagr.rate === 'number'
                            ? `${((data as any).trendAnalysis.cagr.rate * 100).toFixed(1)}%`
                            : (data as any).trendAnalysis.cagr.rate}
                        </div>
                      </div>
                    )}
                    {(data as any).trendAnalysis.cagr.basis !== undefined && (
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                        <div className="text-sm font-medium text-purple-600 dark:text-purple-400">Basis CAGR</div>
                        <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                          {typeof (data as any).trendAnalysis.cagr.basis === 'number'
                            ? `${((data as any).trendAnalysis.cagr.basis * 100).toFixed(1)}%`
                            : (data as any).trendAnalysis.cagr.basis}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Trend Strength */}
              {(data as any).trendAnalysis.trendStrength && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-4">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Trend Strength</div>
                  <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">{(data as any).trendAnalysis.trendStrength}</div>
                </div>
              )}

              {/* Trajectory */}
              {(data as any).trendAnalysis.trajectory && (
                <div className="prose dark:prose-invert max-w-none mb-4">
                  <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Trajectory</h3>
                  {renderSafeContent((data as any).trendAnalysis.trajectory, 'Trajectory', hideCitations)}
                </div>
              )}

              {/* Inflection Points */}
              {(data as any).trendAnalysis.inflectionPoints && Array.isArray((data as any).trendAnalysis.inflectionPoints) && (data as any).trendAnalysis.inflectionPoints.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700">
                  <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">Inflection Points</h4>
                  <ul className="list-disc list-inside space-y-1 text-yellow-700 dark:text-yellow-300">
                    {(data as any).trendAnalysis.inflectionPoints.map((point: string, idx: number) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* Key Findings (Rate Analysis specific) */}
          {(data as any).keyFindings && (
            <section id="rate-analysis-key-findings" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Key Findings
              </h2>
              {(data as any).keyFindings.summary && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-6">
                  <p className="text-gray-700 dark:text-gray-300">{(data as any).keyFindings.summary}</p>
                </div>
              )}
              {(data as any).keyFindings.findings && Array.isArray((data as any).keyFindings.findings) && (
                <div className="space-y-4">
                  {(data as any).keyFindings.findings.map((finding: any, idx: number) => (
                    <div key={idx} className={`rounded-lg p-4 border-l-4 ${
                      finding.magnitude === 'Material (>5%)'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-500'
                        : finding.magnitude === 'Moderate (2-5%)'
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-500'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">{finding.category}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          finding.magnitude === 'Material (>5%)'
                            ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                            : finding.magnitude === 'Moderate (2-5%)'
                            ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200'
                            : 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                        }`}>
                          {finding.magnitude}
                        </span>
                      </div>
                      {finding.finding?.observation && (
                        <div className="mb-2">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Observation:</span>
                          {renderSafeContent(finding.finding.observation, `Finding ${idx} Observation`, hideCitations)}
                        </div>
                      )}
                      {finding.finding?.impact && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <span className="font-medium">Impact:</span> {finding.finding.impact}
                        </div>
                      )}
                      {finding.finding?.recommendation && (
                        <div className="text-sm text-indigo-600 dark:text-indigo-400">
                          <span className="font-medium">Recommendation:</span> {finding.finding.recommendation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Recommendations (Rate Analysis specific) */}
          {(data as any).recommendations && Array.isArray((data as any).recommendations) && (data as any).recommendations.length > 0 && (
            <section id="rate-analysis-recommendations" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Recommendations
              </h2>
              <div className="space-y-4">
                {(data as any).recommendations.map((rec: any, idx: number) => (
                  <div key={idx} className={`rounded-lg p-4 border ${
                    rec.priority === 'Critical'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                      : rec.priority === 'High'
                      ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
                      : rec.priority === 'Medium'
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
                      : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{rec.category}</span>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        rec.priority === 'Critical'
                          ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                          : rec.priority === 'High'
                          ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200'
                          : rec.priority === 'Medium'
                          ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200'
                          : 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                      }`}>
                        {rec.priority}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Action:</span>
                      {renderSafeContent(rec.action, `Recommendation ${idx} Action`, hideCitations)}
                    </div>
                    <div className="mb-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Rationale:</span>
                      {renderSafeContent(rec.rationale, `Recommendation ${idx} Rationale`, hideCitations)}
                    </div>
                    {rec.expectedOutcome && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Expected Outcome:</span> {rec.expectedOutcome}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* ===== PORTFOLIO ANALYZER SECTIONS ===== */}
      {(data.responseMetadata?.workflowType === 'portfolio_analyzer' || (data as any).portfolioStructure || (data as any).scenarioAnalysis) && (
        <>
          {/* Portfolio Structure */}
          {(data as any).portfolioStructure && (
            <section id="portfolio-structure" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-slate-400 pb-2">
                Portfolio Structure
              </h2>

              {/* Vertical Towers */}
              {(data as any).portfolioStructure.verticalTowers && Array.isArray((data as any).portfolioStructure.verticalTowers) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300">Coverage Towers</h3>
                  <div className="space-y-4">
                    {(data as any).portfolioStructure.verticalTowers.map((tower: any, idx: number) => (
                      <div key={idx} className="bg-slate-50 dark:bg-slate-900/30 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                        <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3">
                          {safeString(tower.coverageLine)}
                          {tower.totalVerticalLimit && (
                            <span className="ml-2 text-sm font-normal text-green-600 dark:text-green-400">
                              (Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(tower.totalVerticalLimit)})
                            </span>
                          )}
                        </h4>
                        {tower.layers && Array.isArray(tower.layers) && (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-slate-100 dark:bg-slate-800">
                                <tr>
                                  <th className="px-3 py-2 text-left">Layer</th>
                                  <th className="px-3 py-2 text-left">Carrier</th>
                                  <th className="px-3 py-2 text-right">Limit</th>
                                  <th className="px-3 py-2 text-right">Attachment</th>
                                  <th className="px-3 py-2 text-center">Following Form</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tower.layers.map((layer: any, layerIdx: number) => (
                                  <tr key={layerIdx} className="border-b border-slate-200 dark:border-slate-700">
                                    <td className="px-3 py-2">{safeString(layer.layerType) || `Layer ${layer.layerNumber || layerIdx + 1}`}</td>
                                    <td className="px-3 py-2">{safeString(layer.carrier) || 'N/A'}</td>
                                    <td className="px-3 py-2 text-right">{layer.limit ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(layer.limit) : 'N/A'}</td>
                                    <td className="px-3 py-2 text-right">{layer.attachmentPoint ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(layer.attachmentPoint) : 'Ground'}</td>
                                    <td className="px-3 py-2 text-center">{layer.followingForm ? '✓' : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {tower.gapsIdentified && tower.gapsIdentified.length > 0 && (
                          <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-700">
                            <span className="text-red-700 dark:text-red-300 font-medium">Gaps: </span>
                            <span className="text-red-600 dark:text-red-400">{safeString(tower.gapsIdentified)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Horizontal Coverage Lines */}
              {(data as any).portfolioStructure.horizontalCoverageLines && Array.isArray((data as any).portfolioStructure.horizontalCoverageLines) && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300">Coverage Lines Overview</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border border-slate-200 dark:border-slate-700">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="px-4 py-2 text-left">Coverage Line</th>
                          <th className="px-4 py-2 text-left">Carriers</th>
                          <th className="px-4 py-2 text-right">Primary Limit</th>
                          <th className="px-4 py-2 text-right">Total Available</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data as any).portfolioStructure.horizontalCoverageLines.map((line: any, idx: number) => (
                          <tr key={idx} className="border-b border-slate-200 dark:border-slate-700">
                            <td className="px-4 py-2 font-medium">{safeString(line.coverageLine)}</td>
                            <td className="px-4 py-2">{safeString(line.carriers) || 'N/A'}</td>
                            <td className="px-4 py-2 text-right">{line.primaryLimit ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(line.primaryLimit) : 'N/A'}</td>
                            <td className="px-4 py-2 text-right font-semibold text-green-700 dark:text-green-400">{line.totalAvailable ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(line.totalAvailable) : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Scenario Analysis */}
          {(data as any).scenarioAnalysis && (
            <section id="scenario-analysis" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-purple-400 pb-2">
                Scenario Analysis
              </h2>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-5 rounded-lg border border-purple-200 dark:border-purple-700">
                {(data as any).scenarioAnalysis.scenarioName && (
                  <h3 className="text-xl font-semibold text-purple-800 dark:text-purple-300 mb-3">
                    {safeString((data as any).scenarioAnalysis.scenarioName)}
                  </h3>
                )}
                {(data as any).scenarioAnalysis.scenarioDescription && (
                  <div className="mb-4 text-gray-700 dark:text-gray-300">
                    {renderSafeContent((data as any).scenarioAnalysis.scenarioDescription, 'Scenario Description', hideCitations)}
                  </div>
                )}
                {(data as any).scenarioAnalysis.components && Array.isArray((data as any).scenarioAnalysis.components) && (
                  <div className="mt-4">
                    <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-2">Scenario Components</h4>
                    <div className="space-y-2">
                      {(data as any).scenarioAnalysis.components.map((comp: any, idx: number) => (
                        <div key={idx} className="bg-white dark:bg-gray-800 p-3 rounded border border-purple-100 dark:border-purple-800">
                          <div className="font-medium">{safeString(comp.componentName)}</div>
                          {comp.exposureType && <div className="text-sm text-gray-600 dark:text-gray-400">Type: {safeString(comp.exposureType)}</div>}
                          {comp.estimatedExposure && <div className="text-sm font-semibold text-orange-600 dark:text-orange-400">Estimated Exposure: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(comp.estimatedExposure)}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Coverage Analysis */}
          {(data as any).coverageAnalysis && Array.isArray((data as any).coverageAnalysis) && (data as any).coverageAnalysis.length > 0 && (
            <section id="coverage-analysis" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-blue-400 pb-2">
                Coverage Analysis
              </h2>
              <div className="space-y-6">
                {(data as any).coverageAnalysis.map((component: any, idx: number) => (
                  <div key={idx} className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-700">
                    <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-4">{safeString(component.componentName)}</h3>

                    {component.layerAnalysis && Array.isArray(component.layerAnalysis) && (
                      <div className="overflow-x-auto mb-4">
                        <table className="min-w-full text-sm">
                          <thead className="bg-blue-100 dark:bg-blue-800">
                            <tr>
                              <th className="px-3 py-2 text-left">Layer</th>
                              <th className="px-3 py-2 text-center">Responds</th>
                              <th className="px-3 py-2 text-right">Available Coverage</th>
                              <th className="px-3 py-2 text-center">Probability</th>
                              <th className="px-3 py-2 text-left">Rationale</th>
                            </tr>
                          </thead>
                          <tbody>
                            {component.layerAnalysis.map((layer: any, layerIdx: number) => (
                              <tr key={layerIdx} className="border-b border-blue-200 dark:border-blue-700">
                                <td className="px-3 py-2">{safeString(layer.layerName) || `Layer ${layerIdx + 1}`}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    safeString(layer.responds) === 'Yes' ? 'bg-green-200 text-green-800' :
                                    safeString(layer.responds) === 'No' ? 'bg-red-200 text-red-800' :
                                    'bg-yellow-200 text-yellow-800'
                                  }`}>
                                    {safeString(layer.responds)}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right">{layer.availableCoverage ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(layer.availableCoverage) : 'N/A'}</td>
                                <td className="px-3 py-2 text-center">{layer.probability ? `${layer.probability}%` : 'N/A'}</td>
                                <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                                  {layer.rationale && renderSafeContent(layer.rationale, `Layer ${layerIdx + 1} Rationale`, hideCitations)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {component.totalVerticalCoverage && (
                      <div className="flex gap-4 mt-3 p-3 bg-white dark:bg-gray-800 rounded">
                        <div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Maximum Available: </span>
                          <span className="font-semibold text-green-700 dark:text-green-400">
                            {component.totalVerticalCoverage.maximumAvailable ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(component.totalVerticalCoverage.maximumAvailable) : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Probable Recovery: </span>
                          <span className="font-semibold text-blue-700 dark:text-blue-400">
                            {component.totalVerticalCoverage.probableRecovery ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(component.totalVerticalCoverage.probableRecovery) : 'N/A'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Portfolio Synthesis */}
          {(data as any).portfolioSynthesis && (
            <section id="portfolio-synthesis" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-green-400 pb-2">
                Portfolio Synthesis
              </h2>
              <div className="space-y-4">
                {(data as any).portfolioSynthesis.totalPortfolioExposure && (
                  <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-700">
                    <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-4">Total Portfolio Exposure</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {(data as any).portfolioSynthesis.totalPortfolioExposure.maximumAvailableCoverage !== undefined && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Maximum Available</div>
                          <div className="text-xl font-bold text-green-700 dark:text-green-300">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format((data as any).portfolioSynthesis.totalPortfolioExposure.maximumAvailableCoverage)}
                          </div>
                        </div>
                      )}
                      {(data as any).portfolioSynthesis.totalPortfolioExposure.probableRecovery !== undefined && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Probable Recovery</div>
                          <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format((data as any).portfolioSynthesis.totalPortfolioExposure.probableRecovery)}
                          </div>
                        </div>
                      )}
                      {(data as any).portfolioSynthesis.totalPortfolioExposure.totalInsuredPays !== undefined && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Insured Pays</div>
                          <div className="text-xl font-bold text-orange-700 dark:text-orange-300">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format((data as any).portfolioSynthesis.totalPortfolioExposure.totalInsuredPays)}
                          </div>
                        </div>
                      )}
                      {(data as any).portfolioSynthesis.totalPortfolioExposure.totalUninsuredExposure !== undefined && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow-sm">
                          <div className="text-sm text-gray-600 dark:text-gray-400">Uninsured Exposure</div>
                          <div className="text-xl font-bold text-red-700 dark:text-red-300">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format((data as any).portfolioSynthesis.totalPortfolioExposure.totalUninsuredExposure)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(data as any).portfolioSynthesis.coverageGaps && Array.isArray((data as any).portfolioSynthesis.coverageGaps) && (data as any).portfolioSynthesis.coverageGaps.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-700">
                    <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-3">Coverage Gaps Identified</h3>
                    <div className="space-y-2">
                      {(data as any).portfolioSynthesis.coverageGaps.map((gap: any, idx: number) => (
                        <div key={idx} className="bg-white dark:bg-gray-800 p-3 rounded border-l-4 border-red-500">
                          <div className="font-medium text-gray-800 dark:text-gray-200">{renderSafeContent(gap.description, `Coverage Gap ${idx + 1}`, hideCitations)}</div>
                          {gap.estimatedExposure && (
                            <div className="text-sm text-red-600 dark:text-red-400">
                              Estimated Exposure: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(gap.estimatedExposure)}
                            </div>
                          )}
                          {gap.location && <div className="text-sm text-gray-600 dark:text-gray-400">Location: {renderSafeContent(gap.location, `Gap Location ${idx + 1}`, hideCitations)}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Assessment */}
          {(data as any).assessment && (
            <section id="assessment" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-amber-400 pb-2">
                Assessment
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {(data as any).assessment.portfolioCoverageCertainty && (
                  <div className={`p-4 rounded-lg border ${
                    safeString((data as any).assessment.portfolioCoverageCertainty.level) === 'High' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' :
                    safeString((data as any).assessment.portfolioCoverageCertainty.level) === 'Low' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' :
                    'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700'
                  }`}>
                    <h4 className="font-semibold mb-2">Coverage Certainty</h4>
                    <div className="text-2xl font-bold mb-2">{safeString((data as any).assessment.portfolioCoverageCertainty.level)}</div>
                    {(data as any).assessment.portfolioCoverageCertainty.reasoning && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {renderSafeContent((data as any).assessment.portfolioCoverageCertainty.reasoning, 'Coverage Certainty Reasoning', hideCitations)}
                      </div>
                    )}
                  </div>
                )}
                {(data as any).assessment.portfolioDenialRisk && (
                  <div className={`p-4 rounded-lg border ${
                    safeString((data as any).assessment.portfolioDenialRisk.level) === 'Low' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' :
                    safeString((data as any).assessment.portfolioDenialRisk.level) === 'High' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' :
                    'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700'
                  }`}>
                    <h4 className="font-semibold mb-2">Denial Risk</h4>
                    <div className="text-2xl font-bold mb-2">{safeString((data as any).assessment.portfolioDenialRisk.level)}</div>
                    {(data as any).assessment.portfolioDenialRisk.reasoning && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {renderSafeContent((data as any).assessment.portfolioDenialRisk.reasoning, 'Denial Risk Reasoning', hideCitations)}
                      </div>
                    )}
                  </div>
                )}
                {(data as any).assessment.analysisConfidence && (
                  <div className="p-4 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
                    <h4 className="font-semibold mb-2">Analysis Confidence</h4>
                    <div className="text-2xl font-bold mb-2">{(data as any).assessment.analysisConfidence.percentage}%</div>
                    {(data as any).assessment.analysisConfidence.reasoning && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">{renderSafeContent((data as any).assessment.analysisConfidence.reasoning, 'Analysis Confidence Reasoning', hideCitations)}</div>
                    )}
                    {(data as any).assessment.analysisConfidence.factorsReducingConfidence && Array.isArray((data as any).assessment.analysisConfidence.factorsReducingConfidence) && (
                      <div className="text-xs text-orange-600 dark:text-orange-400">
                        Limiting factors: {safeString((data as any).assessment.analysisConfidence.factorsReducingConfidence)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Recommendations (Portfolio Analyzer specific) */}
          {(data as any).recommendations && Array.isArray((data as any).recommendations) && (data as any).recommendations.length > 0 && data.responseMetadata?.workflowType === 'portfolio_analyzer' && (
            <section id="portfolio-recommendations" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Recommendations
              </h2>
              <div className="space-y-4">
                {(data as any).recommendations.map((rec: any, idx: number) => (
                  <div key={idx} className={`rounded-lg p-4 border ${
                    safeString(rec.priority) === 'Critical' ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700' :
                    safeString(rec.priority) === 'High' ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700' :
                    safeString(rec.priority) === 'Medium' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700' :
                    'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{safeString(rec.category)}</span>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        safeString(rec.priority) === 'Critical' ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200' :
                        safeString(rec.priority) === 'High' ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200' :
                        safeString(rec.priority) === 'Medium' ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200' :
                        'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                      }`}>
                        {safeString(rec.priority)}
                      </span>
                    </div>
                    {rec.recommendation && (
                      <div className="text-gray-700 dark:text-gray-300 mb-2">
                        {renderSafeContent(rec.recommendation, `Recommendation ${idx + 1}`, hideCitations)}
                      </div>
                    )}
                    {rec.rationale && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <span className="font-medium">Rationale:</span> {renderSafeContent(rec.rationale, `Rationale ${idx + 1}`, hideCitations)}
                      </div>
                    )}
                    {rec.expectedBenefit && (
                      <div className="text-sm text-green-600 dark:text-green-400">
                        <span className="font-medium">Expected Benefit:</span> {renderSafeContent(rec.expectedBenefit, `Expected Benefit ${idx + 1}`, hideCitations)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Blind Spots */}
          {(data as any).blindSpots && (
            <section id="blind-spots" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-400 pb-2">
                Blind Spots & Limitations
              </h2>
              <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
                {(data as any).blindSpots.missingPolicies && Array.isArray((data as any).blindSpots.missingPolicies) && (data as any).blindSpots.missingPolicies.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Missing Policies</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                      {(data as any).blindSpots.missingPolicies.map((policy: string, idx: number) => (
                        <li key={idx}>{renderSafeContent(policy, `Missing Policy ${idx + 1}`, hideCitations)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(data as any).blindSpots.unknownClaimsHistory && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Unknown Claims History</h4>
                    <p className="text-gray-600 dark:text-gray-400">{renderSafeContent((data as any).blindSpots.unknownClaimsHistory, 'Unknown Claims History', hideCitations)}</p>
                  </div>
                )}
                {(data as any).blindSpots.carrierCoordinationUncertainty && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Carrier Coordination Uncertainty</h4>
                    <p className="text-gray-600 dark:text-gray-400">{renderSafeContent((data as any).blindSpots.carrierCoordinationUncertainty, 'Carrier Coordination Uncertainty', hideCitations)}</p>
                  </div>
                )}
                {(data as any).blindSpots.otherLimitations && Array.isArray((data as any).blindSpots.otherLimitations) && (data as any).blindSpots.otherLimitations.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Other Limitations</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                      {(data as any).blindSpots.otherLimitations.map((limitation: string, idx: number) => (
                        <li key={idx}>{renderSafeContent(limitation, `Limitation ${idx + 1}`, hideCitations)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {/* ===== HOMEOWNERS COMPARISON / PERSONAL UMBRELLA SECTIONS ===== */}
      {(data.responseMetadata?.workflowType === 'homeowners_comparison' || data.responseMetadata?.workflowType === 'personal_umbrella' || (data as any).baselineExtraction || (data as any).gapSynthesis) && (data as any).documentClassification && (
        <>
          {/* Analysis Header */}
          {(data as any).analysisHeader && (
            <section id="analysis-header" className="mb-10">
              <h2 className={`text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 pb-2 ${data.responseMetadata?.workflowType === 'personal_umbrella' ? 'border-indigo-400' : 'border-teal-400'}`}>
                Analysis Header
              </h2>
              <div className={`p-4 rounded-lg border ${data.responseMetadata?.workflowType === 'personal_umbrella' ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700' : 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700'}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {(data as any).analysisHeader.namedInsured && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Named Insured:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.namedInsured}</span></div>
                  )}
                  {(data as any).analysisHeader.propertyAddress && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Property Address:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.propertyAddress}</span></div>
                  )}
                  {(data as any).analysisHeader.analysisDate && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Analysis Date:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.analysisDate}</span></div>
                  )}
                  {(data as any).analysisHeader.track && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Analysis Track:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.track}</span></div>
                  )}
                  {(data as any).analysisHeader.mode && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Mode:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.mode}</span></div>
                  )}
                  {(data as any).analysisHeader.umbrellaType && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Umbrella Type:</span> <span className={`px-2 py-0.5 rounded text-xs font-medium ${(data as any).analysisHeader.umbrellaType?.toLowerCase().includes('true') ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : (data as any).analysisHeader.umbrellaType?.toLowerCase().includes('excess') ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>{(data as any).analysisHeader.umbrellaType}</span></div>
                  )}
                  {(data as any).analysisHeader.complexityTier && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Complexity Tier:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.complexityTier}</span></div>
                  )}
                  {(data as any).analysisHeader.activeExposureCategories && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Active Exposure Categories:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.activeExposureCategories}</span></div>
                  )}
                  {(data as any).analysisHeader.geographicRiskProfile && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Geographic Risk Profile:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.geographicRiskProfile}</span></div>
                  )}
                  {(data as any).analysisHeader.documentsAnalyzed && (
                    <div className="md:col-span-2"><span className="font-semibold text-gray-700 dark:text-gray-300">Documents:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).analysisHeader.documentsAnalyzed.join(', ')}</span></div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Mode Rationale */}
          {(data as any).modeRationale && (
            <section id="mode-rationale" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Mode Rationale
              </h2>
              <div className="prose dark:prose-invert max-w-none">
                {renderSafeContent((data as any).modeRationale, 'Mode Rationale', hideCitations)}
              </div>
            </section>
          )}

          {/* Document Classification */}
          {(data as any).documentClassification && Array.isArray((data as any).documentClassification) && (
            <section id="document-classification" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Document Classification
              </h2>
              <div className="space-y-4">
                {(data as any).documentClassification.map((doc: any, idx: number) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200">{safeString(doc.fileName)}</h4>
                      {doc.isUmbrella && <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">Umbrella</span>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {doc.documentType && <div><span className="font-medium text-gray-600 dark:text-gray-400">Type:</span> {safeString(doc.documentType)}</div>}
                      {doc.carrierName && <div><span className="font-medium text-gray-600 dark:text-gray-400">Carrier:</span> {safeString(doc.carrierName)}</div>}
                      {doc.formType && <div><span className="font-medium text-gray-600 dark:text-gray-400">Form:</span> {safeString(doc.formType)}</div>}
                      {doc.effectiveDate && <div><span className="font-medium text-gray-600 dark:text-gray-400">Effective:</span> {safeString(doc.effectiveDate)}</div>}
                      {doc.expirationDate && <div><span className="font-medium text-gray-600 dark:text-gray-400">Expiration:</span> {safeString(doc.expirationDate)}</div>}
                      {doc.keyDates && <div><span className="font-medium text-gray-600 dark:text-gray-400">Key Dates:</span> {safeString(doc.keyDates)}</div>}
                      {doc.namedInsured && <div><span className="font-medium text-gray-600 dark:text-gray-400">Insured:</span> {safeString(doc.namedInsured)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Temporal Verification */}
          {(data as any).temporalVerification && (
            <section id="temporal-verification" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Temporal Verification
              </h2>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="mb-2"><span className="font-semibold text-gray-700 dark:text-gray-300">Status:</span> <span className={`ml-2 px-2 py-0.5 rounded text-sm ${(data as any).temporalVerification.status === 'complete' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`}>{safeString((data as any).temporalVerification.status)}</span></div>
                <div className="prose dark:prose-invert max-w-none text-sm mt-2">
                  {renderSafeContent((data as any).temporalVerification.dateSequence, 'Date Sequence', hideCitations)}
                </div>
                {(data as any).temporalVerification.conflicts && (data as any).temporalVerification.conflicts.length > 0 && (
                  <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded">
                    <span className="font-medium text-red-700 dark:text-red-300">Conflicts: </span>
                    {(data as any).temporalVerification.conflicts.map((c: string, i: number) => <span key={i} className="text-red-600 dark:text-red-400">{c}{i < (data as any).temporalVerification.conflicts.length - 1 ? ', ' : ''}</span>)}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Policy Architecture (Personal Umbrella) */}
          {(data as any).policyArchitecture && (
            <section id="policy-architecture" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Policy Architecture
              </h2>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {(data as any).policyArchitecture.policyType && (
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Policy Type:</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${(data as any).policyArchitecture.policyType.toLowerCase().includes('true umbrella') ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : (data as any).policyArchitecture.policyType.toLowerCase().includes('excess') ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>{safeString((data as any).policyArchitecture.policyType)}</span>
                    </div>
                  )}
                  {(data as any).policyArchitecture.defenseCostTreatment && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Defense Cost Treatment:</span> <span className="text-gray-600 dark:text-gray-400">{safeString((data as any).policyArchitecture.defenseCostTreatment)}</span></div>
                  )}
                  {(data as any).policyArchitecture.sirAmount && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Self-Insured Retention:</span> <span className="text-gray-600 dark:text-gray-400">{safeString((data as any).policyArchitecture.sirAmount)}</span></div>
                  )}
                  {(data as any).policyArchitecture.coverageTerritory && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Coverage Territory:</span> <span className="text-gray-600 dark:text-gray-400">{safeString((data as any).policyArchitecture.coverageTerritory)}</span></div>
                  )}
                  {(data as any).policyArchitecture.dropDownProvisions && (
                    <div className="md:col-span-2"><span className="font-semibold text-gray-700 dark:text-gray-300">Drop-Down Provisions:</span> <span className="text-gray-600 dark:text-gray-400">{safeString((data as any).policyArchitecture.dropDownProvisions)}</span></div>
                  )}
                  {(data as any).policyArchitecture.insuredDefinition && (
                    <div className="md:col-span-2"><span className="font-semibold text-gray-700 dark:text-gray-300">Insured Definition:</span> <span className="text-gray-600 dark:text-gray-400">{safeString((data as any).policyArchitecture.insuredDefinition)}</span></div>
                  )}
                  {(data as any).policyArchitecture.scheduleOfForms && (
                    <div className="md:col-span-2"><span className="font-semibold text-gray-700 dark:text-gray-300">Schedule of Forms:</span> <span className="text-gray-600 dark:text-gray-400">{safeString((data as any).policyArchitecture.scheduleOfForms)}</span></div>
                  )}
                  {(data as any).policyArchitecture.totalEndorsements != null && (
                    <div><span className="font-semibold text-gray-700 dark:text-gray-300">Total Endorsements:</span> <span className="text-gray-600 dark:text-gray-400">{(data as any).policyArchitecture.totalEndorsements}</span></div>
                  )}
                </div>
                {(data as any).policyArchitecture.typeEvidence && (
                  <div className="mt-3 prose dark:prose-invert max-w-none text-sm">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Type Evidence:</span>
                    <div className="mt-1">{renderSafeContent((data as any).policyArchitecture.typeEvidence, 'Type Evidence', hideCitations)}</div>
                  </div>
                )}
              </div>

              {/* MUI Schedule */}
              {(data as any).policyArchitecture.muiSchedule && (
                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-700">
                  <h3 className="font-semibold text-indigo-800 dark:text-indigo-200 mb-3">Minimum Underlying Insurance (MUI) Schedule</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {(data as any).policyArchitecture.muiSchedule.homeowners && (
                      <div><span className="font-medium text-gray-600 dark:text-gray-400">Homeowners:</span> <span className="text-gray-800 dark:text-gray-200">{safeString((data as any).policyArchitecture.muiSchedule.homeowners)}</span></div>
                    )}
                    {(data as any).policyArchitecture.muiSchedule.auto && (
                      <div><span className="font-medium text-gray-600 dark:text-gray-400">Auto:</span> <span className="text-gray-800 dark:text-gray-200">{safeString((data as any).policyArchitecture.muiSchedule.auto)}</span></div>
                    )}
                    {(data as any).policyArchitecture.muiSchedule.watercraft && (
                      <div><span className="font-medium text-gray-600 dark:text-gray-400">Watercraft:</span> <span className="text-gray-800 dark:text-gray-200">{safeString((data as any).policyArchitecture.muiSchedule.watercraft)}</span></div>
                    )}
                    {(data as any).policyArchitecture.muiSchedule.other && (
                      <div><span className="font-medium text-gray-600 dark:text-gray-400">Other:</span> <span className="text-gray-800 dark:text-gray-200">{safeString((data as any).policyArchitecture.muiSchedule.other)}</span></div>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Schedule of Forms */}
          {(data as any).scheduleOfForms && Array.isArray((data as any).scheduleOfForms) && (data as any).scheduleOfForms.length > 0 && (
            <section id="schedule-of-forms" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Schedule of Forms
              </h2>
              <div className="space-y-4">
                {(data as any).scheduleOfForms.map((doc: any, idx: number) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">{safeString(doc.carrierName)} ({safeString(doc.docId)})</h4>
                    {doc.baseForm && <div className="text-sm mb-2"><span className="font-medium">Base Form:</span> {safeString(doc.baseForm)}</div>}
                    {doc.endorsements && doc.endorsements.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm font-medium">Endorsements ({doc.totalEndorsements || doc.endorsements.length}):</span>
                        <div className="mt-1 space-y-1">
                          {doc.endorsements.map((e: any, eIdx: number) => (
                            <div key={eIdx} className="text-sm flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${e.classification === 'coverage-expanding' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : e.classification === 'coverage-restricting' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                                {safeString(e.classification)}
                              </span>
                              <span>{safeString(e.formNumber)}</span>
                              {e.description && <span className="text-gray-500 dark:text-gray-400">— {safeString(e.description)}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {doc.expectedButMissing && doc.expectedButMissing.length > 0 && (
                      <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm">
                        <span className="font-medium text-yellow-700 dark:text-yellow-300">Expected but Missing: </span>
                        <span className="text-yellow-600 dark:text-yellow-400">{doc.expectedButMissing.join(', ')}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Baseline Extraction */}
          {(data as any).baselineExtraction && Array.isArray((data as any).baselineExtraction) && (
            <section id="baseline-extraction" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Baseline Coverage Extraction
              </h2>
              <div className="space-y-6">
                {(data as any).baselineExtraction.map((doc: any, idx: number) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200">{safeString(doc.carrierName)} ({safeString(doc.docId)})</h4>
                      {doc.isUmbrella && <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">Umbrella</span>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {/* Umbrella-specific fields */}
                      {doc.perOccurrenceLimit && <div><span className="font-medium text-gray-600 dark:text-gray-400">Per Occurrence Limit:</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.perOccurrenceLimit)}</span></div>}
                      {doc.aggregateLimit && <div><span className="font-medium text-gray-600 dark:text-gray-400">Aggregate Limit:</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.aggregateLimit)}</span></div>}
                      {doc.sir && <div><span className="font-medium text-gray-600 dark:text-gray-400">SIR:</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.sir)}</span></div>}
                      {doc.policyType && <div><span className="font-medium text-gray-600 dark:text-gray-400">Policy Type:</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.policyType)}</span></div>}
                      {doc.muiMet && <div><span className="font-medium text-gray-600 dark:text-gray-400">MUI Met:</span> <span className={`px-1.5 py-0.5 rounded text-xs ${doc.muiMet?.toLowerCase() === 'yes' ? 'bg-green-100 text-green-700' : doc.muiMet?.toLowerCase() === 'no' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{safeString(doc.muiMet)}</span></div>}
                      {doc.muiShortfall && <div><span className="font-medium text-gray-600 dark:text-gray-400">MUI Shortfall:</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.muiShortfall)}</span></div>}
                      {/* Homeowners-specific fields */}
                      {doc.coverageA && <div><span className="font-medium text-gray-600 dark:text-gray-400">Coverage A (Dwelling):</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.coverageA)}</span></div>}
                      {doc.coverageB && <div><span className="font-medium text-gray-600 dark:text-gray-400">Coverage B (Other Structures):</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.coverageB)}</span></div>}
                      {doc.coverageC && <div><span className="font-medium text-gray-600 dark:text-gray-400">Coverage C (Personal Property):</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.coverageC)}</span></div>}
                      {doc.coverageD && <div><span className="font-medium text-gray-600 dark:text-gray-400">Coverage D (Loss of Use):</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.coverageD)}</span></div>}
                      {doc.coverageE && <div><span className="font-medium text-gray-600 dark:text-gray-400">Coverage E (Liability):</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.coverageE)}</span></div>}
                      {doc.coverageF && <div><span className="font-medium text-gray-600 dark:text-gray-400">Coverage F (Medical):</span> <span className="text-gray-800 dark:text-gray-200">{safeString(doc.coverageF)}</span></div>}
                      {doc.deductibleAOP && <div><span className="font-medium text-gray-600 dark:text-gray-400">Deductible (AOP):</span> <span className="text-gray-800 dark:text-gray-200">{renderSafeContent(doc.deductibleAOP, 'Deductible AOP', hideCitations)}</span></div>}
                      {doc.deductibleWind && <div><span className="font-medium text-gray-600 dark:text-gray-400">Deductible (Wind):</span> <span className="text-gray-800 dark:text-gray-200">{renderSafeContent(doc.deductibleWind, 'Deductible Wind', hideCitations)}</span></div>}
                      {doc.deductibleNamedStorm && <div><span className="font-medium text-gray-600 dark:text-gray-400">Named Storm:</span> <span className="text-gray-800 dark:text-gray-200">{renderSafeContent(doc.deductibleNamedStorm, 'Named Storm', hideCitations)}</span></div>}
                      {/* Shared fields */}
                      {doc.annualPremium && <div><span className="font-medium text-gray-600 dark:text-gray-400">Annual Premium:</span> <span className="text-gray-800 dark:text-gray-200 font-semibold">{renderSafeContent(doc.annualPremium, 'Annual Premium', hideCitations)}</span></div>}
                      {doc.settlementBasis && <div><span className="font-medium text-gray-600 dark:text-gray-400">Settlement Basis:</span> <span className="text-gray-800 dark:text-gray-200">{renderSafeContent(doc.settlementBasis, 'Settlement Basis', hideCitations)}</span></div>}
                    </div>
                    {doc.exclusions && doc.exclusions.length > 0 && (
                      <div className="mt-2 text-sm">
                        <span className="font-medium text-gray-600 dark:text-gray-400">Exclusions:</span>
                        <ul className="mt-1 list-disc list-inside text-gray-600 dark:text-gray-400">
                          {doc.exclusions.map((e: string, eIdx: number) => <li key={eIdx}>{safeString(e)}</li>)}
                        </ul>
                      </div>
                    )}
                    {doc.keyFindings && (
                      <div className="mt-2 text-sm prose dark:prose-invert max-w-none">
                        <span className="font-medium text-gray-600 dark:text-gray-400">Key Findings:</span>
                        <div className="mt-1">{renderSafeContent(doc.keyFindings, `Key Findings ${idx}`, hideCitations)}</div>
                      </div>
                    )}
                    {doc.specialLimits && <div className="mt-2 text-sm"><span className="font-medium text-gray-600 dark:text-gray-400">Special Limits:</span> <span className="text-gray-800 dark:text-gray-200">{renderSafeContent(doc.specialLimits, 'Special Limits', hideCitations)}</span></div>}
                    {doc.keyEndorsements && <div className="mt-2 text-sm"><span className="font-medium text-gray-600 dark:text-gray-400">Key Endorsements:</span> <span className="text-gray-800 dark:text-gray-200">{renderSafeContent(doc.keyEndorsements, 'Key Endorsements', hideCitations)}</span></div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Exposure Profile */}
          {(data as any).exposureProfile && (
            <section id="exposure-profile" className="mb-10">
              <h2 className={`text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 pb-2 ${data.responseMetadata?.workflowType === 'personal_umbrella' ? 'border-indigo-400' : 'border-teal-400'}`}>
                Exposure Profile
              </h2>
              <div className="space-y-4">
                {/* Umbrella: track status and active categories */}
                {(data as any).exposureProfile.trackStatus && (
                  <div className="text-sm mb-2">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Track Status:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${(data as any).exposureProfile.trackStatus.toLowerCase().includes('track 1+2') ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>{safeString((data as any).exposureProfile.trackStatus)}</span>
                    {(data as any).exposureProfile.totalActiveCategories != null && (
                      <span className="ml-3 text-gray-500">({(data as any).exposureProfile.totalActiveCategories} active categories)</span>
                    )}
                  </div>
                )}
                {(data as any).exposureProfile.familyComposition && (
                  <div className="text-sm"><span className="font-semibold text-gray-700 dark:text-gray-300">Family Composition:</span> <span className="text-gray-600 dark:text-gray-400">{safeString((data as any).exposureProfile.familyComposition)}</span></div>
                )}
                {(data as any).exposureProfile.jurisdictionalModifiers && (
                  <div className="text-sm"><span className="font-semibold text-gray-700 dark:text-gray-300">Jurisdictional Modifiers:</span> <span className="text-gray-600 dark:text-gray-400">{safeString((data as any).exposureProfile.jurisdictionalModifiers)}</span></div>
                )}
                {(data as any).exposureProfile.activeCategories && (data as any).exposureProfile.activeCategories.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700">
                      <thead className="bg-indigo-50 dark:bg-indigo-900/30">
                        <tr>
                          <th className="px-4 py-2 text-left border-b">Exposure Category</th>
                          <th className="px-4 py-2 text-left border-b">Activity Level</th>
                          <th className="px-4 py-2 text-left border-b">Underlying Policy</th>
                          <th className="px-4 py-2 text-center border-b">MUI Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data as any).exposureProfile.activeCategories.map((cat: any, idx: number) => (
                          <tr key={idx} className="border-b border-gray-200 dark:border-gray-700">
                            <td className="px-4 py-2 font-medium">{safeString(cat.category)}</td>
                            <td className="px-4 py-2">{safeString(cat.activityLevel) || '—'}</td>
                            <td className="px-4 py-2">{safeString(cat.underlyingPolicy) || '—'}</td>
                            <td className="px-4 py-2 text-center">
                              {cat.muiStatus && <span className={`px-2 py-0.5 rounded text-xs ${cat.muiStatus?.toLowerCase().includes('met') ? 'bg-green-100 text-green-700' : cat.muiStatus?.toLowerCase().includes('short') ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{safeString(cat.muiStatus)}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Homeowners: property details, geographic risks, flags */}
                {(data as any).exposureProfile.propertyDetails && (
                  <div className="prose dark:prose-invert max-w-none text-sm">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300">Property Details</h4>
                    {renderSafeContent((data as any).exposureProfile.propertyDetails, 'Property Details', hideCitations)}
                  </div>
                )}
                {(data as any).exposureProfile.geographicRisks && (
                  <div className="prose dark:prose-invert max-w-none text-sm">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300">Geographic Risks</h4>
                    {renderSafeContent((data as any).exposureProfile.geographicRisks, 'Geographic Risks', hideCitations)}
                  </div>
                )}
                {(data as any).exposureProfile.probabilisticFlags && (data as any).exposureProfile.probabilisticFlags.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Risk Flags</h4>
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {(data as any).exposureProfile.probabilisticFlags.map((flag: string, i: number) => <li key={i}>{renderSafeContent(flag, `Flag ${i}`, hideCitations)}</li>)}
                    </ul>
                  </div>
                )}
                {(data as any).exposureProfile.complexityTierAssignment && (
                  <div className="prose dark:prose-invert max-w-none text-sm">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300">Complexity Tier</h4>
                    {renderSafeContent((data as any).exposureProfile.complexityTierAssignment, 'Complexity Tier', hideCitations)}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Drop-Down Matrix (Personal Umbrella) */}
          {(data as any).dropDownMatrix && (
            <section id="drop-down-matrix" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-indigo-400 pb-2">
                Drop-Down Coverage Matrix
              </h2>
              {(data as any).dropDownMatrix.trackStatus && (
                <div className="text-sm mb-3">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Status:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{safeString((data as any).dropDownMatrix.trackStatus)}</span>
                </div>
              )}
              {(data as any).dropDownMatrix.entries && (data as any).dropDownMatrix.entries.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700">
                    <thead className="bg-indigo-50 dark:bg-indigo-900/30">
                      <tr>
                        <th className="px-3 py-2 text-left border-b">Exposure Category</th>
                        <th className="px-3 py-2 text-left border-b">Underlying Status</th>
                        <th className="px-3 py-2 text-left border-b">Umbrella Treatment</th>
                        <th className="px-3 py-2 text-left border-b">Matrix Outcome</th>
                        <th className="px-3 py-2 text-center border-b">Gap Tier</th>
                        <th className="px-3 py-2 text-left border-b">Quantified Exposure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data as any).dropDownMatrix.entries.map((entry: any, idx: number) => (
                        <tr key={idx} className={`border-b border-gray-200 dark:border-gray-700 ${entry.gapTier?.includes('1') || entry.gapTier?.toLowerCase().includes('critical') ? 'bg-red-50/50 dark:bg-red-900/10' : entry.gapTier?.includes('2') || entry.gapTier?.toLowerCase().includes('significant') ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''}`}>
                          <td className="px-3 py-2 font-medium">{safeString(entry.category)}</td>
                          <td className="px-3 py-2">{safeString(entry.underlyingStatus)}</td>
                          <td className="px-3 py-2">{safeString(entry.umbrellaTreatment)}</td>
                          <td className="px-3 py-2">{safeString(entry.matrixOutcome)}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${entry.gapTier?.includes('1') || entry.gapTier?.toLowerCase().includes('critical') ? 'bg-red-200 text-red-800' : entry.gapTier?.includes('2') || entry.gapTier?.toLowerCase().includes('significant') ? 'bg-orange-200 text-orange-800' : entry.gapTier?.includes('3') || entry.gapTier?.toLowerCase().includes('moderate') ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'}`}>{safeString(entry.gapTier)}</span>
                          </td>
                          <td className="px-3 py-2">{safeString(entry.quantifiedExposure) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* Coverage Analysis */}
          {(data as any).coverageAnalysis && (data.responseMetadata?.workflowType === 'homeowners_comparison' || data.responseMetadata?.workflowType === 'personal_umbrella') && (
            <section id="coverage-analysis-ho" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Coverage Analysis ({safeString((data as any).coverageAnalysis.mode)})
              </h2>
              {/* Umbrella-specific narrative sections */}
              {(data as any).coverageAnalysis.structuralAssessment && (
                <div className="mb-4 prose dark:prose-invert max-w-none text-sm">
                  <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-300">Structural Assessment</h3>
                  {renderSafeContent((data as any).coverageAnalysis.structuralAssessment, 'Structural Assessment', hideCitations)}
                </div>
              )}
              {(data as any).coverageAnalysis.exclusionMapping && (
                <div className="mb-4 prose dark:prose-invert max-w-none text-sm">
                  <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-300">Exclusion Mapping</h3>
                  {renderSafeContent((data as any).coverageAnalysis.exclusionMapping, 'Exclusion Mapping', hideCitations)}
                </div>
              )}
              {(data as any).coverageAnalysis.muiCompliance && (
                <div className="mb-4 prose dark:prose-invert max-w-none text-sm">
                  <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-300">MUI Compliance</h3>
                  {renderSafeContent((data as any).coverageAnalysis.muiCompliance, 'MUI Compliance', hideCitations)}
                </div>
              )}
              {(data as any).coverageAnalysis.comparison && (data as any).coverageAnalysis.comparison.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">Side-by-Side Comparison</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700">
                      <thead className="bg-teal-50 dark:bg-teal-900/30">
                        <tr>
                          <th className="px-4 py-2 text-left border-b">Element</th>
                          <th className="px-4 py-2 text-left border-b">Details</th>
                          <th className="px-4 py-2 text-center border-b">Equivalent</th>
                          <th className="px-4 py-2 text-left border-b">Superior Option</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data as any).coverageAnalysis.comparison.map((row: any, idx: number) => (
                          <tr key={idx} className="border-b border-gray-200 dark:border-gray-700">
                            <td className="px-4 py-2 font-medium">{safeString(row.element)}</td>
                            <td className="px-4 py-2">{renderSafeContent(row.details, `Comparison ${idx}`, hideCitations)}</td>
                            <td className="px-4 py-2 text-center">{row.equivalent ? '✓' : '✗'}</td>
                            <td className="px-4 py-2">{safeString(row.superiorOption) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(data as any).coverageAnalysis.changeDetection && (data as any).coverageAnalysis.changeDetection.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">Change Detection</h3>
                  <div className="space-y-2">
                    {(data as any).coverageAnalysis.changeDetection.map((change: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${change.changeType === 'Improved' ? 'bg-green-100 text-green-700' : change.changeType === 'Degraded' || change.changeType === 'Removed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{safeString(change.changeType)}</span>
                        <div className="flex-1">
                          <span className="font-medium">{safeString(change.element)}</span>
                          {change.analysis && <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{renderSafeContent(change.analysis, `Change ${idx}`, hideCitations)}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(data as any).coverageAnalysis.adequacyAssessment && (data as any).coverageAnalysis.adequacyAssessment.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">Adequacy Assessment</h3>
                  <div className="space-y-2">
                    {(data as any).coverageAnalysis.adequacyAssessment.map((item: any, idx: number) => (
                      <div key={idx} className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{safeString(item.exposure)}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${item.covered === 'Yes' ? 'bg-green-100 text-green-700' : item.covered === 'No' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{safeString(item.covered)}</span>
                          {item.gapTier && <span className="text-xs text-gray-500">{safeString(item.gapTier)}</span>}
                        </div>
                        {item.adequacy && <div className="text-sm text-gray-600 dark:text-gray-400">{renderSafeContent(item.adequacy, `Adequacy ${idx}`, hideCitations)}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Compound Failures */}
          {(data as any).compoundFailures && (
            <section id="compound-failures" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Compound Failures
              </h2>
              {!(data as any).compoundFailures.detected ? (
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700">
                  <span className="text-green-700 dark:text-green-300 font-medium">No compound coverage failures detected.</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Homeowners schema: failures array */}
                  {(data as any).compoundFailures.failures?.map((f: any, idx: number) => (
                    <div key={idx} className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                      <div className="font-semibold text-red-800 dark:text-red-200 mb-1">{safeString(f.pattern)} <span className="text-sm font-normal">({safeString(f.tier)})</span></div>
                      <div className="text-sm text-red-700 dark:text-red-300">{renderSafeContent(f.combinedEffect, `Compound ${idx}`, hideCitations)}</div>
                      {f.quantifiedImpact && <div className="text-sm mt-1 text-red-600 dark:text-red-400">Impact: {safeString(f.quantifiedImpact)}</div>}
                    </div>
                  ))}
                  {/* Umbrella schema: patternResults array */}
                  {(data as any).compoundFailures.patternResults?.map((p: any, idx: number) => (
                    <div key={idx} className={`p-4 rounded-lg border ${p.detected ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold ${p.detected ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'}`}>
                          Pattern {p.patternNumber}: {safeString(p.patternName)}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.detected ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>{p.detected ? 'Detected' : 'Not Detected'}</span>
                        {p.tier && <span className="text-xs text-gray-500">({safeString(p.tier)})</span>}
                      </div>
                      {p.combinedEffect && <div className={`text-sm ${p.detected ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>{safeString(p.combinedEffect)}</div>}
                      {p.quantifiedImpact && <div className="text-sm mt-1 text-red-600 dark:text-red-400">Impact: {safeString(p.quantifiedImpact)}</div>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Appearance of Coverage */}
          {(data as any).appearanceOfCoverage && (
            <section id="appearance-of-coverage" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Appearance of Coverage
              </h2>
              {!(data as any).appearanceOfCoverage.detected ? (
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700">
                  <span className="text-green-700 dark:text-green-300 font-medium">No illusory coverage situations detected.</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {(data as any).appearanceOfCoverage.alerts?.map((a: any, idx: number) => (
                    <div key={idx} className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-700">
                      <div className="font-semibold text-orange-800 dark:text-orange-200 mb-2">{safeString(a.signal)}</div>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                        <div><span className="font-medium">Perceived:</span> {safeString(a.perceivedCoverage)}</div>
                        <div><span className="font-medium">Actual:</span> {safeString(a.actualCoverage)}</div>
                      </div>
                      <div className="text-sm text-orange-700 dark:text-orange-300">{renderSafeContent(a.analysis, `Alert ${idx}`, hideCitations)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Premium Analysis */}
          {(data as any).premiumAnalysis && (
            <section id="premium-analysis-ho" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Premium Analysis
              </h2>
              {(data as any).premiumAnalysis.premiums && (data as any).premiumAnalysis.premiums.length > 0 && (
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700">
                    <thead className={`${data.responseMetadata?.workflowType === 'personal_umbrella' ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'bg-teal-50 dark:bg-teal-900/30'}`}>
                      <tr>
                        <th className="px-4 py-2 text-left border-b">Carrier</th>
                        <th className="px-4 py-2 text-right border-b">Annual Premium</th>
                        {data.responseMetadata?.workflowType === 'personal_umbrella' ? (
                          <>
                            <th className="px-4 py-2 text-right border-b">Per $1M</th>
                            <th className="px-4 py-2 text-right border-b">Exclusion-Adj Value</th>
                          </>
                        ) : (
                          <>
                            <th className="px-4 py-2 text-right border-b">Total Cost</th>
                            <th className="px-4 py-2 text-right border-b">Per $1K Cov A</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {(data as any).premiumAnalysis.premiums.map((p: any, idx: number) => (
                        <tr key={idx} className="border-b border-gray-200 dark:border-gray-700">
                          <td className="px-4 py-2 font-medium">{safeString(p.carrierName)}</td>
                          <td className="px-4 py-2 text-right">{safeString(p.annualPremium)}</td>
                          {data.responseMetadata?.workflowType === 'personal_umbrella' ? (
                            <>
                              <td className="px-4 py-2 text-right">{safeString(p.premiumPerMillion) || '—'}</td>
                              <td className="px-4 py-2 text-right">{safeString(p.exclusionAdjustedValue) || '—'}</td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-2 text-right">{safeString(p.totalAnnualCost) || '—'}</td>
                              <td className="px-4 py-2 text-right">{safeString(p.premiumPerThousandCovA) || '—'}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(data as any).premiumAnalysis.sirImpactAnalysis && (
                <div className="mb-4 prose dark:prose-invert max-w-none text-sm">
                  <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-300">SIR Impact Analysis</h3>
                  {renderSafeContent((data as any).premiumAnalysis.sirImpactAnalysis, 'SIR Impact', hideCitations)}
                </div>
              )}
              {(data as any).premiumAnalysis.premiumChangeAnalysis && (
                <div className="prose dark:prose-invert max-w-none text-sm mb-3">
                  {renderSafeContent((data as any).premiumAnalysis.premiumChangeAnalysis, 'Premium Change Analysis', hideCitations)}
                </div>
              )}
              {(data as any).premiumAnalysis.valueAssessment && (
                <div className="prose dark:prose-invert max-w-none text-sm">
                  {renderSafeContent((data as any).premiumAnalysis.valueAssessment, 'Value Assessment', hideCitations)}
                </div>
              )}
            </section>
          )}

          {/* Gap Synthesis */}
          {(data as any).gapSynthesis && (
            <section id="gap-synthesis" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Gap Synthesis ({(data as any).gapSynthesis.totalGaps || 0} gaps identified)
              </h2>
              {(data as any).gapSynthesis.documentDepthNotice && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700 text-sm text-blue-700 dark:text-blue-300">
                  {safeString((data as any).gapSynthesis.documentDepthNotice)}
                </div>
              )}
              {(data as any).gapSynthesis.gaps && (data as any).gapSynthesis.gaps.length > 0 ? (
                <div className="space-y-3">
                  {(data as any).gapSynthesis.gaps.map((gap: any, idx: number) => (
                    <div key={idx} className={`p-4 rounded-lg border ${gap.tier?.includes('1') || gap.tier?.includes('Critical') ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' : gap.tier?.includes('2') || gap.tier?.includes('Significant') ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700' : gap.tier?.includes('3') || gap.tier?.includes('Moderate') ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">{safeString(gap.gapId)}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${gap.tier?.includes('1') || gap.tier?.includes('Critical') ? 'bg-red-200 text-red-800' : gap.tier?.includes('2') || gap.tier?.includes('Significant') ? 'bg-orange-200 text-orange-800' : gap.tier?.includes('3') || gap.tier?.includes('Moderate') ? 'bg-yellow-200 text-yellow-800' : 'bg-gray-200 text-gray-700'}`}>{safeString(gap.tier)}</span>
                        {gap.gapType && <span className="text-xs text-gray-500">{safeString(gap.gapType)}</span>}
                        {gap.isCompound && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Compound</span>}
                      </div>
                      <div className="text-sm">{renderSafeContent(gap.description, `Gap ${idx}`, hideCitations)}</div>
                      {gap.quantifiedExposure && <div className="text-sm mt-1 font-medium">Exposure: {safeString(gap.quantifiedExposure)}</div>}
                      {gap.remediation && <div className="text-sm mt-1 text-gray-600 dark:text-gray-400">Remediation: {renderSafeContent(gap.remediation, `Remediation ${idx}`, hideCitations)}</div>}
                      {gap.remediationPath && <div className="text-sm mt-1 text-gray-600 dark:text-gray-400">Remediation Path: {safeString(gap.remediationPath)}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="prose dark:prose-invert max-w-none text-sm">
                  {renderSafeContent((data as any).gapSynthesis, 'Gap Synthesis', hideCitations)}
                </div>
              )}
              {/* Separate Coverage Recommendations (Personal Umbrella) */}
              {(data as any).gapSynthesis.separateCoverageRecommendations && (data as any).gapSynthesis.separateCoverageRecommendations.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">Separate Coverage Recommendations</h3>
                  <div className="space-y-2">
                    {(data as any).gapSynthesis.separateCoverageRecommendations.map((rec: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded border border-indigo-200 dark:border-indigo-700 text-sm">
                        <div className="flex-1">
                          <div className="font-medium text-gray-800 dark:text-gray-200">{safeString(rec.exposure)}</div>
                          <div className="text-gray-600 dark:text-gray-400 mt-0.5">{safeString(rec.recommendedCoverage)}</div>
                        </div>
                        {rec.urgency && <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium ${rec.urgency?.toLowerCase().includes('high') || rec.urgency?.toLowerCase().includes('immediate') ? 'bg-red-100 text-red-700' : rec.urgency?.toLowerCase().includes('medium') ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{safeString(rec.urgency)}</span>}
                        {rec.estimatedCost && <span className="flex-shrink-0 text-xs text-gray-500">{safeString(rec.estimatedCost)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Decision Framework */}
          {(data as any).decisionFramework && (
            <section id="decision-framework" className="mb-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-teal-400 pb-2">
                Decision Framework
              </h2>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-semibold text-lg">Pathway:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${(data as any).decisionFramework.pathway === 'Approve' ? 'bg-green-100 text-green-800' : (data as any).decisionFramework.pathway === 'Decline' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{safeString((data as any).decisionFramework.pathway)}</span>
                </div>
                <div className="prose dark:prose-invert max-w-none text-sm">
                  {renderSafeContent((data as any).decisionFramework.rationale, 'Rationale', hideCitations)}
                </div>
              </div>
              {(data as any).decisionFramework.remediationItems && (data as any).decisionFramework.remediationItems.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">Remediation Items</h3>
                  <div className="space-y-2">
                    {(data as any).decisionFramework.remediationItems.map((item: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.priority === 'Critical' ? 'bg-red-100 text-red-700' : item.priority === 'High' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>{safeString(item.priority)}</span>
                        <div><span className="font-medium">{safeString(item.gapId)}:</span> {safeString(item.action)} {item.estimatedCostImpact && <span className="text-gray-500">({safeString(item.estimatedCostImpact)})</span>}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(data as any).decisionFramework.recommendation && (
                <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-700">
                  <h3 className="font-semibold text-teal-800 dark:text-teal-200 mb-2">Recommendation</h3>
                  <div className="prose dark:prose-invert max-w-none text-sm">{renderSafeContent((data as any).decisionFramework.recommendation, 'Recommendation', hideCitations)}</div>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {/* Catch-all section for unhandled sections */}
      {Object.keys(data as any)
        .filter(key => ![
          'responseMetadata', 'executiveSummary', 'analysisMatrices', 'keyFindings',
          'strategicConsiderations', 'riskMitigationStrategies', 'riskManagementMatrix',
          'trendAnalysis', 'benchmarkingInsights', 'costBenefitAnalysis', 'dataQualityAssessment',
          'recommendations', 'renewalRecommendations', 'marketAnalysis', 'renewalStrategy',
          'nonStandardProvisions', 'clauseWordingComparison', 'insuranceCoverageRecommendations',
          'policyOverview', 'coverageAnalysisMatrices', 'coverageRecommendations',
          'coverageGapsAndOpportunities', 'scenarioTesting', 'coverageMetrics',
          'analysisSummary', 'coverageSummary', 'summary', 'costBenefitAnalysisExamples',
          'metaAnalysis', 'riskShift', 'tldr', 'complianceTable', 'blindSpotAcknowledgement',
          'professionalDisclaimer', 'lossRunSummary', 'claimsData',
          'detailedClaimsList', 'categoryBreakdown', 'severityBreakdown', 'riskHeatMap',
          'criticalClaims', 'processingLog', 'analysisNarrative', 'recommendations',
          // Simplified loss run schema sections
          'claimsSummary', 'categoryAnalysis', 'severityAnalysis', 'riskAssessment', 'narrative',
          // New structured loss run schema sections
          'crossFootCheck', 'claimLineItems', 'topClaims', 'dataQuality', 'analystNotes',
          // Loss Run Analyzer (forensic) schema sections
          'documentManifest', 'claims', 'validationResults', 'claimDevelopment', 'frequencyAnalysis', 'disclaimer', 'citations',
          // YOY Renewal schema sections
          'strategicActionPlan', 'confidenceStatement', 'velocityAnalysis', 'weakSignals',
          // YOY Renewal opt-in add-on sections (Report Options step)
          'schedulesSection', 'coverageDetailsSection', 'complianceContractualSection', 'reportAddOns',
          // Insurance Requirements v2.0 schema sections
          'complianceMatrix', 'interconnectedAnalysis', 'decisionPathway', 'confidenceAssessment',
          'nytpaoAnalysis', 'stackingAnalysis', 'hardHammerAnalysis',
          // Commercial Lines Executive Renewal schema sections
          'executiveRenewalSummary', 'riskIntelligenceReport', 'confidenceAndLimitations', 'analysisConfidence',
          // Financial Analysis schema sections
          'financialRiskAssessment', 'underwritingRecommendation', 'periodComparison',
          'detailedAnalysis', 'ermRiskMapping',
          // Client Renewal Presentation schema sections
          'coverageHighlights', 'restrictiveEndorsements', 'actionItems', 'redFlags', 'positiveSignals',
          'conversationPoints', 'glossary', 'programOverview', 'premiumAnalysis', 'coverageChanges',
          'companyContext', 'documentInventory',
          // Rate Analysis schema sections
          'analysisTables', 'driverHierarchy', 'weakSignals',
          // Portfolio Analyzer schema sections
          'portfolioStructure', 'scenarioAnalysis', 'coverageAnalysis', 'portfolioSynthesis', 'assessment', 'blindSpots',
          // Homeowners Comparison + Personal Umbrella schema sections
          'analysisHeader', 'documentClassification', 'modeRationale', 'temporalVerification',
          'scheduleOfForms', 'baselineExtraction', 'exposureProfile', 'compoundFailures',
          'appearanceOfCoverage', 'gapSynthesis', 'decisionFramework',
          'policyArchitecture', 'dropDownMatrix'
        ].includes(key) && hasValidContent((data as any)[key]))
        .map(sectionKey => (
          <section key={sectionKey} className="mb-10">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
              {sectionKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            </h2>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded border-l-4 border-yellow-400">
              <div className="text-yellow-700 dark:text-yellow-300 font-medium mb-2">
                Additional Section Found: {sectionKey}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                This section was found in the report but is not explicitly handled by the current renderer.
              </div>
              {renderSafeContent((data as any)[sectionKey], `Additional Section: ${sectionKey}`, hideCitations)}
            </div>
          </section>
        ))}

      {/* YOY Renewal opt-in add-on: Schedules */}
      {(() => {
        // Tolerate every shape Gemini has produced for this field:
        //   reportAddOns.schedules: [ {scheduleType,...}, ... ]      (canonical — current)
        //   reportAddOns.schedules: { schedules: [ {...}, ... ] }    (double-wrap from old schema)
        //   reportAddOns.schedules: [ [ {...}, ... ] ]               (extra-array wrap)
        const raw = (data as any).reportAddOns?.schedules;
        let scheduleList: any[] = [];
        if (Array.isArray(raw)) {
          scheduleList = raw.length > 0 && raw.every((s) => Array.isArray(s)) ? raw.flat() : raw;
        } else if (raw && Array.isArray(raw.schedules)) {
          scheduleList = raw.schedules;
        }
        if (!shouldShowSection('schedules-section') || scheduleList.length === 0) return null;
        return (
        <div {...getSectionDataAttrs('schedules-section')} style={getSectionStyles('schedules-section')}>
          <section id="schedules-section" className="mb-10">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white border-b-2 border-blue-500 pb-2">
              Schedules
            </h2>
            <div className="space-y-8">
              {scheduleList.map((schedule: any, sIdx: number) => {
                // New structured-citation shape: { scheduleType, policyDocName,
                // headers, rows: [{cells: [{column,value,citations}]}] }.
                // Old markdown-string shape (legacy reports): { markdownTable: "..." }.
                const hasStructured =
                  Array.isArray(schedule.headers) && Array.isArray(schedule.rows);
                return (
                  <div key={sIdx} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{schedule.scheduleType}</h3>
                        {/* Schedule-level citations — clickable markers identical to the
                            ones used in analysisMatrices cells. Lets users jump to the
                            schedule's source page in the PDF viewer. */}
                        {Array.isArray(schedule.citations) && schedule.citations.length > 0 && !hideCitations && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {schedule.citations.map((citation: any, ci: number) => (
                              <CitationMarker
                                key={ci}
                                citation={{
                                  docName: citation.docName || citation.docId || 'Document',
                                  docId: citation.docId,
                                  page: citation.page,
                                  confidence: citation.confidence,
                                }}
                                onClick={() => onCitationClick({
                                  type: 'page',
                                  page: citation.page,
                                  section: citation.section,
                                  documentId: citation.docId,
                                  documentName: citation.docName,
                                  confidence: citation.confidence,
                                  text: citation.quote || schedule.scheduleType,
                                  found: true,
                                })}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      {schedule.policyDocName && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Source: {schedule.policyDocName}</div>
                      )}
                    </div>
                    {hasStructured ? (
                      <div className="p-3">
                        {renderMatrix(
                          { title: '', headers: schedule.headers, rows: schedule.rows },
                          sIdx
                        )}
                      </div>
                    ) : (
                      typeof schedule.markdownTable === 'string' && schedule.markdownTable.trim() && (
                        <div className="overflow-x-auto p-3">
                          <MarkdownBlock>{schedule.markdownTable}</MarkdownBlock>
                        </div>
                      )
                    )}
                    {(Array.isArray(schedule.additionsVsPriorPolicy) && schedule.additionsVsPriorPolicy.length > 0) && (
                      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">Added vs prior policy</div>
                        <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
                          {schedule.additionsVsPriorPolicy.map((a: string, i: number) => <li key={i}>{a}</li>)}
                        </ul>
                      </div>
                    )}
                    {(Array.isArray(schedule.removalsVsPriorPolicy) && schedule.removalsVsPriorPolicy.length > 0) && (
                      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">Removed vs prior policy</div>
                        <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
                          {schedule.removalsVsPriorPolicy.map((r: string, i: number) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
        );
      })()}

      {/* YOY Renewal opt-in add-on: Coverage Details */}
      {shouldShowSection('coverage-details-section') && (data as any).reportAddOns?.coverageDetails && (
        <div {...getSectionDataAttrs('coverage-details-section')} style={getSectionStyles('coverage-details-section')}>
          <section id="coverage-details-section" className="mb-10">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white border-b-2 border-blue-500 pb-2">
              Coverage Details
            </h2>
            {(() => {
              const cds = (data as any).reportAddOns.coverageDetails;
              // A matrix is { headers: [...], rows: [...] } from the new
              // structured schema. Defensive: also tolerate legacy
              // markdown-string fields from older reports.
              const isStructured = (m: any) =>
                m && typeof m === 'object' && Array.isArray(m.headers) && Array.isArray(m.rows);
              const renderField = (title: string, value: any, idx: number) => {
                if (isStructured(value)) {
                  if (!value.rows.length) return null;
                  return (
                    <div className="mb-6" key={title}>
                      <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">{title}</h3>
                      {renderMatrix({ title: '', headers: value.headers, rows: value.rows }, idx)}
                    </div>
                  );
                }
                if (typeof value === 'string' && value.trim() && value !== 'No material changes identified.') {
                  return (
                    <div className="mb-6" key={title}>
                      <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">{title}</h3>
                      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                        <MarkdownBlock>{value}</MarkdownBlock>
                      </div>
                    </div>
                  );
                }
                return null;
              };
              const RichTextList: React.FC<{ title: string; items: any[]; color: 'red' | 'green' }> = ({ title, items, color }) => {
                if (!Array.isArray(items) || items.length === 0) return null;
                const cls = color === 'red'
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                  : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300';
                return (
                  <div className={`mb-4 border rounded-lg p-3 ${cls}`}>
                    <div className="text-sm font-semibold mb-1">{title}</div>
                    <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                      {items.map((e: any, i: number) => (
                        <li key={i}>
                          {typeof e === 'string'
                            ? e
                            : renderSafeContent(e, `${title} #${i + 1}`, hideCitations)}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              };
              return (
                <>
                  {renderField('Deductibles & Retentions', cds.deductiblesAndRetentions, 0)}
                  {renderField('Sub-Limits', cds.subLimits, 1)}
                  {renderField('Enhancement Endorsement Limits', cds.enhancementEndorsementLimits, 2)}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <RichTextList title="Exclusions Added" items={cds.exclusionsAdded || []} color="red" />
                    <RichTextList title="Exclusions Removed" items={cds.exclusionsRemoved || []} color="green" />
                  </div>
                  {Array.isArray(cds.exclusionsModified) && cds.exclusionsModified.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Exclusions Modified</h3>
                      <div className="space-y-2">
                        {cds.exclusionsModified.map((m: any, i: number) => (
                          <div key={i} className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                            <div className="font-medium text-gray-900 dark:text-white">{m.exclusion}</div>
                            {m.expiringLanguage && (
                              <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                                <span className="font-semibold">Expiring: </span>
                                {renderSafeContent(m.expiringLanguage, 'Expiring Language', hideCitations)}
                              </div>
                            )}
                            {m.renewalLanguage && (
                              <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                                <span className="font-semibold">Renewal: </span>
                                {renderSafeContent(m.renewalLanguage, 'Renewal Language', hideCitations)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Legacy single-string form (older reports) */}
                  {typeof cds.exclusionsModified === 'string' && renderField('Exclusions Modified', cds.exclusionsModified, 3)}
                  {renderField('Coverage Triggers', cds.coverageTriggers, 4)}
                  {Array.isArray(cds.keyDefinitions) && cds.keyDefinitions.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Key Definitions</h3>
                      <div className="space-y-2">
                        {cds.keyDefinitions.map((kd: any, i: number) => (
                          <div key={i} className={`border rounded-lg p-3 ${kd.materialDifference ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/30'}`}>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {kd.term}
                              {kd.materialDifference && <span className="ml-2 text-xs font-semibold text-amber-700 dark:text-amber-300">Material difference</span>}
                            </div>
                            {kd.expiringDefinition && (
                              <div className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                                <span className="font-semibold">Expiring: </span>
                                {renderSafeContent(kd.expiringDefinition, `${kd.term} expiring`, hideCitations)}
                              </div>
                            )}
                            {kd.renewalDefinition && (
                              <div className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                                <span className="font-semibold">Renewal: </span>
                                {renderSafeContent(kd.renewalDefinition, `${kd.term} renewal`, hideCitations)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Legacy single-string form for keyDefinitions */}
                  {typeof cds.keyDefinitions === 'string' && renderField('Key Definitions', cds.keyDefinitions, 5)}
                </>
              );
            })()}
          </section>
        </div>
      )}

      {/* YOY Renewal opt-in add-on: Compliance & Contractual */}
      {shouldShowSection('compliance-contractual-section') && (data as any).reportAddOns?.complianceContractual && (
        <div {...getSectionDataAttrs('compliance-contractual-section')} style={getSectionStyles('compliance-contractual-section')}>
          <section id="compliance-contractual-section" className="mb-10">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white border-b-2 border-blue-500 pb-2">
              Compliance & Contractual
            </h2>
            {(() => {
              const cc = (data as any).reportAddOns.complianceContractual;
              return (
                <>
                  {cc.summary && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mb-4 rounded">
                      <div className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
                        {typeof cc.summary === 'string'
                          ? cc.summary
                          : renderSafeContent(cc.summary, 'Compliance Summary', hideCitations)}
                      </div>
                    </div>
                  )}
                  {!cc.contractFound && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4 text-sm text-amber-900 dark:text-amber-200">
                      No underlying contract was uploaded. Upload the relevant contract(s) on the Additional Context step to receive a full compliance review.
                    </div>
                  )}
                  {(() => {
                    const m = cc.coiRequirements;
                    if (m && typeof m === 'object' && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
                      return (
                        <div className="mb-6">
                          <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">COI Requirements vs Policy</h3>
                          {renderMatrix({ title: '', headers: m.headers, rows: m.rows }, 0)}
                        </div>
                      );
                    }
                    if (typeof m === 'string' && m.trim()) {
                      return (
                        <div className="mb-6">
                          <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">COI Requirements vs Policy</h3>
                          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                            <MarkdownBlock>{m}</MarkdownBlock>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {(() => {
                    const m = cc.contractualObligations;
                    if (m && typeof m === 'object' && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
                      return (
                        <div className="mb-2">
                          <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Contractual Obligations</h3>
                          {renderMatrix({ title: '', headers: m.headers, rows: m.rows }, 1)}
                        </div>
                      );
                    }
                    if (typeof m === 'string' && m.trim()) {
                      return (
                        <div className="mb-2">
                          <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Contractual Obligations</h3>
                          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                            <MarkdownBlock>{m}</MarkdownBlock>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              );
            })()}
          </section>
        </div>
      )}

      {/* Sources — Wikipedia-style list of every document this report
          analyzed. Letter prefix matches the inline CitationMarker so users
          can map "A" badges in the prose back to the actual filename.
          Different workflow schemas use different field names — we look at
          both responseMetadata.documentsAnalyzed and documentsProcessed. */}
      {(() => {
        const meta: any = data.responseMetadata || {};
        const docs: Array<{ id?: string; name?: string; docName?: string; type?: string; docType?: string; policyType?: string }> =
          (Array.isArray(meta.documentsAnalyzed) && meta.documentsAnalyzed.length > 0 && meta.documentsAnalyzed) ||
          (Array.isArray(meta.documentsProcessed) && meta.documentsProcessed.length > 0 && meta.documentsProcessed) ||
          [];
        if (!docs.length) return null;
        return (
          <section id="sources" className="mb-10 mt-12">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
              Sources
            </h2>
            <ol className="space-y-1.5 text-sm">
              {docs.map((doc, i) => (
                <li key={doc.id || (doc as any).docId || i} className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-semibold leading-none bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 flex-shrink-0 mt-0.5">
                    {String.fromCharCode(65 + (i % 26))}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-800 dark:text-gray-100 font-medium break-words">
                      {doc.name || doc.docName || 'Document'}
                    </div>
                    {(doc.type || doc.docType || doc.policyType) && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {[doc.type || doc.docType, doc.policyType].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        );
      })()}

      {/* Professional Disclaimer - ALWAYS AT BOTTOM OF EVERY REPORT */}
      {(() => {
        const DEFAULT_DISCLAIMER = `This analysis is generated by an AI assistant and is intended for use by experienced insurance professionals as a supplementary tool, not as a substitute for their own expert judgment. By using this analysis, you acknowledge and agree to the following limitations:

Not Legal or Professional Advice: I am not a licensed attorney, claims adjuster, or broker. This analysis is a form of sophisticated pattern recognition and language processing; it is not legal advice, a definitive coverage determination, or a substitute for the nuanced judgment of a seasoned human professional qualified to provide such advice.

Knowledge Cutoff: My understanding of jurisdictional precedent is based on training data that is not real-time. The analysis may not reflect the most recent court decisions, regulatory changes, or emerging legal interpretations.

Context is Key: This analysis is strictly limited to the documents and context provided. I am not aware of the client's specific operational risks, risk appetite, prior claims history, or any verbal representations made by underwriters, all of which are critical for a complete and accurate human-led review.

All findings must be independently verified by a qualified professional. The failure to do so could result in significant unintended consequences, including uninsured losses and breach of contract.`;

        // Use disclaimer from data if available, otherwise use default
        const disclaimerText = (data as any).disclaimer ||
                              ((data as any).professionalDisclaimer?.text || (data as any).professionalDisclaimer) ||
                              DEFAULT_DISCLAIMER;

        return (
          <section id="disclaimer" className="mb-10 mt-16">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white border-b-2 border-gray-200 dark:border-gray-600 pb-2">
              Professional Disclaimer
            </h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {typeof disclaimerText === 'string' ? disclaimerText : DEFAULT_DISCLAIMER}
              </p>
            </div>
          </section>
        );
      })()}

      </div>{/* End sections-container */}

      {/* Inline Styles */}
      <style>{`
        /* Sections container - ensure overflow visible for keep-together labels */
        .sections-container {
          overflow: visible;
        }

        .citation-link {
          color: #2563eb !important;
          text-decoration: underline !important;
          cursor: pointer !important;
          background-color: #eff6ff !important;
          padding: 2px 6px !important;
          border-radius: 4px !important;
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
        }
        
        .citation-link:hover {
          color: #1d4ed8 !important;
          background-color: #dbeafe !important;
          transform: translateY(-1px) !important;
        }

        .dark .citation-link {
          color: #60a5fa !important;
          background-color: #1e3a8a !important;
        }
        
        .dark .citation-link:hover {
          color: #93c5fd !important;
          background-color: #1e40af !important;
        }

        .analysis-matrix table {
          font-size: 0.875rem !important;
          table-layout: fixed !important;
          width: 100% !important;
          min-width: 100% !important;
        }
        
        .analysis-matrix .overflow-x-auto {
          width: 100% !important;
          max-width: 100% !important;
        }
        
        .analysis-matrix {
          width: 100% !important;
        }

        .analysis-matrix th {
          background: linear-gradient(to bottom, #f9fafb, #f3f4f6) !important;
        }

        .analysis-matrix td, .analysis-matrix th {
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          box-sizing: border-box !important;
          max-width: none !important;
        }
        
        .analysis-matrix td {
          height: auto !important;
          vertical-align: top !important;
        }

        .dark .analysis-matrix th {
          background: linear-gradient(to bottom, #374151, #4b5563) !important;
        }

        .universal-json-report h2 {
          position: relative !important;
        }

        .universal-json-report h2::after {
          content: '' !important;
          position: absolute !important;
          bottom: -2px !important;
          left: 0 !important;
          width: 50px !important;
          height: 3px !important;
          background: linear-gradient(to right, #3b82f6, #1d4ed8) !important;
          border-radius: 2px !important;
        }

        /* Page break and keep-together visual indicators */
        [data-page-break-after="true"] {
          position: relative;
          padding-bottom: 2rem;
          margin-bottom: 2rem;
        }

        [data-page-break-after="true"]::after {
          content: '✂ Page Break';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem;
          font-size: 0.75rem;
          font-weight: 500;
          color: #ea580c;
          background: linear-gradient(to right, transparent, #fff7ed 20%, #fff7ed 80%, transparent);
          border-top: 2px dashed #fb923c;
          border-bottom: 2px dashed #fb923c;
        }

        [data-keep-together="true"] {
          position: relative;
          border-left: 4px solid #22c55e;
          padding-left: 1rem;
          margin-top: 1.5rem;
          overflow: visible;
          background: linear-gradient(to right, rgba(34, 197, 94, 0.05), transparent 10%);
        }

        [data-keep-together="true"]::before {
          content: '🔗 Keep Together';
          position: absolute;
          top: -1.25rem;
          left: -4px;
          font-size: 0.7rem;
          font-weight: 600;
          color: #16a34a;
          background: #dcfce7;
          padding: 0.2rem 0.6rem;
          border-radius: 0.25rem 0.25rem 0 0;
          border: 1px solid #22c55e;
          border-bottom: none;
          z-index: 10;
        }

        @media print {
          [data-page-break-after="true"] {
            page-break-after: always !important;
            break-after: page !important;
          }

          [data-page-break-after="true"]::after {
            display: none !important;
          }

          [data-keep-together="true"] {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            border-left: none !important;
            padding-left: 0 !important;
            margin-top: 0 !important;
            background: none !important;
          }

          [data-keep-together="true"]::before {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

// ===== WORKFLOW SELECTOR COMPONENT =====

interface WorkflowSelectorProps {
  documents: any[];
  onExecute: (workflowType: string) => void;
  loading?: boolean;
  className?: string;
}

export const WorkflowSelector: React.FC<WorkflowSelectorProps> = ({ 
  documents, 
  onExecute, 
  loading = false,
  className = '' 
}) => {
  
  const workflows = [
    {
      type: 'policy_comparison',
      title: 'Policy Comparison',
      description: 'Compare multiple insurance policies side-by-side with detailed analysis',
      icon: '⚖️',
      minDocuments: 2,
      color: 'blue',
      cardClasses: 'border-blue-300 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-100',
      badgeClasses: 'bg-blue-100 text-blue-800'
    },
    {
      type: 'policy_analysis',
      title: 'Policy Analysis',
      description: 'Comprehensive analysis of individual policy coverage and terms',
      icon: '🔍',
      minDocuments: 1,
      color: 'green',
      cardClasses: 'border-green-300 hover:border-green-500 hover:shadow-lg hover:shadow-green-100',
      badgeClasses: 'bg-green-100 text-green-800'
    },
    {
      type: 'loss_run_analysis',
      title: 'Loss Run Analysis',
      description: 'Analyze claims history and identify loss trends and patterns',
      icon: '📈',
      minDocuments: 1,
      color: 'orange',
      cardClasses: 'border-orange-300 hover:border-orange-500 hover:shadow-lg hover:shadow-orange-100',
      badgeClasses: 'bg-orange-100 text-orange-800'
    },
    {
      type: 'research_analysis',
      title: 'Research Analysis',
      description: 'Market research and competitive analysis with industry benchmarks',
      icon: '🔬',
      minDocuments: 1,
      color: 'purple',
      cardClasses: 'border-purple-300 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-100',
      badgeClasses: 'bg-purple-100 text-purple-800'
    },
    {
      type: 'risk_assessment',
      title: 'Risk Assessment',
      description: 'Comprehensive risk evaluation and mitigation recommendations',
      icon: '⚠️',
      minDocuments: 1,
      color: 'red',
      cardClasses: 'border-red-300 hover:border-red-500 hover:shadow-lg hover:shadow-red-100',
      badgeClasses: 'bg-red-100 text-red-800'
    },
    {
      type: 'coverage_analysis',
      title: 'Coverage Analysis',
      description: 'Detailed analysis of insurance policy coverage, gaps, and adequacy',
      icon: '🛡️',
      minDocuments: 1,
      color: 'indigo',
      cardClasses: 'border-indigo-300 hover:border-indigo-500 hover:shadow-lg hover:shadow-indigo-100',
      badgeClasses: 'bg-indigo-100 text-indigo-800'
    }
  ];

  return (
    <div className={`workflow-selector ${className}`}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2 text-gray-800 dark:text-white">Select Analysis Type</h2>
        <p className="text-gray-600 dark:text-gray-300">
          Choose the type of analysis you want to perform on your selected documents.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {workflows.map(workflow => {
          const isEnabled = documents.length >= workflow.minDocuments && !loading;
          const missingDocs = Math.max(0, workflow.minDocuments - documents.length);
          
          return (
            <div 
              key={workflow.type}
              className={`workflow-card relative p-6 border-2 rounded-xl transition-all duration-200 cursor-pointer ${
                isEnabled 
                  ? `${workflow.cardClasses} bg-white dark:bg-gray-800` 
                  : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 cursor-not-allowed opacity-60'
              }`}
              onClick={() => {
                if (isEnabled) {
                  onExecute(workflow.type);
                }
              }}
            >
              <div className="text-center mb-4">
                <div className="text-4xl mb-2">{workflow.icon}</div>
                <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">{workflow.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{workflow.description}</p>
              </div>
              
              <div className="text-center text-xs">
                <div className={`px-3 py-1 rounded-full ${
                  isEnabled 
                    ? workflow.badgeClasses 
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                  Requires {workflow.minDocuments} document{workflow.minDocuments > 1 ? 's' : ''}
                </div>
                
                {missingDocs > 0 && (
                  <div className="text-red-500 mt-2 font-medium">
                    {missingDocs} more document{missingDocs > 1 ? 's' : ''} needed
                  </div>
                )}
              </div>
              
              {loading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-xl">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UniversalJsonRenderer;